import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {Checkbox, CircularProgress, FormControl, InputLabel, MenuItem, Select, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Box, Typography, FormControlLabel, Switch} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import TextField from "@material-ui/core/TextField";
import {Button} from "@material-ui/core";
import { v4 as uuid } from 'uuid';
import Autocomplete from "@material-ui/lab/Autocomplete";
import {faTrash, faInfoCircle, faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { DatePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import PrintIcon from '@material-ui/icons/Print';
import { AuthenticationService } from "../../../Services/AuthenticationService";
import LzString from "lz-string";
import {getNumberInputDisplayValue, parseNumberInputValue} from '../../../utils/numberInputUtils';
import {
    buildInTransitByPartId,
    computeCriticalEffectiveInventoryWeeks,
} from '../../../utils/inventoryCriticalUtils';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import VehicleFitmentPanel from './VehicleFitmentPanel';
import {buildBomIndex, containerHasTarget, buildContributionChains, formatBomQty, chainMultiplier} from '../../../utils/bomContributions';
import PartDisplay from '../../Common/PartDisplay';
import {formatUtcForDisplay} from '../../../utils/dateUtils';
import { isPackingSlipFullyReceived } from '../../../utils/packingSlipShipmentUtils';
import { addDays } from 'date-fns';
import {
    buildMsrpTierDisplayRows,
    createManualMsrpTierLine,
    getActiveMsrpTierCopyRows,
    hydratePartMsrpTierLines,
    parseMsrpTierLines,
    parseOptionalMsrpValue,
    serializeMsrpTierLines,
    updatePartMsrpTierLinesFromDisplay,
} from '../../../utils/msrpTierUtils';

const DEFAULT_MSRP_ROUND_THRESHOLD = 15;
const DEFAULT_MSRP_ROUND_BELOW_INCREMENT = 0.25;
const DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT = 1;

/** Ensure MSRP margin/rounding fields exist on a part loaded from the API. */
function normalizePartMsrpPricingFields(part) {
    if (!part) return;
    if (part.msrpUseMarginPricing === undefined || part.msrpUseMarginPricing === null) {
        part.msrpUseMarginPricing = false;
    }
    if (part.msrpMarginPercent === undefined || part.msrpMarginPercent === null || part.msrpMarginPercent === '') {
        part.msrpMarginPercent = 0;
    }
    const th = parseNumberInputValue(part.msrpRoundThreshold);
    if (part.msrpRoundThreshold === undefined || part.msrpRoundThreshold === null || part.msrpRoundThreshold === '' || !Number.isFinite(th)) {
        part.msrpRoundThreshold = DEFAULT_MSRP_ROUND_THRESHOLD;
    }
    const low = parseNumberInputValue(part.msrpRoundBelowIncrement);
    if (part.msrpRoundBelowIncrement === undefined || part.msrpRoundBelowIncrement === null || part.msrpRoundBelowIncrement === '' || !Number.isFinite(low)) {
        part.msrpRoundBelowIncrement = DEFAULT_MSRP_ROUND_BELOW_INCREMENT;
    }
    const high = parseNumberInputValue(part.msrpRoundAtOrAboveIncrement);
    if (part.msrpRoundAtOrAboveIncrement === undefined || part.msrpRoundAtOrAboveIncrement === null || part.msrpRoundAtOrAboveIncrement === '' || !Number.isFinite(high)) {
        part.msrpRoundAtOrAboveIncrement = DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT;
    }
    hydratePartMsrpTierLines(part);
}

/** Snapshot MSRP + margin pricing for clone / new revision (does not mutate `part`). */
function copyMsrpPricingFromPart(part) {
    if (!part || typeof part !== 'object') {
        return {
            msrp: 0,
            msrpUseMarginPricing: false,
            msrpMarginPercent: 0,
            msrpRoundThreshold: DEFAULT_MSRP_ROUND_THRESHOLD,
            msrpRoundBelowIncrement: DEFAULT_MSRP_ROUND_BELOW_INCREMENT,
            msrpRoundAtOrAboveIncrement: DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT,
            msrpTiersJson: null,
        };
    }
    const snapshot = {
        msrp: part.msrp,
        msrpUseMarginPricing: part.msrpUseMarginPricing,
        msrpMarginPercent: part.msrpMarginPercent,
        msrpRoundThreshold: part.msrpRoundThreshold,
        msrpRoundBelowIncrement: part.msrpRoundBelowIncrement,
        msrpRoundAtOrAboveIncrement: part.msrpRoundAtOrAboveIncrement,
        msrpTiersJson: part.msrpTiersJson ?? serializeMsrpTierLines(part.msrpTierLines) ?? null,
    };
    normalizePartMsrpPricingFields(snapshot);
    return {
        msrp: parseNumberInputValue(snapshot.msrp),
        msrpUseMarginPricing: !!snapshot.msrpUseMarginPricing,
        msrpMarginPercent: parseNumberInputValue(snapshot.msrpMarginPercent),
        msrpRoundThreshold: parseNumberInputValue(snapshot.msrpRoundThreshold) || DEFAULT_MSRP_ROUND_THRESHOLD,
        msrpRoundBelowIncrement: parseNumberInputValue(snapshot.msrpRoundBelowIncrement) || DEFAULT_MSRP_ROUND_BELOW_INCREMENT,
        msrpRoundAtOrAboveIncrement: parseNumberInputValue(snapshot.msrpRoundAtOrAboveIncrement) || DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT,
        msrpTiersJson: snapshot.msrpTiersJson ?? null,
    };
}

function roundMsrpPriceUp(value, increment) {
    const v = Number(value);
    const inc = Number(increment);
    if (!Number.isFinite(v) || !Number.isFinite(inc) || inc <= 0) return v;
    return Math.ceil((v - 1e-9) / inc) * inc;
}

/** Post-margin price: round up to low or high increment based on comparison to threshold (on post-margin amount). */
function applyMsrpMarginAndRounding(unitCost, marginPercent, threshold, lowInc, highInc) {
    const cost = Number(unitCost);
    const mPct = Number(marginPercent);
    if (!Number.isFinite(cost) || !Number.isFinite(mPct)) return NaN;
    const raw = cost * (1 + mPct / 100);
    if (!Number.isFinite(raw)) return NaN;
    const th = Number(threshold);
    const low = Number(lowInc);
    const high = Number(highInc);
    const inc = Number.isFinite(th) && raw < th && Number.isFinite(low) ? low : (Number.isFinite(high) ? high : low);
    return roundMsrpPriceUp(raw, inc);
}

/** Plain-text MSRP tier line for email copy (e.g. "2\t $       57.00"). */
function formatMsrpCopyPlainLine(quantity, msrp) {
    const amount = Number(msrp).toFixed(2);
    return `${quantity}\t $${amount.padStart(12, ' ')}`;
}

function formatMsrpCopyPriceDisplay(msrp) {
    const amount = Number(msrp).toFixed(2);
    return `$${amount.padStart(12, ' ')}`;
}

function formatMsrpCopyPartHeader(partNumber, revision) {
    return `${partNumber} rev ${revision ?? ''}`;
}

// Status normalization function to ensure consistent casing
const normalizeStatus = (status) => {
    if (!status) return '-';
    
    const normalized = status.toString().toLowerCase();
    if (normalized === 'fulfilled' || normalized === 'fulfill') {
        return 'Fulfilled';
    } else if (normalized === 'pending') {
        return 'Pending';
    } else if (normalized === 'cancelled' || normalized === 'canceled') {
        return 'Cancelled';
    } else if (normalized === 'shipped') {
        return 'Shipped';
    }
    
    // For unknown statuses, capitalize first letter and lowercase the rest
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    printButton: {
        marginLeft: 10,
        cursor: 'pointer',
        color: '#2196F3'
    }
});

let editingPart;
let assemblies;
let subAssemblies;

class EditPart extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        adminUsers: [],
        vendors: [],
        showAdvancedInfo: false,
        newRevision: '',
        newRevisionNotes: '',
        showCloneDialog: false,
        clonedPartData: null,
        cloneImagesAndFiles: false,
        focusQuantityField: null,
        originalPart: null, // Store original part data for change detection
        inventoryData: null, // Store inventory analytics data
        imageModalOpen: false,
        selectedImageUrl: null,
        isDraggingOverMain: false,
        isDraggingOverZone: null,
        isDraggingOverFilesMain: false,
        isDraggingOverFilesZone: null,
        partTypeInputValue: '', // Track input value for filtering
        manufacturerForInputValue: '', // Track input value for filtering
        tertiaryTypeInputValue: '', // Track input value for filtering
        brandInputValue: '', // Track input value for filtering
        // Calculation contributions state
        calculationContributionsTab: 0, // Active tab index
        showParentChildOptionalColumns: false, // Parent/Child: show hidden detail columns
        showParentChildImpliedParts: false, // Parent/Child: include implied parts in the list
        calculationContributionsData: {
            inventoryLogs: null,
            packageLogs: null,
            purchaseLogs: null,
            packingSlips: null,
            salesLogs: null
        },
        loadingCalculationContributions: false,
        // Purchase drawer (from EditPart vendor)
        purchaseDrawerOpen: false,
        newLogParts: [],
        newPurchaseOrderDate: null,
        newVendorOrderNumber: '',
        savingPurchaseLog: false,
        partFamilies: [],
        showCopyTable: false,
        copyTablesByVendor: {},
        showMsrpCopyTable: false,
        msrpCopyTableData: null,
        useCustomStoreDescription: false,
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        // Update document title when part data is loaded
        if (editingPart && editingPart.part && (!prevState.editingPart || prevState.editingPart.part.id !== editingPart.part.id)) {
            document.title = `${editingPart.part.partId} rev ${editingPart.part.revision}`;
        }
    }
    
    checkForChanges = () => {
        if (!this.state.originalPart || !editingPart) return false;
        
        const original = this.state.originalPart;
        const current = editingPart;
        
        // Deep comparison of the part objects
        return JSON.stringify(original) !== JSON.stringify(current);
    };

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('EditAssembliesAndParts')) {
            globalProps.history.push('/admin/parts');
            return;
        }

        return Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts(), self.loadAdminUsers()]).then(() => {
            self.setState({loading: false, user, permissions: user.userPermissions});
            // Fetch inventory data after main data is loaded
            self.fetchInventoryData();
            // Note: First tab (Inventory Calculations) uses inventoryData which is loaded by fetchInventoryData
            // Ensure fileURLs is converted to array after reload
            if (editingPart && editingPart.part) {
                if (typeof editingPart.part.fileURLs === 'string') {
                    editingPart.part.fileURLs = editingPart.part.fileURLs 
                        ? editingPart.part.fileURLs.split('~|~').filter(x => x && x.trim() !== '')
                        : [];
                }
                self.setState({editingPart});
            }
            // Preload calculation contribution tabs (1–5) in the background; tab 0 uses inventoryData above; tab 6 is BOM-only
            self.prefetchCalculationContributionsTableData();
        });
    };

    loadVendors = () => {
        const vendorService = ServiceLocator.Instance.getVendorService();
        return vendorService.getAllVendors().then((vendors) => {
            this.setState({vendors});
        });
    };

    loadAdminUsers = () => {
        const userService = ServiceLocator.Instance.getUserService();
        return userService.getAdminUsers().then((adminUsers) => {
            this.setState({adminUsers});
        });
    };

    loadPartFamilies = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getPartFamilies().then((partFamilies) => {
            this.setState({partFamilies});
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assembliesResult) => {
            assemblies = assembliesResult.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                return 0;
            });
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssembliesResult) => {
            subAssemblies = subAssembliesResult.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return this.loadVendors().then(() => {
            return partService.getAllPartsExpanded().then((parts) => {
                // Sort parts exactly like in Parts.js
                const driveshaftCarriersParts = parts.filter(x => x.part.partType === 'Driveshaft Carriers').sort(function (a, b) {
                    // Sort by manufacturerFor first
                    if (a.part.manufacturerFor !== b.part.manufacturerFor) {
                        if (a.part.manufacturerFor < b.part.manufacturerFor) return -1;
                        if (a.part.manufacturerFor > b.part.manufacturerFor) return 1;
                    }

                    // Then by tertiaryType (empty/null first)
                    const aTertiary = a.part.tertiaryType || '';
                    const bTertiary = b.part.tertiaryType || '';
                    if (aTertiary !== bTertiary) {
                        if (aTertiary === '' && bTertiary !== '') return -1;
                        if (aTertiary !== '' && bTertiary === '') return 1;
                        if (aTertiary < bTertiary) return -1;
                        if (aTertiary > bTertiary) return 1;
                    }

                    // Then by partId
                    if (a.part.partId !== b.part.partId) {
                        if (a.part.partId < b.part.partId) return -1;
                        if (a.part.partId > b.part.partId) return 1;
                    }

                    // Finally by revision (descending)
                    if (a.part.revision !== b.part.revision) {
                        if (a.part.revision > b.part.revision) return -1;
                        if (a.part.revision < b.part.revision) return 1;
                    }

                    return 0;
                });
                const otherParts = parts.filter(x => x.part.partType !== 'Driveshaft Carriers').sort(function (a, b) {
                    // Sort by partType first
                    if (a.part.partType !== b.part.partType) {
                        if (a.part.partType < b.part.partType) return -1;
                        if (a.part.partType > b.part.partType) return 1;
                    }

                    // Then by manufacturerFor
                    if (a.part.manufacturerFor !== b.part.manufacturerFor) {
                        if (a.part.manufacturerFor < b.part.manufacturerFor) return -1;
                        if (a.part.manufacturerFor > b.part.manufacturerFor) return 1;
                    }

                    // Then by tertiaryType (empty/null first)
                    const aTertiary = a.part.tertiaryType || '';
                    const bTertiary = b.part.tertiaryType || '';
                    if (aTertiary !== bTertiary) {
                        if (aTertiary === '' && bTertiary !== '') return -1;
                        if (aTertiary !== '' && bTertiary === '') return 1;
                        if (aTertiary < bTertiary) return -1;
                        if (aTertiary > bTertiary) return 1;
                    }

                    // Then by partId
                    if (a.part.partId !== b.part.partId) {
                        if (a.part.partId < b.part.partId) return -1;
                        if (a.part.partId > b.part.partId) return 1;
                    }

                    // Finally by revision (descending)
                    if (a.part.revision !== b.part.revision) {
                        if (a.part.revision > b.part.revision) return -1;
                        if (a.part.revision < b.part.revision) return 1;
                    }

                    return 0;
                });
                parts = driveshaftCarriersParts.concat(otherParts);
                const partId = globalProps.match.params.partId;
                editingPart = parts.find(x => x.part.id === partId);
                if (!editingPart) globalProps.history.push('/admin/parts');
                else {
                    editingPart.part.imageURLs = editingPart.part.imageURL.split('~|~');
                    // FileURLs are stored as "filename|url" entries separated by ~|~
                    // Filter out empty entries when loading
                    const fileURLsValue = editingPart.part.fileURLs;
                    console.log('Loading fileURLs from database:', fileURLsValue, 'type:', typeof fileURLsValue);
                    if (fileURLsValue && typeof fileURLsValue === 'string' && fileURLsValue.trim() !== '') {
                        editingPart.part.fileURLs = fileURLsValue.split('~|~').filter(x => x && x.trim() !== '');
                    } else {
                        editingPart.part.fileURLs = [];
                    }
                    console.log('Loaded fileURLs (converted to array):', editingPart.part.fileURLs);
                    editingPart.part.vendorParts.forEach((vendorPart) => {
                        const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                        vendorPart.vendorName = vendor ? vendor.name : '';
                        vendorPart.tieredCostFormulas = vendorPart.tieredCostFormulas.sort((a, b) => a.quantity > b.quantity ? 1 : -1);
                        
                        // Ensure at least one tiered cost formula is selected if they exist
                        if (vendorPart.tieredCostFormulas && vendorPart.tieredCostFormulas.length > 0) {
                            vendorPart.tieredCostFormulas.forEach(tcf => {
                                tcf.price = Number.parseFloat(tcf.price).toFixed(2);
                            });
                            const hasSelected = vendorPart.tieredCostFormulas.some(tcf => tcf.isSelected);
                            if (!hasSelected) {
                                vendorPart.tieredCostFormulas[0].isSelected = true;
                            }
                        }
                    });
                    
                    // Set selectedVendorId if there's only one vendor or if no vendor is selected but there are vendors
                    const validVendorParts = editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion);
                    if (validVendorParts.length === 1) {
                        editingPart.part.selectedVendorId = validVendorParts[0].vendorId;
                    } else if (editingPart.part.selectedVendorId === null && validVendorParts.length > 0) {
                        // If no vendor is selected but there are vendors, select the first one that has a selected tiered formula
                        const vendorWithSelectedFormula = validVendorParts.find(vp => 
                            vp.tieredCostFormulas && vp.tieredCostFormulas.some(tcf => tcf.isSelected)
                        );
                        if (vendorWithSelectedFormula) {
                            editingPart.part.selectedVendorId = vendorWithSelectedFormula.vendorId;
                        }
                    }
                    if (validVendorParts.length === 0) {
                        editingPart.part.vendorParts.push({
                            id: uuid(),
                            vendorId: uuid(),
                            vendorName: '',
                            partId: editingPart.part.id,
                            costType: 1,
                            basePrice: 0.00,
                            quantity: 1,
                            partUrl: '',
                            tieredCostFormulas: []
                        });
                    }
                    normalizePartMsrpPricingFields(editingPart.part);
                    // Store original part data for change detection
                    const originalPart = JSON.parse(JSON.stringify(editingPart));
                    self.setState({
                        parts,
                        editingPart,
                        originalPart,
                        useCustomStoreDescription: !!(editingPart.part.customerSiteDescription || '').trim(),
                    });
                    // Update document title
                    document.title = `${editingPart.part.partId} rev ${editingPart.part.revision}`;
                }
            });
        });
    };

    handleCostTypeChange = (vendorPart, costType) => {
        vendorPart.costType = costType;
        if (vendorPart.tieredCostFormulas.length === 0) {
            vendorPart.tieredCostFormulas.push({
                id: uuid(),
                vendorPartFk: vendorPart.id,
                isSelected: true,
                price: 0,
                quantity: 1
            });
        }
        // Set this vendor as the selected vendor when switching to tiered cost type
        editingPart.part.selectedVendorId = vendorPart.vendorId;
        this.setState({editingPart});
    };

    handleInventoryStrategyChange = (strategy) => {
        editingPart.part.inventoryStrategy = strategy;
        this.setState({editingPart});
    };

    addTieredCostFormula = (vendorPart) => {
        vendorPart.tieredCostFormulas.push({
            id: uuid(),
            vendorPartFk: vendorPart.id,
            isSelected: false,
            price: 0,
            quantity: 1
        });
        // Force a re-render by updating the state
        this.setState({editingPart: {...editingPart}});
    };

    addTieredCostFormulaAndFocus = (vendorPart) => {
        const newFormula = {
            id: uuid(),
            vendorPartFk: vendorPart.id,
            isSelected: false,
            price: 0,
            quantity: 1
        };
        vendorPart.tieredCostFormulas.push(newFormula);
        // Force a re-render by updating the state and set focus to the new quantity field
        this.setState({
            editingPart: {...editingPart},
            focusQuantityField: newFormula.id
        });
    };

    removeTieredCostFormula = (vendorPart, tcf) => {
        vendorPart.tieredCostFormulas.splice(vendorPart.tieredCostFormulas.indexOf(tcf), 1);
        this.setState({loading: true});
        setTimeout(() => {
            this.setState({editingPart, loading: false});
        }, 100);
    };

    selectTieredFormula = (vendorPart, id) => {
        vendorPart.tieredCostFormulas.forEach((tieredFormula) => {
            tieredFormula.isSelected = tieredFormula.id === id;
        });
        // Set this vendor as the selected vendor when a tiered formula is selected
        editingPart.part.selectedVendorId = vendorPart.vendorId;
        // Force a re-render by updating the state with a new object reference
        this.setState({editingPart: {...editingPart}});
    };

    setDefaultVendorId = (vendorId) => {
        // Only set the default vendor if the vendor part is not marked for deletion
        const vendorPart = editingPart.part.vendorParts.find(vp => vp.vendorId === vendorId);
        if (vendorPart && !vendorPart.markedForDeletion) {
            editingPart.part.selectedVendorId = vendorId;
            this.setState({editingPart});
        }
    };

    changePartTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingPart.part.partType = !newValue ? '' : newValue;
            this.setState({editingPart, newPartType: true});
        } else {
            editingPart.part.partType = newValue;
            this.setState({editingPart, newPartType: false});
        }
    };

    changePartType = (event) => {
        editingPart.part.partType = event.target.value;
        this.setState({editingPart});
    };

    changeManufacturerForSelection = (event, newValue) => {
        if (!newValue || newValue.subType === 'New...') {
            editingPart.part.manufacturerFor = !newValue ? '' : newValue.subType;
            this.setState({editingPart, newManufacturerFor: true});
        } else {
            editingPart.part.manufacturerFor = newValue.subType;
            this.setState({editingPart, newManufacturerFor: false});
        }
    };

    changeManufacturerFor = (event) => {
        editingPart.part.manufacturerFor = event.target.value;
        this.setState({editingPart});
    };

    changeTertiaryTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingPart.part.tertiaryType = !newValue ? '' : newValue;
            this.setState({editingPart, newTertiaryType: true});
        } else {
            editingPart.part.tertiaryType = newValue;
            this.setState({editingPart, newTertiaryType: false});
        }
    };

    changeTertiaryType = (event) => {
        editingPart.part.tertiaryType = event.target.value;
        this.setState({editingPart});
    };

    // New freeSolo change handlers for organization dropdowns
    changePartTypeFreeSolo = (newValue) => {
        editingPart.part.partType = newValue || '';
        this.setState({editingPart});
    };

    changePartTypeInput = (newInputValue) => {
        editingPart.part.partType = newInputValue || '';
        this.setState({editingPart, partTypeInputValue: newInputValue || ''});
    };

    changeManufacturerForFreeSolo = (newValue) => {
        editingPart.part.manufacturerFor = newValue || '';
        this.setState({editingPart});
    };

    changeManufacturerForInput = (newInputValue) => {
        editingPart.part.manufacturerFor = newInputValue || '';
        this.setState({editingPart, manufacturerForInputValue: newInputValue || ''});
    };

    changeTertiaryTypeFreeSolo = (newValue) => {
        editingPart.part.tertiaryType = newValue || '';
        this.setState({editingPart});
    };

    changeTertiaryTypeInput = (newInputValue) => {
        editingPart.part.tertiaryType = newInputValue || '';
        this.setState({editingPart, tertiaryTypeInputValue: newInputValue || ''});
    };

    changeBrandFreeSolo = (newValue) => {
        editingPart.part.brand = newValue || '';
        this.setState({editingPart});
    };

    changeBrandInput = (newInputValue) => {
        editingPart.part.brand = newInputValue || '';
        this.setState({editingPart, brandInputValue: newInputValue || ''});
    };

    changePartId = (event) => {
        editingPart.part.partId = event.target.value;
        this.setState({editingPart});
    };

    changeRevision = (event) => {
        editingPart.part.revision = event.target.value;
        this.setState({editingPart});
    };

    changeRevisionNotes = (event) => {
        editingPart.part.revisionNotes = event.target.value;
        this.setState({editingPart});
    };

    changeDescription = (event) => {
        editingPart.part.description = event.target.value;
        this.setState({editingPart});
    };

    toggleUseCustomStoreDescription = (event) => {
        const checked = event.target.checked;
        if (!checked) {
            editingPart.part.customerSiteDescription = '';
        }
        this.setState({ useCustomStoreDescription: checked, editingPart });
    };

    changeCustomerSiteDescription = (event) => {
        editingPart.part.customerSiteDescription = event.target.value;
        this.setState({ editingPart });
    };

    addNewImage = () => {
        editingPart.part.imageURLs.push('');
        this.setState({editingPart});
    };

    changeImageURL = (index, event) => {
        editingPart.part.imageURLs[index] = event.target.value;
        this.setState({editingPart});
    };

    removeImage = (imageURL) => {
        editingPart.part.imageURLs.splice(editingPart.part.imageURLs.indexOf(imageURL), 1);
        this.setState({editingPart});
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    processImageFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate all files
        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            window.$alertError("Please select valid image files only.");
            return;
        }

        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 10MB.");
            return;
        }

        // Set uploading state for the index
        this.setState({[`uploadingImage${index}`]: true});

        const partService = ServiceLocator.Instance.getPartService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await partService.uploadPartImage(editingPart.part.id, files[i]);
                    uploadedUrls.push(response.imageUrl);
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading image ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedUrls.length > 0) {
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingPart.part.imageURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedUrls.forEach((url, i) => {
                    if (blankIndex !== -1 && blankIndex < editingPart.part.imageURLs.length) {
                        // Replace the blank slot
                        editingPart.part.imageURLs[blankIndex] = url;
                        // Find next blank slot
                        blankIndex = editingPart.part.imageURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingPart.part.imageURLs.push(url);
                    }
                });
                
                this.setState({editingPart, [`uploadingImage${index}`]: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({[`uploadingImage${index}`]: false});
                window.$alertError("Failed to upload images. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading images:", error);
            window.$alertError("Failed to upload images. Please try again.");
            this.setState({[`uploadingImage${index}`]: false});
        }
    };

    handleImageUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processImageFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    handleDragOverMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverMain: true });
    };

    handleDragLeaveMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverMain: false });
        }
    };

    handleDragOverZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverZone: index });
    };

    handleDragLeaveZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverZone: null });
        }
    };

    handleDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverMain: false, isDraggingOverZone: null });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processImageFiles(files, index);
        }
    };

    processFileFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate file size (50MB limit)
        const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 50MB.");
            return;
        }

        // Set uploading state for the index
        this.setState({[`uploadingFile${index}`]: true});

        const partService = ServiceLocator.Instance.getPartService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            const uploadedFiles = [];
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await partService.uploadPartFile(editingPart.part.id, files[i]);
                    // Store as "filename|url" format
                    // API returns { fileUrl, fileName }
                    const fileName = response.fileName || files[i].name || `file_${i + 1}`;
                    const fileUrl = response.fileUrl;
                    if (!fileUrl) {
                        console.error('No fileUrl in response:', response);
                        throw new Error('No fileUrl in upload response');
                    }
                    console.log('Uploaded file:', { fileName, fileUrl, originalFileName: files[i].name, response });
                    uploadedFiles.push({ fileName, fileUrl });
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading file ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedFiles.length > 0) {
                // Initialize fileURLs if it doesn't exist, ensure it's an array
                if (!editingPart.part.fileURLs) {
                    editingPart.part.fileURLs = [];
                } else if (typeof editingPart.part.fileURLs === 'string') {
                    // Convert string to array if needed
                    editingPart.part.fileURLs = editingPart.part.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
                } else if (!Array.isArray(editingPart.part.fileURLs)) {
                    editingPart.part.fileURLs = [];
                }
                
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingPart.part.fileURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedFiles.forEach((file, i) => {
                    // Store as "filename|url" format
                    const fileEntry = `${file.fileName}|${file.fileUrl}`;
                    if (blankIndex !== -1 && blankIndex < editingPart.part.fileURLs.length) {
                        // Replace the blank slot
                        editingPart.part.fileURLs[blankIndex] = fileEntry;
                        // Find next blank slot
                        blankIndex = editingPart.part.fileURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingPart.part.fileURLs.push(fileEntry);
                    }
                });
                
                this.setState({editingPart, [`uploadingFile${index}`]: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({[`uploadingFile${index}`]: false});
                window.$alertError("Failed to upload files. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading files:", error);
            window.$alertError("Failed to upload files. Please try again.");
            this.setState({[`uploadingFile${index}`]: false});
        }
    };

    handleFileUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processFileFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    handleDragOverFilesMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverFilesMain: true });
    };

    handleDragLeaveFilesMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverFilesMain: false });
        }
    };

    handleDragOverFilesZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverFilesZone: index });
    };

    handleDragLeaveFilesZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverFilesZone: null });
        }
    };

    handleFilesDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverFilesMain: false, isDraggingOverFilesZone: null });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processFileFiles(files, index);
        }
    };

    addNewFile = () => {
        if (!editingPart.part.fileURLs) {
            editingPart.part.fileURLs = [''];
        }
        editingPart.part.fileURLs.push('');
        this.setState({editingPart});
    };

    changeFileURL = (index, event) => {
        if (!editingPart.part.fileURLs) {
            editingPart.part.fileURLs = [''];
        }
        editingPart.part.fileURLs[index] = event.target.value;
        this.setState({editingPart});
    };

    removeFile = (fileEntry) => {
        console.log('removeFile called with:', fileEntry);
        // Ensure fileURLs is an array
        if (!editingPart.part.fileURLs) {
            editingPart.part.fileURLs = [];
        } else if (typeof editingPart.part.fileURLs === 'string') {
            // Convert string to array if needed
            editingPart.part.fileURLs = editingPart.part.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(editingPart.part.fileURLs)) {
            editingPart.part.fileURLs = [];
        }
        
        console.log('Current fileURLs array:', editingPart.part.fileURLs);
        
        // Find the index - handle both "filename|url" format and legacy URL-only format
        const index = editingPart.part.fileURLs.findIndex(entry => {
            // Exact match
            if (entry === fileEntry) return true;
            // If fileEntry is just a URL, check if entry contains it (for legacy files)
            if (!fileEntry.includes('|') && entry.includes(fileEntry)) return true;
            // If fileEntry is "filename|url", check if entry matches the URL part
            if (fileEntry.includes('|')) {
                const urlPart = fileEntry.split('|').slice(1).join('|');
                return entry.includes(urlPart);
            }
            return false;
        });
        
        console.log('Found index:', index);
        
        if (index !== -1) {
            editingPart.part.fileURLs.splice(index, 1);
            console.log('After removal, fileURLs:', editingPart.part.fileURLs);
            this.setState({editingPart});
        } else {
            console.error('File entry not found in array:', fileEntry);
        }
    };

    getFileNameFromUrl = (fileEntry) => {
        if (!fileEntry) return 'File';
        
        // Check if it's in "filename|url" format
        if (fileEntry.includes('|')) {
            const parts = fileEntry.split('|');
            if (parts.length >= 2) {
                const filename = parts[0].trim();
                // Return the filename part if it's not empty
                if (filename && filename.length > 0 && filename !== '') {
                    return filename;
                }
                // If filename part is empty, fall through
            }
        }
        
        // Legacy format: just URL - these are old files without filename stored
        // Try to extract something meaningful from the URL, otherwise return "File"
        try {
            const urlObj = new URL(fileEntry);
            const pathname = urlObj.pathname;
            const lastPart = pathname.split('/').pop();
            // If it's a GUID (has dashes and is long), return "File"
            if (lastPart && lastPart.includes('-') && lastPart.length > 30) {
                return 'File';
            }
            // Otherwise try to use the last part of the path
            return lastPart || 'File';
        } catch (e) {
            return 'File';
        }
        
        // Fallback: try to extract extension and create a generic name
        const urlMatch = fileEntry.match(/\.([a-zA-Z0-9]+)(\?|$)/);
        if (urlMatch) {
            return `file.${urlMatch[1]}`;
        }
        
        return 'file';
    };

    getFileUrlFromEntry = (fileEntry) => {
        if (!fileEntry) return '';
        
        // Check if it's in "filename|url" format
        if (fileEntry.includes('|')) {
            const parts = fileEntry.split('|');
            if (parts.length >= 2) {
                return parts.slice(1).join('|'); // Return everything after first | as URL (in case URL contains |)
            }
        }
        
        // Legacy format: just URL
        return fileEntry;
    };

    getFileURLsArray = () => {
        // Ensure fileURLs is always an array
        if (!editingPart || !editingPart.part) return [];
        
        const fileURLs = editingPart.part.fileURLs;
        if (!fileURLs) return [];
        
        // If it's already an array, return it
        if (Array.isArray(fileURLs)) {
            return fileURLs;
        }
        
        // If it's a string, split it
        if (typeof fileURLs === 'string') {
            return fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        }
        
        return [];
    };

    downloadFile = (fileEntry, index) => {
        const url = this.getFileUrlFromEntry(fileEntry);
        if (!url) return;
        
        // Use XMLHttpRequest to download the file properly with the correct filename
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        
        xhr.onload = function () {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const hyperlinkElement = document.createElement("a");
                const objectUrl = URL.createObjectURL(blob);
                hyperlinkElement.href = objectUrl;
                
                // Try to extract filename from file entry or use a default name
                let filename = this.getFileNameFromUrl(fileEntry);
                
                // If we still have a generic name, add index
                if (filename === 'file' || filename === 'File') {
                    filename = `file_${index + 1}`;
                    // Try to get extension from blob content type
                    const contentType = blob.type;
                    if (contentType) {
                        const extension = contentType.split('/')[1];
                        if (extension && extension !== 'octet-stream') {
                            filename = `${filename}.${extension}`;
                        }
                    }
                }
                
                // Use part ID in filename if available
                if (editingPart && editingPart.part && editingPart.part.partId) {
                    const partId = editingPart.part.partId.replace(/[^a-zA-Z0-9]/g, '_');
                    filename = `${partId}_${filename}`;
                }
                
                hyperlinkElement.setAttribute("download", filename);
                document.body.appendChild(hyperlinkElement);
                hyperlinkElement.click();
                document.body.removeChild(hyperlinkElement);
                // Clean up the object URL after a delay
                setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
            } else {
                // Fallback: open in new tab if download fails
                window.open(url, '_blank');
            }
        }.bind(this);
        
        xhr.onerror = function () {
            // Fallback: open in new tab if download fails
            window.open(url, '_blank');
        };
        
        xhr.open('GET', url);
        xhr.send();
    };

    addNewVendor = () => {
        const newVendorPart = {
            id: uuid(),
            vendorId: uuid(),
            vendorName: '',
            partId: editingPart.part.id,
            costType: 1,
            basePrice: 0.00,
            quantity: 1,
            partUrl: '',
            tieredCostFormulas: [],
            isNew: true // Flag to indicate this is a new vendor part
        };
        editingPart.part.vendorParts.push(newVendorPart);
        
        // If this is the first vendor part, set it as default
        if (editingPart.part.vendorParts.length === 1) {
            editingPart.part.selectedVendorId = newVendorPart.vendorId;
        }
        
        this.setState({editingPart});
    };

    openPurchaseDrawerFromVendor = (vendorPart) => {
        if (!editingPart || !editingPart.part || !this.state.parts) return;
        const partExpanded = this.state.parts.find(x => x.part.id === editingPart.part.id) || { part: editingPart.part };
        const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
        let initialPrice = 0;
        let initialQuantity = 1;
        if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas && vendorPart.tieredCostFormulas.length > 0) {
            const selectedTier = vendorPart.tieredCostFormulas.find(t => t.isSelected);
            const tier = selectedTier || [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity)[0];
            initialPrice = Number(tier.price).toFixed(2);
            initialQuantity = tier.quantity || 1;
        } else {
            initialPrice = Number(vendorPart.basePrice / (vendorPart.quantity || 1)).toFixed(3) || 0;
            initialQuantity = vendorPart.quantity || 1;
        }
        const total = parseFloat(initialPrice) * initialQuantity;
        const newLogParts = [{
            id: uuid(),
            partId: editingPart.part.id,
            description: partExpanded.part.description,
            vendorParts: editingPart.part.vendorParts,
            vendorId: vendorPart.vendorId,
            vendor: vendor?.name || vendorPart.vendorName,
            partQuantity: initialQuantity,
            price: initialPrice,
            total,
            isFamilyRow: false,
            isFamilyPart: false,
            selectedOption: {
                title: `${editingPart.part.partId} rev ${editingPart.part.revision}`,
                value: editingPart.part.id,
                isFamily: false,
                part: editingPart.part
            },
            isModifying: false
        }];
        const openDrawer = () => this.setState({
            purchaseDrawerOpen: true,
            newLogParts,
            newPurchaseOrderDate: dayjs(new Date().toUTCString()),
            newVendorOrderNumber: ''
        });
        if (this.state.partFamilies.length === 0) {
            this.loadPartFamilies().then(openDrawer);
        } else {
            openDrawer();
        }
    };

    closePurchaseDrawer = () => {
        this.setState({ purchaseDrawerOpen: false, newVendorOrderNumber: '' });
    };

    getTieredPricingTooltip = (vendorPart) => {
        if (!vendorPart) return '';
        if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
            return vendorPart.tieredCostFormulas
                .sort((a, b) => a.quantity - b.quantity)
                .map(tier => `${tier.quantity}: $${Number(tier.price).toFixed(2)} ea`)
                .join('\n');
        }
        return `${vendorPart.quantity}: $${Number(vendorPart.basePrice / vendorPart.quantity).toFixed(3) || '0.00'} ea`;
    };

    addNewLine = () => {
        const newLine = {
            id: uuid(),
            isFamilyRow: true,
            selectedOption: null,
            familyQuantity: 1,
            description: '',
            isFamilyPart: false,
            price: 0,
            partQuantity: 1,
            total: 0
        };
        this.setState(prevState => ({ newLogParts: [...prevState.newLogParts, newLine] }));
    };

    removeLine = (index) => {
        const newLogParts = [...this.state.newLogParts];
        newLogParts.splice(index, 1);
        this.setState({ newLogParts });
    };

    handlePartQuantityChange = (event, index) => {
        const quantity = event.target.value;
        const newLogParts = [...this.state.newLogParts];
        const changingPart = newLogParts[index];
        if (!changingPart) return;
        let newPrice = changingPart.price || 0;
        const vendorPart = changingPart.vendorParts?.find(vp => vp.vendorId === changingPart.vendorId);
        if (vendorPart && vendorPart.costType === 0 && !changingPart.isOverridden && vendorPart.tieredCostFormulas?.length > 0) {
            const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
            const tier = [...sortedTiers].reverse().find(t => quantity >= t.quantity) || sortedTiers[0];
            if (tier && tier.price !== undefined) newPrice = Number(tier.price).toFixed(2);
        }
        changingPart.partQuantity = quantity;
        changingPart.price = newPrice;
        changingPart.total = parseNumberInputValue(newPrice) * parseNumberInputValue(quantity);
        this.setState({ newLogParts });
    };

    handlePartPriceChange = (event, index) => {
        const newLogParts = [...this.state.newLogParts];
        const row = newLogParts[index];
        if (!row) return;
        const newPrice = event.target.value;
        const quantity = row.partQuantity || 0;
        row.price = newPrice;
        row.total = parseNumberInputValue(newPrice) * parseNumberInputValue(quantity);
        row.isOverridden = true;
        this.setState({ newLogParts });
    };

    handleVendorChange = (event, index) => {
        const newLogParts = [...this.state.newLogParts];
        const row = newLogParts[index];
        const vendorId = event.target.value;
        const vendor = this.state.vendors.find(x => x.id === vendorId);
        const vendorPart = row.vendorParts?.find(vp => vp.vendorId === vendorId);
        if (vendorPart) {
            let newPrice = 0, newQuantity = row.partQuantity || 0;
            if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
                const selectedTier = vendorPart.tieredCostFormulas.find(t => t.isSelected);
                const tier = selectedTier || [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity)[0];
                newPrice = Number(tier.price).toFixed(3);
                if (!row.isFamilyPart) newQuantity = tier.quantity;
            } else {
                newPrice = Number(vendorPart.basePrice / vendorPart.quantity).toFixed(3) || 0;
                if (!row.isFamilyPart) newQuantity = vendorPart.quantity || newQuantity;
            }
            row.vendor = vendor?.name;
            row.vendorId = vendorId;
            row.price = Number(newPrice).toFixed(3);
            row.partQuantity = newQuantity;
            row.total = newPrice * newQuantity;
            row.isOverridden = true;
        }
        this.setState({ newLogParts });
    };

    handleQuantityKeyDown = (event, currentIndex) => {
        if (event.key === 'Tab' && !event.shiftKey && currentIndex === this.state.newLogParts.length - 1) {
            event.preventDefault();
            this.addNewLine();
            setTimeout(() => {
                const nextInput = document.querySelector(`input[data-row-index="${this.state.newLogParts.length - 1}"][data-field-type="part-number"]`);
                if (nextInput) nextInput.focus();
            }, 100);
        }
    };

    handlePartDisplayClick = (rowIndex) => {
        const updatedRows = [...this.state.newLogParts];
        updatedRows[rowIndex].isModifying = true;
        this.setState({ newLogParts: updatedRows });
    };

    handleFamilyQuantityChange = (event, rowIndex) => {
        const newQuantity = event.target.value;
        const familyId = this.state.newLogParts[rowIndex].familyId;
        const updatedLogParts = this.state.newLogParts.map((row, index) => {
            if (index === rowIndex) return { ...row, familyQuantity: newQuantity };
            if (row.familyId === familyId && !row.isFamilyRow) {
                const part = this.state.parts.find(x => x.part.id === row.partId);
                if (!part) return row;
                const partToFamily = part.part.partToFamilies?.find(ptf => ptf.familyId === familyId);
                const purchaseQuantity = parseInt(partToFamily?.purchaseQuantity) || 1;
                const partQuantity = purchaseQuantity * parseNumberInputValue(newQuantity);
                const vendorPart = row.vendorParts?.find(vp => vp.vendorId === row.vendorId);
                let newPrice = Number(row.price).toFixed(3);
                if (vendorPart && vendorPart.costType === 0 && !row.isOverridden && vendorPart.tieredCostFormulas?.length > 0) {
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    const tier = [...sortedTiers].reverse().find(t => partQuantity >= t.quantity) || sortedTiers[0];
                    newPrice = Number(tier.price).toFixed(2);
                }
                return { ...row, partQuantity, price: Number(newPrice).toFixed(2), total: newPrice * partQuantity, isOverridden: false };
            }
            return row;
        });
        this.setState({ newLogParts: updatedLogParts });
    };

    handleFamilyChange = (event, newValue, index) => {
        if (!newValue) return;
        const newLogParts = [...this.state.newLogParts];
        if (newValue.isFamily) {
            const family = this.state.partFamilies.find(x => x.id === newValue.value);
            if (family) {
                newLogParts[index] = {
                    ...newLogParts[index],
                    familyId: family.id,
                    familyQuantity: 1,
                    isFamilyRow: true,
                    description: family.description,
                    partId: null,
                    partQuantity: null,
                    vendor: '',
                    price: 0,
                    total: 0,
                    selectedOption: { ...newValue, family }
                };
                const familyParts = this.state.parts.filter(part =>
                    part.part.partToFamilies?.some(ptf => ptf.familyId === family.id)
                );
                const filteredLogParts = newLogParts.filter((row, i) => i === index || row.familyId !== family.id);
                const filteredFamilyParts = familyParts.filter(part => {
                    const ptf = part.part.partToFamilies?.find(p => p.familyId === family.id);
                    return !ptf || !ptf.isImpliedPart;
                });
                filteredFamilyParts.forEach((part, i) => {
                    const partToFamily = part.part.partToFamilies?.find(ptf => ptf.familyId === family.id);
                    const vendorPart = part.part.vendorParts?.find(x => x.vendorId === part.part.selectedVendorId) || part.part.vendorParts?.[0];
                    const vendorName = vendorPart ? this.state.vendors.find(x => x.id === vendorPart.vendorId)?.name : '';
                    let initialPrice = 0, initialQuantity = partToFamily?.purchaseQuantity || 1;
                    if (vendorPart) {
                        if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
                            const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                            const tier = initialQuantity < sortedTiers[0].quantity ? sortedTiers[0] : [...sortedTiers].reverse().find(t => initialQuantity >= t.quantity) || sortedTiers[0];
                            initialPrice = Number(tier.price).toFixed(2);
                        } else {
                            initialPrice = Number(vendorPart.basePrice / vendorPart.quantity).toFixed(3) || 0;
                        }
                    }
                    filteredLogParts.splice(index + 1 + i, 0, {
                        id: uuid(),
                        familyId: family.id,
                        familyQuantity: 1,
                        partId: part.part.id,
                        partQuantity: initialQuantity,
                        description: part.part.description,
                        vendor: vendorName,
                        vendorId: vendorPart?.vendorId,
                        price: parseFloat(initialPrice),
                        total: initialPrice * initialQuantity,
                        isFamilyRow: false,
                        isOverridden: false,
                        isFamilyPart: true,
                        vendorParts: part.part.vendorParts,
                        selectedOption: { title: `${part.part.partId} rev ${part.part.revision}`, value: part.part.id, isFamily: false, part: part.part },
                        isModifying: false
                    });
                });
                this.setState({ newLogParts: filteredLogParts });
            }
        } else {
            const part = this.state.parts.find(x => x.part.id === newValue.value);
            if (part) {
                if (part.part.partToFamilies?.length === 1) {
                    const familyId = part.part.partToFamilies[0].familyId;
                    const partFamily = this.state.partFamilies.find(f => f.id === familyId);
                    if (partFamily) {
                        this.handleFamilyChange(event, { title: `${partFamily.partFamilyId} (Family)`, value: partFamily.id, isFamily: true, family: partFamily }, index);
                        return;
                    }
                }
                const vendorPart = part.part.vendorParts?.find(x => x.vendorId === part.part.selectedVendorId) || part.part.vendorParts?.[0];
                const vendorName = vendorPart ? this.state.vendors.find(x => x.id === vendorPart.vendorId)?.name : '';
                let initialPrice = 0, initialQuantity = 1;
                if (vendorPart) {
                    if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
                        const selectedTier = vendorPart.tieredCostFormulas.find(t => t.isSelected);
                        if (selectedTier) {
                            initialPrice = selectedTier.price;
                            initialQuantity = selectedTier.quantity;
                        } else {
                            const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                            initialPrice = sortedTiers[0].price;
                            initialQuantity = sortedTiers[0].quantity;
                        }
                    } else {
                        initialPrice = Number(vendorPart.basePrice / vendorPart.quantity).toFixed(3) || 0;
                        initialQuantity = vendorPart.quantity || 1;
                    }
                }
                newLogParts[index] = {
                    id: uuid(),
                    familyId: null,
                    familyQuantity: 1,
                    partId: part.part.id,
                    partQuantity: initialQuantity,
                    description: part.part.description,
                    vendor: vendorName,
                    vendorId: vendorPart?.vendorId,
                    price: parseFloat(initialPrice),
                    total: initialPrice * initialQuantity,
                    isFamilyRow: false,
                    isOverridden: false,
                    isFamilyPart: false,
                    selectedOption: { title: `${part.part.partId} rev ${part.part.revision}`, value: part.part.id, part: part.part },
                    vendorParts: part.part.vendorParts
                };
                this.setState({ newLogParts });
            }
        }
    };

    submitPurchaseOrder = () => {
        const self = this;
        this.setState({ savingPurchaseLog: true });
        const vendorGroups = {};
        this.state.newLogParts.filter(x => !x.isFamilyRow).forEach(part => {
            if (!vendorGroups[part.vendor]) vendorGroups[part.vendor] = [];
            vendorGroups[part.vendor].push(part);
        });
        const partService = ServiceLocator.Instance.getPartService();
        const promises = Object.entries(vendorGroups).map(([vendor, parts]) => {
            const payload = {
                id: uuid(),
                placedOn: this.state.newPurchaseOrderDate,
                vendorOrderNumber: this.state.newVendorOrderNumber,
                pricePaid: parts.reduce((sum, part) => sum + part.total, 0),
                invoices: [],
                packingSlips: [],
                userId: AuthenticationService.getInstance().getUserId(),
                purchasedParts: parts.map(part => {
                    const basePart = {
                        id: uuid(),
                        companyName: vendor,
                        quantityReceived: 0,
                        isPaid: false,
                        isReceived: false,
                        orderDate: this.state.newPurchaseOrderDate,
                        receivedDate: null,
                        pricePaid: part.total,
                        userId: AuthenticationService.getInstance().getUserId()
                    };
                    if (part.family && !part.quantity) {
                        return { ...basePart, partFamilyId: part.family.id, quantity: part.family.quantity };
                    }
                    return { ...basePart, partId: part.partId, quantity: part.partQuantity };
                })
            };
            return partService.createPurchaseOrder(payload);
        });
        Promise.all(promises).then(() => {
            const copyTablesByVendor = {};
            Object.entries(vendorGroups).forEach(([vendor, parts]) => {
                copyTablesByVendor[vendor] = parts.map((part) => {
                    const partExpanded = self.state.parts?.find((x) => x.part.id === part.partId);
                    return {
                        partNumber: partExpanded?.part.partId || 'Unknown',
                        revision: partExpanded?.part.revision ?? '',
                        quantity: part.partQuantity
                    };
                });
            });
            window.$alertSuccess("Purchase orders created");
            self.setState({
                purchaseDrawerOpen: false,
                savingPurchaseLog: false,
                newLogParts: [],
                showCopyTable: true,
                copyTablesByVendor
            });
            self.loadData();
        }).catch(() => {
            self.setState({ savingPurchaseLog: false });
        });
    };

    closeCopyTable = () => {
        this.setState({
            showCopyTable: false,
            copyTablesByVendor: {}
        });
    };

    copyVendorTableToClipboard = async (vendor, tableData) => {
        const tableHtml = `
            <table style="border-collapse: collapse; border: 1px solid black; font-family: Arial, sans-serif; font-size: 12px;">
                <thead>
                    <tr>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">Quantity</th>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">Item</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableData.map((item) =>
                        `<tr>
                            <td style="border: 1px solid black; padding: 8px; text-align: center;">${item.quantity}</td>
                            <td style="border: 1px solid black; padding: 8px; text-align: left;">${item.partNumber} rev ${item.revision}</td>
                        </tr>`
                    ).join('')}
                </tbody>
            </table>
        `;
        const plainText = `Item\tQuantity\n${tableData.map((item) =>
            `${item.partNumber} rev ${item.revision}\t${item.quantity}`
        ).join('\n')}`;
        try {
            if (window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([tableHtml], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' })
                    })
                ]);
                window.$alertSuccess(`${vendor} table copied to clipboard!`);
            } else {
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess(`${vendor} table copied to clipboard as text!`);
            }
        } catch (err) {
            console.error('Failed to copy table: ', err);
            window.$alertError('Failed to copy table to clipboard');
        }
    };

    changeVendor = (index, event) => {
        editingPart.part.vendorParts[index].vendorName = event.target.value;
        this.setState({editingPart});
    };

    changeVendorAutocomplete = (index, newValue) => {
        const vendorPart = editingPart.part.vendorParts[index];
        if (newValue && newValue.id) {
            // Existing vendor selected
            vendorPart.vendorId = newValue.id;
            vendorPart.vendorName = newValue.name;
        } else {
            // New vendor name entered
            vendorPart.vendorId = uuid();
            vendorPart.vendorName = newValue ? newValue.name || newValue : '';
        }
        this.setState({editingPart});
    };

    changeVendorInput = (index, newInputValue) => {
        const vendorPart = editingPart.part.vendorParts[index];
        // Update the vendor name as user types
        vendorPart.vendorName = newInputValue;
        // If it's a new name (not matching existing vendor), generate new ID
        const existingVendor = this.state.vendors.find(v => v.name === newInputValue);
        if (!existingVendor && newInputValue) {
            vendorPart.vendorId = uuid();
        }
        // Store the input value for filtering
        this.setState({
            editingPart,
            [`vendorInput_${index}`]: newInputValue
        });
    };

    removeVendorPart = (vendorPart) => {
        // Mark the vendor part for deletion instead of removing it immediately
        vendorPart.markedForDeletion = true;
        
        // If this was the selected vendor, select another vendor as default
        if (editingPart.part.selectedVendorId === vendorPart.vendorId) {
            const remainingVendorParts = editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion && vp !== vendorPart);
            if (remainingVendorParts.length > 0) {
                editingPart.part.selectedVendorId = remainingVendorParts[0].vendorId;
            } else {
                editingPart.part.selectedVendorId = null;
            }
        }
        
        this.setState({loading: true});
        setTimeout(() => {
            this.setState({editingPart, loading: false});
        }, 100);
    };

    changeQuantity = (vendorPart, newValue) => {
        vendorPart.quantity = newValue;
        this.setState({editingPart});
    };

    changeLotPrice = (vendorPart, newValue) => {
        vendorPart.basePrice = newValue;
        this.setState({editingPart});
    };

    changePartUrl = (vendorPart, newValue) => {
        vendorPart.partUrl = newValue;
        this.setState({editingPart});
    };

    openPartUrl = (url) => {
        if (url && url.trim() !== '') {
            // Ensure the URL has a protocol
            let formattedUrl = url.trim();
            if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
                formattedUrl = 'https://' + formattedUrl;
            }
            window.open(formattedUrl, '_blank');
        }
    };

    changeMSRP = (event) => {
        editingPart.part.msrp = event.target.value;
        this.setState({editingPart});
    };

    changeMsrpUseMarginPricing = (event) => {
        editingPart.part.msrpUseMarginPricing = event.target.checked;
        this.setState({editingPart: {...editingPart}});
    };

    changeMsrpMarginPercent = (event) => {
        editingPart.part.msrpMarginPercent = event.target.value;
        this.setState({editingPart: {...editingPart}});
    };

    changeMsrpRoundThreshold = (event) => {
        editingPart.part.msrpRoundThreshold = event.target.value;
        this.setState({editingPart: {...editingPart}});
    };

    changeMsrpRoundBelowIncrement = (event) => {
        editingPart.part.msrpRoundBelowIncrement = event.target.value;
        this.setState({editingPart: {...editingPart}});
    };

    changeMsrpRoundAtOrAboveIncrement = (event) => {
        editingPart.part.msrpRoundAtOrAboveIncrement = event.target.value;
        this.setState({editingPart: {...editingPart}});
    };

    getDefaultVendorPartForMsrp = () => {
        const part = editingPart?.part;
        if (!part) return null;
        const vendors = (part.vendorParts || []).filter(vp => !vp.markedForDeletion);
        if (vendors.length === 0) return null;
        let vp = vendors.find(v => v.vendorId === part.selectedVendorId);
        if (!vp && vendors.length === 1) {
            vp = vendors[0];
        }
        return vp || null;
    };

    getMsrpCostPreviewRows = () => {
        const vp = this.getDefaultVendorPartForMsrp();
        if (!vp) {
            return {kind: 'none', rows: []};
        }
        if (vp.costType === 1) {
            const qty = parseNumberInputValue(vp.quantity) || 0;
            const base = parseNumberInputValue(vp.basePrice);
            const unitCost = qty > 0 ? base / qty : base;
            return {kind: 'fixed', rows: [{quantity: qty, unitCost}]};
        }
        const tiers = [...(vp.tieredCostFormulas || [])].sort((a, b) =>
            parseNumberInputValue(a.quantity) - parseNumberInputValue(b.quantity));
        return {
            kind: 'tiered',
            rows: tiers.map(t => ({
                quantity: parseNumberInputValue(t.quantity),
                unitCost: parseNumberInputValue(t.price)
            }))
        };
    };

    computeRoundedMsrpForUnitCost = (unitCost) => {
        const part = editingPart.part;
        const margin = parseNumberInputValue(part.msrpMarginPercent) || 0;
        const th = parseNumberInputValue(part.msrpRoundThreshold);
        const low = parseNumberInputValue(part.msrpRoundBelowIncrement);
        const high = parseNumberInputValue(part.msrpRoundAtOrAboveIncrement);
        const threshold = Number.isFinite(th) ? th : DEFAULT_MSRP_ROUND_THRESHOLD;
        const lowInc = Number.isFinite(low) && low > 0 ? low : DEFAULT_MSRP_ROUND_BELOW_INCREMENT;
        const highInc = Number.isFinite(high) && high > 0 ? high : DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT;
        return applyMsrpMarginAndRounding(unitCost, margin, threshold, lowInc, highInc);
    };

    getMsrpTierDisplayRows = () => {
        const part = editingPart?.part;
        if (!part) return [];
        const preview = this.getMsrpCostPreviewRows();
        const saved = part.msrpTierLines || parseMsrpTierLines(part.msrpTiersJson);
        return buildMsrpTierDisplayRows(preview, saved, (unitCost) =>
            this.computeRoundedMsrpForUnitCost(unitCost)
        );
    };

    commitMsrpTierDisplayRows = (displayRows) => {
        updatePartMsrpTierLinesFromDisplay(editingPart.part, displayRows);
        this.setState({ editingPart: { ...editingPart } });
    };

    addMsrpTierLine = () => {
        const saved = [...(editingPart.part.msrpTierLines || parseMsrpTierLines(editingPart.part.msrpTiersJson))];
        saved.push(createManualMsrpTierLine(1, null));
        editingPart.part.msrpTierLines = saved;
        editingPart.part.msrpTiersJson = serializeMsrpTierLines(saved);
        this.setState({ editingPart: { ...editingPart } });
    };

    changeMsrpTierIncluded = (rowId, included) => {
        const displayRows = this.getMsrpTierDisplayRows().map((row) =>
            row.id === rowId ? { ...row, excluded: !included } : row
        );
        this.commitMsrpTierDisplayRows(displayRows);
    };

    changeMsrpTierQuantity = (rowId, value) => {
        const qty = parseNumberInputValue(value);
        if (!Number.isFinite(qty) || qty <= 0) return;
        const displayRows = this.getMsrpTierDisplayRows().map((row) => {
            if (row.id !== rowId) return row;
            return { ...row, quantity: qty };
        });
        this.commitMsrpTierDisplayRows(displayRows);
    };

    changeMsrpTierMsrp = (rowId, value) => {
        const displayRows = this.getMsrpTierDisplayRows().map((row) => {
            if (row.id !== rowId) return row;
            const msrpOverride =
                value === '' || value == null
                    ? null
                    : parseOptionalMsrpValue(value);
            const effectiveMsrp =
                msrpOverride != null
                    ? msrpOverride
                    : row.source === 'vendor' && Number.isFinite(row.calculatedMsrp)
                        ? row.calculatedMsrp
                        : null;
            return { ...row, msrpOverride, effectiveMsrp };
        });
        this.commitMsrpTierDisplayRows(displayRows);
    };

    clearMsrpTierOverride = (rowId) => {
        const displayRows = this.getMsrpTierDisplayRows().map((row) => {
            if (row.id !== rowId || row.source !== 'vendor') return row;
            const effectiveMsrp = Number.isFinite(row.calculatedMsrp) ? row.calculatedMsrp : null;
            return { ...row, msrpOverride: null, effectiveMsrp };
        });
        this.commitMsrpTierDisplayRows(displayRows);
    };

    removeMsrpTierLine = (rowId) => {
        const target = this.getMsrpTierDisplayRows().find((row) => row.id === rowId);
        if (!target) return;
        if (target.source === 'manual') {
            const saved = (editingPart.part.msrpTierLines || []).filter((line) => line.id !== rowId);
            editingPart.part.msrpTierLines = saved;
            editingPart.part.msrpTiersJson = serializeMsrpTierLines(saved);
            this.setState({ editingPart: { ...editingPart } });
            return;
        }
        const displayRows = this.getMsrpTierDisplayRows().map((row) =>
            row.id === rowId ? { ...row, excluded: true } : row
        );
        this.commitMsrpTierDisplayRows(displayRows);
    };

    getMsrpCopyTableData = () => {
        const part = editingPart?.part;
        if (!part?.partId) return null;

        if (part.msrpUseMarginPricing) {
            const copyRows = getActiveMsrpTierCopyRows(this.getMsrpTierDisplayRows());
            if (!copyRows.length) return null;
            return { partNumber: part.partId, revision: part.revision ?? '', rows: copyRows };
        }

        const msrp = parseNumberInputValue(part.msrp);
        if (!Number.isFinite(msrp)) return null;
        return { partNumber: part.partId, revision: part.revision ?? '', rows: [{ quantity: 1, msrp }] };
    };

    openMsrpCopyTable = () => {
        if (editingPart?.part?.msrpUseMarginPricing) {
            updatePartMsrpTierLinesFromDisplay(editingPart.part, this.getMsrpTierDisplayRows());
        }
        const data = this.getMsrpCopyTableData();
        if (!data) {
            window.$alertWarning('No MSRP tiers available to copy.');
            return;
        }
        this.setState({ showMsrpCopyTable: true, msrpCopyTableData: data });
    };

    closeMsrpCopyTable = () => {
        this.setState({ showMsrpCopyTable: false, msrpCopyTableData: null });
    };

    copyMsrpTableToClipboard = async () => {
        const data = this.state.msrpCopyTableData;
        if (!data) return;

        const partHeader = formatMsrpCopyPartHeader(data.partNumber, data.revision);

        const tableHtml = `
            <table style="border-collapse: collapse; border: 1px solid black; font-family: Arial, sans-serif; font-size: 12px;">
                <thead>
                    <tr>
                        <th colspan="2" style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">${partHeader}</th>
                    </tr>
                </thead>
                <tbody>
                    ${data.rows.map((row) =>
                        `<tr>
                            <td style="border: 1px solid black; padding: 8px; text-align: center;">${row.quantity}</td>
                            <td style="border: 1px solid black; padding: 8px; text-align: right;">${formatMsrpCopyPriceDisplay(row.msrp)}</td>
                        </tr>`
                    ).join('')}
                </tbody>
            </table>
        `;

        const plainText = [
            partHeader,
            ...data.rows.map((row) => formatMsrpCopyPlainLine(row.quantity, row.msrp)),
        ].join('\n');

        try {
            if (window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([tableHtml], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' }),
                    }),
                ]);
                window.$alertSuccess('MSRP table copied to clipboard!');
            } else {
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess('MSRP table copied to clipboard as text!');
            }
        } catch (err) {
            console.error('Failed to copy MSRP table: ', err);
            window.$alertError('Failed to copy table to clipboard');
        }
    };

    changeLeadTime = (event) => {
        editingPart.part.leadTimeInWeeks = event.target.value;
        this.setState({editingPart});
    };

    changeWeightPounds = (event) => {
        const pounds = parseInt(event.target.value) || 0;
        editingPart.part.weightPounds = pounds;
        this.setState({editingPart});
    };

    changeWeightOunces = (event) => {
        let ounces = parseInt(event.target.value) || 0;

        if (ounces >= 16) {
            const additionalPounds = Math.floor(ounces / 16);
            const remainingOunces = ounces % 16;

            editingPart.part.weightPounds = (editingPart.part.weightPounds || 0) + additionalPounds;
            editingPart.part.weightOunces = remainingOunces;
        } else {
            editingPart.part.weightOunces = ounces;
        }

        this.setState({editingPart});
    };

    changeIsSmallBinSize = (event) => {
        editingPart.part.isSmallBinSize = event.target.checked;
        this.setState({editingPart});
    };

    changeIsInventoryItem = (event) => {
        editingPart.part.isInventoryItem = event.target.checked;
        this.setState({editingPart});
    };

    changeIsOnBackorder = (event) => {
        editingPart.part.isOnBackorder = event.target.checked;
        if (!event.target.checked) {
            editingPart.part.backorderEta = null;
        }
        this.setState({editingPart});
    };

    changeBackorderEta = (date) => {
        editingPart.part.backorderEta = date;
        this.setState({editingPart});
    };

    changeReorderByMinimumQuantity = (event) => {
        editingPart.part.reorderByMinimumQuantity = event.target.checked;
        this.setState({editingPart});
    };

    changeMinimumQuantity = (event) => {
        editingPart.part.minimumQuantity = event.target.value;
        this.setState({editingPart});
    };

    // New method to handle checkbox change for any revision
    handleRevisionCurrentlyPurchasingChange = async (partId, checked) => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        try {
            // If unchecking, just update that part
            if (!checked) {
                const partToUpdate = self.state.parts.find(p => p.part.id === partId);
                if (!partToUpdate) return;
                
                // Update local state immediately
                const updatedParts = self.state.parts.map(p => ({
                    ...p,
                    part: p.part.id === partId 
                        ? { ...p.part, currentlyPurchasing: false }
                        : p.part
                }));
                
                // Update editingPart if it's the one being changed
                if (editingPart.part.id === partId) {
                    editingPart.part.currentlyPurchasing = false;
                    self.setState({ parts: updatedParts, editingPart });
                } else {
                    self.setState({ parts: updatedParts });
                }
                
                // Create clean part object for API call
                // Convert fileURLs to string if it's an array
                let fileURLsString = '';
                if (Array.isArray(partToUpdate.part.fileURLs)) {
                    fileURLsString = partToUpdate.part.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
                } else if (typeof partToUpdate.part.fileURLs === 'string') {
                    fileURLsString = partToUpdate.part.fileURLs;
                }
                
                const cleanPart = {
                    id: partToUpdate.part.id,
                    partType: partToUpdate.part.partType,
                    manufacturerFor: partToUpdate.part.manufacturerFor,
                    chassisFor: partToUpdate.part.chassisFor,
                    tertiaryType: partToUpdate.part.tertiaryType,
                    brand: partToUpdate.part.brand,
                    partId: partToUpdate.part.partId,
                    revision: partToUpdate.part.revision,
                    revisionNotes: partToUpdate.part.revisionNotes,
                    description: partToUpdate.part.description,
                    customerSiteDescription: partToUpdate.part.customerSiteDescription,
                    imageURL: partToUpdate.part.imageURL,
                    fileURLs: fileURLsString,
                    costType: partToUpdate.part.costType,
                    isActive: partToUpdate.part.isActive,
                    isSmallBinSize: partToUpdate.part.isSmallBinSize,
                    basePrice: partToUpdate.part.basePrice,
                    quantity: partToUpdate.part.quantity,
                    minimumQuantity: partToUpdate.part.minimumQuantity,
                    reorderByMinimumQuantity: !!partToUpdate.part.reorderByMinimumQuantity,
                    msrp: partToUpdate.part.msrp,
                    msrpUseMarginPricing: !!partToUpdate.part.msrpUseMarginPricing,
                    msrpMarginPercent: partToUpdate.part.msrpMarginPercent,
                    msrpRoundThreshold: partToUpdate.part.msrpRoundThreshold,
                    msrpRoundBelowIncrement: partToUpdate.part.msrpRoundBelowIncrement,
                    msrpRoundAtOrAboveIncrement: partToUpdate.part.msrpRoundAtOrAboveIncrement,
                    msrpTiersJson: partToUpdate.part.msrpTiersJson ?? serializeMsrpTierLines(partToUpdate.part.msrpTierLines) ?? null,
                    leadTimeInWeeks: partToUpdate.part.leadTimeInWeeks,
                    inventoryStrategy: partToUpdate.part.inventoryStrategy,
                    inventoryCorrection: partToUpdate.part.inventoryCorrection,
                    supersededById: partToUpdate.part.supersededById,
                    supersedesId: partToUpdate.part.supersedesId,
                    effectiveDate: partToUpdate.part.effectiveDate,
                    currentlyPurchasing: false,
                    selectedVendorId: partToUpdate.part.selectedVendorId,
                    vendorParts: partToUpdate.part.vendorParts,
                    isInventoryItem: partToUpdate.part.isInventoryItem
                };
                
                await partService.updatePart(cleanPart);
                
            } else {
                // If checking, we need to uncheck all others for this part number and check this one
                const currentPartNumber = self.state.parts.find(p => p.part.id === partId)?.part.partId;
                if (!currentPartNumber) return;
                
                // Get all parts with the same part number
                const samePartNumberParts = self.state.parts.filter(p => p.part.partId === currentPartNumber);
                
                // Update local state immediately - uncheck all others, check this one
                const updatedParts = self.state.parts.map(p => ({
                    ...p,
                    part: p.part.partId === currentPartNumber 
                        ? { ...p.part, currentlyPurchasing: p.part.id === partId }
                        : p.part
                }));
                
                // Update editingPart if it's one of the parts being changed
                if (editingPart.part.partId === currentPartNumber) {
                    editingPart.part.currentlyPurchasing = editingPart.part.id === partId;
                    self.setState({ parts: updatedParts, editingPart });
                } else {
                    self.setState({ parts: updatedParts });
                }
                
                // Update all parts with the same part number via API
                const updatePromises = samePartNumberParts.map(partData => {
                    // Convert fileURLs to string if it's an array
                    let fileURLsString = '';
                    if (Array.isArray(partData.part.fileURLs)) {
                        fileURLsString = partData.part.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
                    } else if (typeof partData.part.fileURLs === 'string') {
                        fileURLsString = partData.part.fileURLs;
                    }
                    
                    const cleanPart = {
                        id: partData.part.id,
                        partType: partData.part.partType,
                        manufacturerFor: partData.part.manufacturerFor,
                        chassisFor: partData.part.chassisFor,
                        tertiaryType: partData.part.tertiaryType,
                        brand: partData.part.brand,
                        partId: partData.part.partId,
                        revision: partData.part.revision,
                        revisionNotes: partData.part.revisionNotes,
                        description: partData.part.description,
                        customerSiteDescription: partData.part.customerSiteDescription,
                        imageURL: partData.part.imageURL,
                        fileURLs: fileURLsString,
                        costType: partData.part.costType,
                        isActive: partData.part.isActive,
                        isSmallBinSize: partData.part.isSmallBinSize,
                        basePrice: partData.part.basePrice,
                        quantity: partData.part.quantity,
                        minimumQuantity: partData.part.minimumQuantity,
                        reorderByMinimumQuantity: !!partData.part.reorderByMinimumQuantity,
                        msrp: partData.part.msrp,
                        msrpUseMarginPricing: !!partData.part.msrpUseMarginPricing,
                        msrpMarginPercent: partData.part.msrpMarginPercent,
                        msrpRoundThreshold: partData.part.msrpRoundThreshold,
                        msrpRoundBelowIncrement: partData.part.msrpRoundBelowIncrement,
                        msrpRoundAtOrAboveIncrement: partData.part.msrpRoundAtOrAboveIncrement,
                        msrpTiersJson: partData.part.msrpTiersJson ?? serializeMsrpTierLines(partData.part.msrpTierLines) ?? null,
                        leadTimeInWeeks: partData.part.leadTimeInWeeks,
                        inventoryStrategy: partData.part.inventoryStrategy,
                        inventoryCorrection: partData.part.inventoryCorrection,
                        supersededById: partData.part.supersededById,
                        supersedesId: partData.part.supersedesId,
                        effectiveDate: partData.part.effectiveDate,
                        currentlyPurchasing: partData.part.id === partId,
                        selectedVendorId: partData.part.selectedVendorId,
                        vendorParts: partData.part.vendorParts,
                        isInventoryItem: partData.part.isInventoryItem
                    };
                    
                    return partService.updatePartNoCache(cleanPart);
                });
                
                await Promise.all(updatePromises);
            }
            
            // Manually update the cache with the correct data after all operations complete
            await self.updatePartsCacheAfterBatchUpdate();
            
            window.$alertSuccess("Part updated successfully");
        } catch (error) {
            console.error('Error updating part:', error);
            window.$alertError("Error updating part");
            // Revert local state on error
            self.loadData();
        }
    };

    // Method to manually update the parts cache after batch operations
    updatePartsCacheAfterBatchUpdate = async () => {
        try {
            
            // Get the current cached parts
            const cachedPartsCompressed = localStorage.getItem('parts');
            if (!cachedPartsCompressed) {
                console.log('No cached parts data found, skipping cache update');
                return;
            }

            const cachedParts = JSON.parse(LzString.decompress(cachedPartsCompressed));
            if (!Array.isArray(cachedParts)) {
                console.log('Invalid cached parts data, skipping cache update');
                return;
            }

            // Create a map of updated parts from the current state
            const updatedPartsMap = new Map();
            this.state.parts.forEach(partData => {
                updatedPartsMap.set(partData.part.id, partData.part);
            });

            // Update the cached parts with the current state data
            const updatedCachedParts = cachedParts.map(cachedPart => {
                const partId = cachedPart.part?.id || cachedPart.id;
                const updatedPart = updatedPartsMap.get(partId);
                if (updatedPart) {
                    // Return the updated part with the same structure as cached
                    return {
                        ...cachedPart,
                        part: {
                            ...cachedPart.part,
                            currentlyPurchasing: updatedPart.currentlyPurchasing
                        }
                    };
                }
                return cachedPart;
            });

            // Save the updated cache
            localStorage.setItem('parts', LzString.compress(JSON.stringify(updatedCachedParts)));
            
            // Update the timestamp to force a refresh if needed
            const partService = ServiceLocator.Instance.getPartService();
            const updatedOn = await partService.getTableUpdatedDate('Parts');
            localStorage.setItem('partsUpdated', updatedOn);
            
            console.log('Parts cache updated successfully after batch operation');
        } catch (error) {
            console.error('Error updating parts cache:', error);
        }
    };

    changeSupersedes = (event, newValue) => {
        if (!newValue) {
            editingPart.part.supersedesId = null;
            this.setState({editingPart});
        } else {
            editingPart.part.supersedesId = newValue.id;
            this.setState({editingPart});
        }
    };

    changeSupersededBy = (event, newValue) => {
        if (!newValue) {
            editingPart.part.supersededById = null;
            this.setState({editingPart});
        } else {
            editingPart.part.supersededById = newValue.id;
            this.setState({editingPart});
        }
    };

    savePart = () => {
        const self = this;
        self.setState({savingPart: true, chassisInputPart: ''});
        const partService = ServiceLocator.Instance.getPartService();
        
        // Check for duplicate partId/revision combination
        const existingPart = this.state.parts.find(x => 
            x.part.partId.toLowerCase().trim() === editingPart.part.partId.toLowerCase().trim() && 
            x.part.revision.toLowerCase().trim() === editingPart.part.revision.toLowerCase().trim() &&
            x.part.id !== editingPart.part.id // Exclude current part when updating
        );
        
        if (existingPart) {
            self.setState({savingPart: false});
            return window.$alertWarning("A part already exists with that Part Number and Revision!");
        }
        
        // Check for empty vendor names (excluding vendor parts marked for deletion)
        const emptyVendorNames = editingPart.part.vendorParts.filter(vendorPart => 
            !vendorPart.markedForDeletion && (!vendorPart.vendorName || vendorPart.vendorName.trim() === '')
        );
        
        if (emptyVendorNames.length > 0) {
            self.setState({savingPart: false});
            return window.$alertWarning("Vendor names cannot be left blank. Please enter a name for all vendors.");
        }
        
        Promise.all(this.saveNewVendors()).then(() => {
            editingPart.part.imageURL = editingPart.part.imageURLs.filter(x => x !== '').join('~|~');
            // Ensure fileURLs is always a string (empty string if no files)
            // First ensure it's an array
            let fileURLsArray = editingPart.part.fileURLs;
            if (!fileURLsArray) {
                fileURLsArray = [];
            } else if (typeof fileURLsArray === 'string') {
                fileURLsArray = fileURLsArray.split('~|~').filter(x => x && x.trim() !== '');
            } else if (!Array.isArray(fileURLsArray)) {
                fileURLsArray = [];
            }
            // Filter out empty entries and convert to string
            const filteredFileURLs = fileURLsArray.filter(x => x !== '' && x != null && x.trim() !== '');
            // Ensure empty array becomes empty string for database
            editingPart.part.fileURLs = filteredFileURLs.length > 0 ? filteredFileURLs.join('~|~') : '';
            console.log('Saving fileURLs:', editingPart.part.fileURLs, 'from array:', fileURLsArray, 'filtered:', filteredFileURLs, 'cleanPart will have:', (editingPart.part.fileURLs !== undefined && editingPart.part.fileURLs !== null) ? editingPart.part.fileURLs : '');
            
            // Ensure selectedVendorId is valid (not pointing to a deleted vendor part)
            const validVendorParts = editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion);
            if (editingPart.part.selectedVendorId && !validVendorParts.find(vp => vp.vendorId === editingPart.part.selectedVendorId)) {
                // Selected vendor was deleted, select the first remaining vendor
                if (validVendorParts.length > 0) {
                    editingPart.part.selectedVendorId = validVendorParts[0].vendorId;
                } else {
                    editingPart.part.selectedVendorId = null;
                }
            }
            
            if (validVendorParts.length === 1) editingPart.part.selectedVendorId = validVendorParts[0].vendorId;

            if (editingPart.part.msrpUseMarginPricing) {
                updatePartMsrpTierLinesFromDisplay(editingPart.part, this.getMsrpTierDisplayRows());
            }

            if (!this.state.useCustomStoreDescription) {
                editingPart.part.customerSiteDescription = null;
            } else if (!(editingPart.part.customerSiteDescription || '').trim()) {
                editingPart.part.customerSiteDescription = null;
            }
            
            // Create a clean part object without navigation properties
            const cleanPart = {
                id: editingPart.part.id,
                partType: editingPart.part.partType,
                manufacturerFor: editingPart.part.manufacturerFor,
                chassisFor: editingPart.part.chassisFor,
                tertiaryType: editingPart.part.tertiaryType,
                brand: editingPart.part.brand,
                partId: editingPart.part.partId,
                revision: editingPart.part.revision,
                revisionNotes: editingPart.part.revisionNotes,
                description: editingPart.part.description,
                customerSiteDescription: editingPart.part.customerSiteDescription,
                imageURL: editingPart.part.imageURL,
                fileURLs: (editingPart.part.fileURLs !== undefined && editingPart.part.fileURLs !== null) ? editingPart.part.fileURLs : '',
                costType: editingPart.part.costType,
                isActive: editingPart.part.isActive,
                isSmallBinSize: editingPart.part.isSmallBinSize,
                basePrice: parseNumberInputValue(editingPart.part.basePrice),
                quantity: parseNumberInputValue(editingPart.part.quantity),
                msrp: parseNumberInputValue(editingPart.part.msrp),
                msrpUseMarginPricing: !!editingPart.part.msrpUseMarginPricing,
                msrpMarginPercent: parseNumberInputValue(editingPart.part.msrpMarginPercent),
                msrpRoundThreshold: parseNumberInputValue(editingPart.part.msrpRoundThreshold) || 15,
                msrpRoundBelowIncrement: parseNumberInputValue(editingPart.part.msrpRoundBelowIncrement) || 0.25,
                msrpRoundAtOrAboveIncrement: parseNumberInputValue(editingPart.part.msrpRoundAtOrAboveIncrement) || 1,
                msrpTiersJson: editingPart.part.msrpTiersJson ?? serializeMsrpTierLines(editingPart.part.msrpTierLines) ?? null,
                leadTimeInWeeks: parseNumberInputValue(editingPart.part.leadTimeInWeeks),
                minimumQuantity: parseNumberInputValue(editingPart.part.minimumQuantity),
                reorderByMinimumQuantity: !!editingPart.part.reorderByMinimumQuantity,
                inventoryStrategy: editingPart.part.inventoryStrategy,
                inventoryCorrection: editingPart.part.inventoryCorrection,
                supersededById: editingPart.part.supersededById,
                supersedesId: editingPart.part.supersedesId,
                effectiveDate: editingPart.part.effectiveDate,
                currentlyPurchasing: editingPart.part.currentlyPurchasing,
                isOnBackorder: !!editingPart.part.isOnBackorder,
                backorderEta: editingPart.part.isOnBackorder ? editingPart.part.backorderEta || null : null,
                selectedVendorId: editingPart.part.selectedVendorId,
                vendorParts: validVendorParts.map(vendorPart => ({
                        ...vendorPart,
                        quantity: parseNumberInputValue(vendorPart.quantity),
                        basePrice: parseNumberInputValue(vendorPart.basePrice),
                        partUrl: vendorPart.partUrl || '',
                        tieredCostFormulas: vendorPart.tieredCostFormulas?.map(formula => ({
                            ...formula,
                            quantity: parseNumberInputValue(formula.quantity),
                            price: parseNumberInputValue(formula.price)
                        })) || []
                    })),
                isInventoryItem: editingPart.part.isInventoryItem
            };
            
            if (editingPart.part.id === '') {
                cleanPart.id = uuid();
                partService.createPart(cleanPart).then((part) => {
                    window.$alertSuccess("Part created");
                    self.setState({savingPart: false});
                    // Clear cache to force fresh data from server
                    localStorage.removeItem('parts');
                    localStorage.removeItem('partsUpdated');
                    // Reload data and ensure fileURLs is converted to array
                    self.loadData().then(() => {
                        // After reload, ensure fileURLs is an array in the state
                        if (editingPart && editingPart.part) {
                            if (typeof editingPart.part.fileURLs === 'string') {
                                editingPart.part.fileURLs = editingPart.part.fileURLs && editingPart.part.fileURLs.trim() !== ''
                                    ? editingPart.part.fileURLs.split('~|~').filter(x => x && x.trim() !== '')
                                    : [];
                            } else if (!Array.isArray(editingPart.part.fileURLs)) {
                                editingPart.part.fileURLs = [];
                            }
                            console.log('After reload (create), fileURLs:', editingPart.part.fileURLs);
                            self.setState({editingPart});
                        }
                    });
                });
            } else {
                partService.updatePart(cleanPart).then((part) => {
                    console.log('Update response from server:', part);
                    window.$alertSuccess("Part updated");
                    self.setState({savingPart: false});
                    // Clear cache to force fresh data from server
                    localStorage.removeItem('parts');
                    localStorage.removeItem('partsUpdated');
                    // Small delay to ensure server has processed the update
                    setTimeout(() => {
                        // Reload data and ensure fileURLs is converted to array
                        self.loadData().then(() => {
                            // After reload, ensure fileURLs is an array in the state
                            if (editingPart && editingPart.part) {
                                console.log('After reload, editingPart.part.fileURLs (raw):', editingPart.part.fileURLs, 'type:', typeof editingPart.part.fileURLs);
                                if (typeof editingPart.part.fileURLs === 'string') {
                                    editingPart.part.fileURLs = editingPart.part.fileURLs && editingPart.part.fileURLs.trim() !== ''
                                        ? editingPart.part.fileURLs.split('~|~').filter(x => x && x.trim() !== '')
                                        : [];
                                } else if (!Array.isArray(editingPart.part.fileURLs)) {
                                    editingPart.part.fileURLs = [];
                                }
                                console.log('After reload (update), fileURLs:', editingPart.part.fileURLs);
                                self.setState({editingPart});
                            }
                        });
                    }, 500);
                });
            }
        });
    };

    saveNewVendors = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return editingPart.part.vendorParts
            .filter(vendorPart => !vendorPart.markedForDeletion) // Only process vendor parts not marked for deletion
            .map((vendorPart) => {
            // Skip if vendor already has a valid ID that exists in our vendors list
            if (this.state.vendors.find(x => x.id === vendorPart.vendorId)) {
                return Promise.resolve();
            }
            
            // Search for existing vendor by name
            const existingVendor = this.state.vendors.find(x => 
                x.name.toLowerCase().trim() === vendorPart.vendorName.toLowerCase().trim()
            );
            
            if (existingVendor) {
                // Use existing vendor
                const oldVendorId = vendorPart.vendorId;
                vendorPart.vendorId = existingVendor.id;
                
                // Update selectedVendorId if it was pointing to the old vendorId
                if (editingPart.part.selectedVendorId === oldVendorId) {
                    editingPart.part.selectedVendorId = existingVendor.id;
                }
                
                return Promise.resolve();
            } else {
                // Create new vendor
                const oldVendorId = vendorPart.vendorId;
                return partService.createVendor({id: vendorPart.vendorId, name: vendorPart.vendorName}).then((newVendor) => {
                    vendorPart.vendorId = newVendor.id;
                    
                    // Update selectedVendorId if it was pointing to the old vendorId
                    if (editingPart.part.selectedVendorId === oldVendorId) {
                        editingPart.part.selectedVendorId = newVendor.id;
                    }
                    
                    // Add the new vendor to the vendors state
                    this.setState(prevState => ({
                        vendors: [...prevState.vendors, newVendor]
                    }));
                    
                    return newVendor;
                });
            }
        });
    };

    deactivatePart = () => {
        const bomAssemblies = assemblies.filter(x => x.assembly.isActive && x.assembly.partsToAssemblies.find(y => y.partFk === editingPart.part.id));
        const bomSubAssemblies = subAssemblies.filter(x => x.subAssembly.isActive && x.subAssembly.partsToSubAssemblies.find(y => y.partFk === editingPart.part.id));

        if (bomAssemblies.length > 0 || bomSubAssemblies.length > 0) {
            return window.$alertError("You cannot deactivate this part. It is in the BOM of " + (bomAssemblies.length + bomSubAssemblies.length) + " active assemblies.");
        }

        const confirm = window.confirm("Are you sure you want to deactivate this part?");
        if (!confirm) return;

        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        editingPart.part.isActive = false;
        self.setState({savingPart: true});
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingPart.part.fileURLs)) {
            fileURLsString = editingPart.part.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingPart.part.fileURLs === 'string') {
            fileURLsString = editingPart.part.fileURLs;
        }
        
        const partToUpdate = {
            ...editingPart.part,
            fileURLs: fileURLsString
        };
        
        partService.updatePart(partToUpdate).then(() => {
            window.$alertSuccess("Part deactivated");
            self.setState({savingPart: false});
            globalProps.history.push('/admin/parts');
        });
    };

    reactivatePart = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        editingPart.part.isActive = true;
        self.setState({savingPart: true});
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingPart.part.fileURLs)) {
            fileURLsString = editingPart.part.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingPart.part.fileURLs === 'string') {
            fileURLsString = editingPart.part.fileURLs;
        }
        
        const partToUpdate = {
            ...editingPart.part,
            fileURLs: fileURLsString
        };
        
        partService.updatePart(partToUpdate).then(() => {
            window.$alertSuccess("Part reactivated");
            self.setState({editingPart, savingPart: false});
        });
    };

    deletePart = () => {
        if (window.confirm("Are you certain you want to permanently delete this part?")) {
            this.setState({savingPart: true});
            const partService = ServiceLocator.Instance.getPartService();
            partService.deletePart(editingPart.part.id).then(() => {
                window.$alertSuccess("Part deleted successfully");
                globalProps.history.push('/admin/parts');
            });
        }
    };

    printPartLabel = () => {
        if (!this.state.editingPart || !this.state.editingPart.part) return;
        
        const part = this.state.editingPart.part;
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        if (part.isSmallBinSize) {
            // New 4" x 2" small label design (using only left 3" for content, right 1" is scrap)
            let partNumberFontSize;
            if (part.partId.length >= 18) {
                partNumberFontSize = '20px';
            } else if (part.partId.length >= 16) {
                partNumberFontSize = '24px';
            } else if (part.partId.length >= 14) {
                partNumberFontSize = '28px';
            } else if (part.partId.length >= 12) {
                partNumberFontSize = '32px';
            } else {
                partNumberFontSize = '36px';
            }
            containerHtml += `<div class="label-small-4x2">
                <div class="label-content-area">
                    <div class="label-text-area">
                        <div class="part-number" style="font-size: ${partNumberFontSize};">${part.partId}</div>
                        <div class="revision">rev ${part.revision}</div>
                        <div class="description">${part.description}</div>
                    </div>
                    <div class="label-qr-area">
                        <div class="qr-left">
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + part.id)}" style="width: 70px; height: 70px;"/>
                            <div class="qr-label" style="font-size: 10px;">Homepage</div>
                        </div>
                        <div class="qr-right">
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${part.id}" style="width: 70px; height: 70px;"/>
                            <div class="qr-label" style="font-size: 10px;">BOM Check</div>
                        </div>
                    </div>
                    <div class="cut-line-top"></div>
                    <div class="cut-line-bottom"></div>
                </div>
                <div class="scrap-area"></div>
            </div>`;
        } else {
            // Standard large label (unchanged)
            containerHtml += `<div class="label-standard">
                <div class="label-left">
                    <div style="text-align: center;">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + part.id)}" style="width: 100px; height: 100px;"/>
                        <div style="font-size: 11px; margin-top: 10px;">Homepage</div>
                    </div>
                </div>
                <div class="label-center">
                    <div style="font-weight: 700; font-size: 19px;">${part.partId}<br />rev ${part.revision}</div>
                    <div style="font-weight: 700; font-size: 8px;">${part.description}</div>
                </div>
                <div class="label-right">
                    <div style="text-align: center;">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${part.id}" style="width: 100px; height: 100px;"/>
                        <div style="font-size: 11px; margin-top: 10px;">BOM Check</div>
                    </div>
                </div>
            </div>`;
        }
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100`);
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .label-left, .label-right {
                            width: 30%;
                            display: inline-block;
                            text-align: center;
                        }
                        .label-center {
                            width: 40%;
                            display: inline-block;
                            text-align: center;
                            word-break: break-all;
                        }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: ${part.isSmallBinSize ? '4in' : '4in'};
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-standard {
                            height: 2in;
                            text-align: center;
                            display: flex;
                            align-items: center;
                            position: relative;
                            padding: 0 10px;
                        }
                        .label-small-4x2 {
                            width: 4in;
                            height: 2in;
                            position: relative;
                            border: 0px solid #000;
                            font-weight: 800;
                            display: flex;
                        }
                        .label-content-area {
                            width: 3in;
                            height: 2in;
                            position: relative;
                            border-right: 1px dashed #666;
                        }
                        .scrap-area {
                            width: 1in;
                            height: 2in;
                            background-color: #f0f0f0;
                        }
                        .label-text-area {
                            width: 100%;
                            height: 0.75in;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .part-number {
                            font-weight: 900;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .revision {
                            font-size: 8px;
                            font-weight: 600;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .description {
                            font-size: 7px;
                            font-weight: 500;
                            text-align: center;
                            word-break: break-word;
                            max-width: 100%;
                            line-height: 1.0;
                        }
                        .label-qr-area {
                            width: 100%;
                            height: 1in;
                            display: flex;
                            justify-content: space-around;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .qr-left, .qr-right {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        }
                        .qr-label {
                            font-size: 6px;
                            margin-top: 1px;
                            font-weight: 600;
                        }
                        .cut-line-top {
                            position: absolute;
                            top: 0.75in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                        .cut-line-bottom {
                            position: absolute;
                            bottom: 0.25in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    cloneNewRevision = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        if (!this.state.newRevision || this.state.newRevision.trim() === '') {
            window.$alertError("Please enter a revision number");
            return;
        }

        self.setState({savingPart: true});
        
        // Check for duplicate partId/revision combination
        const existingPart = this.state.parts.find(x => 
            x.part.partId.toLowerCase().trim() === editingPart.part.partId.toLowerCase().trim() && 
            x.part.revision.toLowerCase().trim() === this.state.newRevision.trim().toLowerCase().trim()
        );
        
        if (existingPart) {
            self.setState({savingPart: false});
            return window.$alertWarning("A part already exists with that Part Number and Revision!");
        }
        
        // Check if there are images or files to clone
        const hasImages = editingPart.part.imageURLs && editingPart.part.imageURLs.length > 0 && editingPart.part.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingPart.part.fileURLs && (
            (Array.isArray(editingPart.part.fileURLs) && editingPart.part.fileURLs.length > 0 && editingPart.part.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingPart.part.fileURLs === 'string' && editingPart.part.fileURLs.trim() !== '')
        );
        
        // Determine image and file values based on checkbox (defaults to false)
        const cloneImagesAndFiles = this.state.cloneImagesAndFiles === true; // Default to true
        let imageURLs = [];
        let fileURLsString = '';
        
        if (cloneImagesAndFiles) {
            // Clone images if they exist
            if (hasImages && editingPart.part.imageURLs) {
                imageURLs = [...editingPart.part.imageURLs];
            }
            
            // Clone files if they exist
            if (hasFiles && editingPart.part.fileURLs) {
                if (typeof editingPart.part.fileURLs === 'string') {
                    fileURLsString = editingPart.part.fileURLs;
                } else if (Array.isArray(editingPart.part.fileURLs)) {
                    fileURLsString = editingPart.part.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                }
            }
        }

        const newPartId = uuid();
        const newPart = {
            ...editingPart.part,
            ...copyMsrpPricingFromPart(editingPart.part),
            id: newPartId,
            revision: this.state.newRevision,
            revisionNotes: this.state.newRevisionNotes || '',
            imageURLs: imageURLs, // Clone or clear images
            fileURLs: fileURLsString, // Clone or clear files (must be string)
            vendorParts: editingPart.part.vendorParts,
            currentlyPurchasing: false,
            partToFamilies: []
        };

        newPart.vendorParts.forEach(vp => {
            const vpId = uuid();

            vp.id = vpId;
            vp.partId = newPart.id;
            vp.tieredCostFormulas.forEach(tcf => {
                tcf.id = uuid();
                tcf.partFk = null; // Clear partFk since this belongs to a vendor part
                tcf.vendorPartFk = vpId;
                // Keep other properties as they are
            });
        });
        
        partService.createPart(newPart).then(() => {
            window.$alertSuccess("New revision created successfully");
            self.setState({newRevision: '', newRevisionNotes: ''});
            globalProps.history.push(`/admin/parts/${newPart.id}`);
            window.location.reload();
        }).catch((error) => {
            console.error("Error creating new revision:", error);
            window.$alertError("Error creating new revision");
        });
    };

    clonePart = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        // Check if there are images or files to clone
        const hasImages = editingPart.part.imageURLs && editingPart.part.imageURLs.length > 0 && editingPart.part.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingPart.part.fileURLs && (
            (Array.isArray(editingPart.part.fileURLs) && editingPart.part.fileURLs.length > 0 && editingPart.part.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingPart.part.fileURLs === 'string' && editingPart.part.fileURLs.trim() !== '')
        );
        
        // Create cloned part data with new GUIDs (images/files will be set in handleClonePartSubmit based on checkbox)
        const clonedPart = {
            ...editingPart.part,
            ...copyMsrpPricingFromPart(editingPart.part),
            id: uuid(),
            partId: '', // Will be set by user
            revision: '', // Will be set by user
            imageURL: '', // Will be set in handleClonePartSubmit if checkbox is checked
            imageURLs: [], // Will be set in handleClonePartSubmit if checkbox is checked
            fileURLs: '', // Will be set in handleClonePartSubmit if checkbox is checked
            vendorParts: [], // Clear vendor parts
            supersedesId: null, // Clear supersedes relationship
            supersededById: null, // Clear superseded by relationship
            partToFamilies: [], // Clear family relationships
            currentlyPurchasing: true
        };

        // Clear vendor parts and create new GUIDs for them (excluding vendor parts marked for deletion)
        const validVendorParts = editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion);
        if (validVendorParts && validVendorParts.length > 0) {
            clonedPart.vendorParts = validVendorParts.map(vp => ({
                ...vp,
                id: uuid(),
                partId: clonedPart.id,
                // Generate new GUIDs for TieredCostFormulas and reset prices to 0
                tieredCostFormulas: vp.tieredCostFormulas ? vp.tieredCostFormulas.map(tcf => ({
                    id: uuid(),
                    partFk: null, // Clear partFk since this belongs to a vendor part
                    vendorPartFk: '00000000-0000-0000-0000-000000000000', // Will be set after vendor part is created
                    quantity: tcf.quantity,
                    price: 0, // Reset price to 0 while keeping quantity the same
                    isSelected: tcf.isSelected
                })) : []
            }));
            
            // Update the tieredCostFormulas vendorPartFk references
            clonedPart.vendorParts.forEach(vp => {
                if (vp.tieredCostFormulas) {
                    vp.tieredCostFormulas.forEach(tcf => {
                        tcf.vendorPartFk = vp.id;
                    });
                }
            });
        }

        // Store cloned data in session storage for the clone dialog
        sessionStorage.setItem('clonedPartData', JSON.stringify(clonedPart));
        
        // Open clone dialog
        this.setState({
            showCloneDialog: true,
            clonedPartData: clonedPart
        });
    };

    handleClonePartSubmit = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        
        if (!this.state.clonedPartData.partId || this.state.clonedPartData.partId.trim() === '') {
            window.$alertError("Please enter a part number");
            return;
        }

        if (!this.state.clonedPartData.revision || this.state.clonedPartData.revision.trim() === '') {
            window.$alertError("Please enter a revision");
            return;
        }

        // Check for duplicate partId/revision combination
        const existingPart = this.state.parts.find(x => 
            x.part.partId.toLowerCase().trim() === this.state.clonedPartData.partId.toLowerCase().trim() && 
            x.part.revision.toLowerCase().trim() === this.state.clonedPartData.revision.toLowerCase().trim()
        );
        
        if (existingPart) {
            return window.$alertWarning("A part already exists with that Part Number and Revision!");
        }

        this.setState({cloningPart: true});
        
        // Determine image and file values based on checkbox
        let imageURL = '';
        let imageURLs = [];
        let fileURLs = '';
        
        if (this.state.cloneImagesAndFiles) {
            // Clone images if they exist
            if (editingPart.part.imageURLs && editingPart.part.imageURLs.length > 0) {
                imageURLs = [...editingPart.part.imageURLs];
                imageURL = editingPart.part.imageURL || '';
            }
            
            // Clone files if they exist
            if (editingPart.part.fileURLs) {
                if (typeof editingPart.part.fileURLs === 'string') {
                    fileURLs = editingPart.part.fileURLs;
                } else if (Array.isArray(editingPart.part.fileURLs)) {
                    fileURLs = editingPart.part.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                }
            }
        }
        
        // Ensure fileURLs is a string before sending to API
        const clonedPartToSave = {
            ...this.state.clonedPartData,
            ...copyMsrpPricingFromPart(this.state.clonedPartData),
            imageURL: imageURL,
            imageURLs: imageURLs,
            fileURLs: fileURLs
        };
        
        partService.createPart(clonedPartToSave).then((createdPart) => {
            window.$alertSuccess("Part cloned successfully");
            self.setState({
                showCloneDialog: false,
                clonedPartData: null,
                cloningPart: false
            });
            sessionStorage.removeItem('clonedPartData');
            globalProps.history.push(`/admin/parts/${createdPart.id}`);
            window.location.reload();
        }).catch((error) => {
            console.error("Error cloning part:", error);
            window.$alertError("Error cloning part");
            self.setState({cloningPart: false});
        });
    };

    cancelClonePart = () => {
        this.setState({
            showCloneDialog: false,
            clonedPartData: null
        });
        sessionStorage.removeItem('clonedPartData');
    };

    changeClonedPartId = (event) => {
        const clonedData = { ...this.state.clonedPartData };
        clonedData.partId = event.target.value;
        this.setState({ clonedPartData: clonedData });
    };

    changeClonedRevision = (event) => {
        const clonedData = { ...this.state.clonedPartData };
        clonedData.revision = event.target.value;
        this.setState({ clonedPartData: clonedData });
    };

    // Navigation methods
    navigateToPreviousPart = () => {
        if (!this.state.parts || !editingPart) return;
        
        const currentIndex = this.state.parts.findIndex(p => p.part.id === editingPart.part.id);
        if (currentIndex > 0) {
            const previousPart = this.state.parts[currentIndex - 1];
            globalProps.history.push(`/admin/parts/${previousPart.part.id}`);
            window.location.reload();
        }
    };

    navigateToNextPart = () => {
        if (!this.state.parts || !editingPart) return;
        
        const currentIndex = this.state.parts.findIndex(p => p.part.id === editingPart.part.id);
        if (currentIndex < this.state.parts.length - 1) {
            const nextPart = this.state.parts[currentIndex + 1];
            globalProps.history.push(`/admin/parts/${nextPart.part.id}`);
            window.location.reload();
        }
    };

    // Fetch inventory analytics data for the current part
    fetchInventoryData = async () => {
        if (!editingPart || !editingPart.part) return;
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const allPartsExpanded = await partService.getAllPartsExpanded();
            
            // Find the current part in the expanded data
            const partExpanded = allPartsExpanded.find(p => p.part.id === editingPart.part.id);
            
            if (partExpanded) {
                const part = editingPart.part;
                
                // Use the global assemblies and subAssemblies variables that are already loaded
                // Calculate minQuantity using the exact same logic as Inventory.js
                let minQuantity = 0;
                let packagingDeficit = "0";
                
                if (part.inventoryStrategy === "B") {
                    let sumOfPackagingDeficits = 0;
                    subAssemblies.forEach((subAssembly) => {
                        const assemblyPart = subAssembly.subAssembly.partsToSubAssemblies.find(x => x.partFk === part.id);
                        if (assemblyPart && !assemblyPart.isImpliedPart) {
                            const averageSalesFourAndTwentyFour = (subAssembly.fourWeekSalesPerWeekWithRev + subAssembly.twentyFourWeekSalesPerWeekWithRev) / 2;
                            let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                            let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                            let avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;

                            sumOfPackagingDeficits += (subAssembly.totalAssembledOnHandWithRev * assemblyPart.quantity) - (Math.ceil(avgAssembledOnHand) * assemblyPart.quantity);
                            minQuantity += Math.ceil(minAssembledOnHand) * assemblyPart.quantity;
                        }
                    });
                    assemblies.forEach((assembly) => {
                        const assemblyPart = assembly.assembly.partsToAssemblies.find(x => x.partFk === part.id);
                        if (assemblyPart && !assemblyPart.isImpliedPart) {
                            const averageSalesFourAndTwentyFour = (assembly.fourWeekSalesPerWeekWithRev + assembly.twentyFourWeekSalesPerWeekWithRev) / 2;
                            let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                            let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                            let avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;

                            sumOfPackagingDeficits += (assembly.totalAssembledOnHandWithRev * assemblyPart.quantity) - (Math.ceil(avgAssembledOnHand) * assemblyPart.quantity);
                            minQuantity += Math.ceil(minAssembledOnHand) * assemblyPart.quantity;
                        }
                    });
                    packagingDeficit = sumOfPackagingDeficits < 0 ? sumOfPackagingDeficits.toFixed(0).toString() : "0";
                } else {
                    subAssemblies.forEach((subAssembly) => {
                        const assemblyPart = subAssembly.subAssembly.partsToSubAssemblies.find(x => x.partFk === part.id);
                        if (assemblyPart && !assemblyPart.isImpliedPart) {
                            const averageSalesFourAndTwentyFour = (subAssembly.fourWeekSalesPerWeekWithRev + subAssembly.twentyFourWeekSalesPerWeekWithRev) / 2;
                            let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                            minQuantity += Math.ceil(minAssembledOnHand) * assemblyPart.quantity;
                        }
                    });
                    assemblies.forEach((assembly) => {
                        const assemblyPart = assembly.assembly.partsToAssemblies.find(x => x.partFk === part.id);
                        if (assemblyPart && !assemblyPart.isImpliedPart) {
                            const averageSalesFourAndTwentyFour = (assembly.fourWeekSalesPerWeekWithRev + assembly.twentyFourWeekSalesPerWeekWithRev) / 2;
                            let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                            minQuantity += Math.ceil(minAssembledOnHand) * assemblyPart.quantity;
                        }
                    });
                    packagingDeficit = "-";
                }
                
                // Apply minimum quantity floor from part settings
                if (part.reorderByMinimumQuantity) {
                    minQuantity = Number(part.minimumQuantity) || 0;
                } else if (minQuantity < part.minimumQuantity) {
                    minQuantity = part.minimumQuantity;
                }
                
                // Calculate effective inventory using exact same logic as Inventory.js
                let effectiveInventory = part.inventoryStrategy === "A" ? 
                    partExpanded.totalReceived - partExpanded.totalSold : 
                    partExpanded.totalUnpackaged + Number.parseFloat(packagingDeficit);

                let weeksOfStock;
                let weeksToOrder;
                let weeksToOrderColor = '#f5f5f5';
                let numberToOrder;

                let combinedEffectiveInventory = effectiveInventory;
                if (partExpanded.supersedesTwentyFourWeekAvgSales > 0) {
                    combinedEffectiveInventory += part.inventoryStrategy === "A" ?
                        partExpanded.supersedesReceived - partExpanded.supersedesSold :
                        partExpanded.supersedesUnpackaged;
                }
                let combinedAverageSalesPerWeek = partExpanded.averageSalesPerWeek;
                if (partExpanded.supersedesTwentyFourWeekAvgSales > 0) {
                    const combinedFourWeekSales = partExpanded.fourWeekAvgSales + partExpanded.supersedesFourWeekAvgSales;
                    const combinedTwentyFourWeekSales = partExpanded.twentyFourWeekAvgSales + partExpanded.supersedesTwentyFourWeekAvgSales;
                    combinedAverageSalesPerWeek = (combinedFourWeekSales + combinedTwentyFourWeekSales) / 2;
                }

                if (part.reorderByMinimumQuantity) {
                    const threshold = Number(part.minimumQuantity) || 0;
                    const position = combinedEffectiveInventory + partExpanded.onOrder;
                    weeksOfStock = "∞";
                    weeksToOrder = (threshold > 0 && position < threshold) ? 0 : "∞";
                    if (weeksToOrder < 1) weeksToOrderColor = 'red';
                    else if (weeksToOrder < 3) weeksToOrderColor = 'orange';
                    else if (weeksToOrder < 5) weeksToOrderColor = 'yellow';
                    numberToOrder = Math.max(0, Math.ceil(threshold - combinedEffectiveInventory));
                } else {
                weeksOfStock = "∞";
                if (partExpanded.fourWeekAvgSales > 0 || partExpanded.twentyFourWeekAvgSales > 0) {
                    weeksOfStock = Math.floor((effectiveInventory + partExpanded.onOrder - minQuantity) / partExpanded.averageSalesPerWeek);
                }

                weeksToOrder = "∞";
                if (partExpanded.supersedesTwentyFourWeekAvgSales > 0) {
                    const combinedWeeksOfStock = Math.floor((combinedEffectiveInventory + partExpanded.onOrder - minQuantity) / combinedAverageSalesPerWeek);
                    weeksToOrder = Number.parseFloat(combinedWeeksOfStock) - part.leadTimeInWeeks - 1; // Using 1 week buffer
                } else if (weeksOfStock !== "∞") {
                    weeksToOrder = Math.ceil(Number.parseFloat(weeksOfStock) - part.leadTimeInWeeks - 1); // Using 1 week buffer
                }

                if (weeksToOrder < 1) weeksToOrderColor = 'red';
                else if (weeksToOrder < 3) weeksToOrderColor = 'orange';
                else if (weeksToOrder < 5) weeksToOrderColor = 'yellow';

                let salesPerWeekForCalculation = partExpanded.averageSalesPerWeek;
                if (partExpanded.supersedesTwentyFourWeekAvgSales > 0) {
                    const combinedFourWeekSales = partExpanded.fourWeekAvgSales + partExpanded.supersedesFourWeekAvgSales;
                    const combinedTwentyFourWeekSales = partExpanded.twentyFourWeekAvgSales + partExpanded.supersedesTwentyFourWeekAvgSales;
                    salesPerWeekForCalculation = (combinedFourWeekSales + combinedTwentyFourWeekSales) / 2;
                }

                const weeksToTarget = 10;
                numberToOrder = Math.ceil(weeksToTarget * salesPerWeekForCalculation);

                }
                
                const [purchaseLogs, packingSlips] = await Promise.all([
                    partService.getPurchaseLogs(),
                    partService.getAllPackingSlips(),
                ]);
                const inTransitByPartId = buildInTransitByPartId(purchaseLogs, packingSlips);
                const inTransit = inTransitByPartId[part.id] || 0;
                const aggregatedSupersedesForCritical = partExpanded.supersedesTwentyFourWeekAvgSales > 0 ? {
                    fourWeekSales: partExpanded.supersedesFourWeekAvgSales,
                    twentyFourWeekSales: partExpanded.supersedesTwentyFourWeekAvgSales,
                } : null;
                const hasSupersedesChainWithSales = function checkSupersedesChain(p) {
                    if (p.totalSold > 0) return true;
                    if (!p.part.supersedesId) return false;
                    const older = allPartsExpanded.find(x => x.part.id === p.part.supersedesId);
                    return older ? checkSupersedesChain(older) : false;
                };
                const minReorderForCritical = part.reorderByMinimumQuantity ? {
                    weeksOfStock: "∞",
                    weeksToOrder: (Number(part.minimumQuantity) || 0) > 0 &&
                        (effectiveInventory + inTransit) < (Number(part.minimumQuantity) || 0) ? 0 : "∞",
                } : null;
                const criticalEffectiveInventory = computeCriticalEffectiveInventoryWeeks({
                    inTransit,
                    minReorderWeeksResult: minReorderForCritical,
                    combinedEffectiveInventory,
                    effectiveInventory,
                    minQuantity,
                    averageSalesPerWeek: partExpanded.averageSalesPerWeek,
                    combinedAverageSalesPerWeek: combinedAverageSalesPerWeek || partExpanded.averageSalesPerWeek,
                    leadTimeInWeeks: part.leadTimeInWeeks || 0,
                    weeksToOrderBuffer: 1,
                    partExpanded,
                    part,
                    aggregatedSupersedesData: aggregatedSupersedesForCritical,
                    parts: allPartsExpanded,
                    hasSupersedesChainWithSales,
                    inTransitByPartId,
                });

                // Calculate earliest ETA
                let earliestETA = '';
                if (partExpanded.onOrder > 0) {
                    // Find the earliest purchase log for this part
                    const partPurchaseLogs = this.state.parts
                        .filter(p => p.part.id === part.id)
                        .flatMap(p => p.purchaseLogs || [])
                        .filter(log => log.quantityReceived < log.quantity)
                        .sort((a, b) => new Date(a.orderDate) - new Date(b.orderDate));
                    
                    if (partPurchaseLogs.length > 0) {
                        const earliestOrderDate = new Date(partPurchaseLogs[0].orderDate);
                        const etaDate = new Date(earliestOrderDate.getTime() + (part.leadTimeInWeeks * 7 * 24 * 60 * 60 * 1000));
                        earliestETA = etaDate.toISOString().split('T')[0];
                    }
                }

                this.setState({
                    inventoryData: {
                        inventoryStrategy: part.inventoryStrategy,
                        fourWeekAvgSales: partExpanded.fourWeekAvgSales || 0,
                        twentyFourWeekAvgSales: partExpanded.twentyFourWeekAvgSales || 0,
                        totalReceived: partExpanded.totalReceived || 0,
                        totalSold: partExpanded.totalSold || 0,
                        totalUnpackaged: partExpanded.totalUnpackaged || 0,
                        totalInFG: partExpanded.totalInFG || 0,
                        packagingDeficit: packagingDeficit,
                        effectiveInventory: effectiveInventory,
                        minQuantity: minQuantity,
                        onOrder: partExpanded.onOrder || 0,
                        earliestETA: earliestETA,
                        weeksOfStock: weeksOfStock,
                        leadTimeInWeeks: part.leadTimeInWeeks || 0,
                        weeksToOrder: weeksToOrder,
                        weeksToOrderColor: weeksToOrderColor,
                        numberToOrder: numberToOrder,
                        inTransit,
                        criticalInventoryQuantity: criticalEffectiveInventory.criticalInventoryQuantity,
                        criticalInventoryWeeks: criticalEffectiveInventory.criticalInventoryWeeks,
                        criticalInventoryWeeksColor: criticalEffectiveInventory.color,
                        criticalWeeksToOrder: criticalEffectiveInventory.criticalWeeksToOrder,
                        isCriticalEffectiveInventory: criticalEffectiveInventory.isCritical,
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching inventory data:', error);
            this.setState({ inventoryData: null });
        }
    };

    // Handle calculation contributions tab change
    handleCalculationContributionsTabChange = (event, newValue) => {
        this.setState({ calculationContributionsTab: newValue });
        // Lazy load data when tab is selected (skip tab 0 as it uses existing inventoryData)
        if (newValue !== 0) {
            this.loadCalculationContributionsData(newValue);
        }
    };

    // Helper function to get first purchase order number from packing slip items
    getFirstPurchaseOrderNumber = (packingSlip) => {
        if (!packingSlip.items || packingSlip.items.length === 0) return '';
        
        // Find the first item that has a purchase order number
        for (const item of packingSlip.items) {
            if (item.purchasedPart && item.purchasedPart.purchaseOrderNumber) {
                return item.purchasedPart.purchaseOrderNumber.toString();
            }
        }
        return '';
    };

    // Helper to get first purchase order id from packing slip items (for clickable PO# link)
    getFirstPurchaseOrderId = (packingSlip) => {
        if (!packingSlip.items || packingSlip.items.length === 0) return null;
        for (const item of packingSlip.items) {
            if (item.purchasedPart && (item.purchasedPart.purchaseOrderId || item.purchasedPart.purchaseOrder?.id)) {
                return item.purchasedPart.purchaseOrderId || item.purchasedPart.purchaseOrder?.id;
            }
        }
        return null;
    };

    prefetchCalculationContributionsTableData = () => {
        if (!editingPart || !editingPart.part) return;
        const opts = { background: true };
        [1, 2, 3, 4, 5].forEach((tabIndex) => {
            void this.loadCalculationContributionsData(tabIndex, opts);
        });
    };

    // Lazy load calculation contributions data (functional merges support parallel background prefetch)
    loadCalculationContributionsData = async (tabIndex, options = {}) => {
        const { background = false } = options;
        if (!editingPart || !editingPart.part) return;

        const partId = editingPart.part.id;
        const currentData = this.state.calculationContributionsData;

        // Don't reload if data already exists
        if (tabIndex === 1 && currentData.inventoryLogs !== null) return; // Inventory Logs
        if (tabIndex === 2 && currentData.packageLogs !== null) return; // Package Logs
        if (tabIndex === 3 && currentData.purchaseLogs !== null) return; // Purchase Logs
        if (tabIndex === 4 && currentData.packingSlips !== null) return; // Packing Slips
        if (tabIndex === 5 && currentData.salesLogs !== null) return; // Sales Logs

        if (!background) {
            this.setState({ loadingCalculationContributions: true });
        }

        try {
            const partService = ServiceLocator.Instance.getPartService();
            const assemblyService = ServiceLocator.Instance.getAssemblyService();

            if (tabIndex === 1) {
                // Load Inventory Logs
                const allInventoryLogs = await partService.getInventoryLogs();
                // Check first log structure for debugging
                if (allInventoryLogs.length > 0) {
                    console.log('Sample Inventory Log:', allInventoryLogs[0]);
                }
                const filteredLogs = allInventoryLogs.filter(log => log.partId === partId);
                console.log('Inventory Logs - All:', allInventoryLogs.length, 'Filtered:', filteredLogs.length, 'PartId:', partId, 'Sample log partId:', allInventoryLogs[0]?.partId);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        inventoryLogs: filteredLogs
                    }
                }));
            } else if (tabIndex === 2) {
                // Load Package Logs - filter by parts used in assemblies/subassemblies
                const allPackageLogs = await assemblyService.getPackageLogs();
                // Check first log structure for debugging
                if (allPackageLogs.length > 0) {
                    console.log('Sample Package Log:', allPackageLogs[0]);
                }
                console.log('Assemblies loaded:', assemblies?.length || 0, 'SubAssemblies loaded:', subAssemblies?.length || 0);
                const packageBomIndex = buildBomIndex(assemblies, subAssemblies);
                const packageTarget = { id: partId, kind: 'part' };
                const filteredLogs = allPackageLogs.filter(log => {
                    // Check if this package log directly references this part
                    if (log.partId === partId) {
                        return true;
                    }
                    // Check if this package log uses an assembly that (transitively) contains this part
                    if (log.assemblyId) {
                        const node = packageBomIndex.assemblyById.get(log.assemblyId);
                        if (node && containerHasTarget(node, 'assembly', packageTarget, packageBomIndex)) {
                            return true;
                        }
                    }
                    // Check if this package log uses a subassembly that (transitively) contains this part
                    if (log.subAssemblyId) {
                        const node = packageBomIndex.subById.get(log.subAssemblyId);
                        if (node && containerHasTarget(node, 'subassembly', packageTarget, packageBomIndex)) {
                            return true;
                        }
                    }
                    return false;
                });
                console.log('Package Logs - All:', allPackageLogs.length, 'Filtered:', filteredLogs.length, 'PartId:', partId);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        packageLogs: filteredLogs
                    }
                }));
            } else if (tabIndex === 3) {
                // Load Purchase Logs - get from purchase orders' purchasedParts (same as Purchases.js)
                let allPurchaseOrders = [];
                try {
                    allPurchaseOrders = await partService.getPurchaseOrders();
                } catch (error) {
                    console.error('Error loading purchase orders:', error);
                    // Continue without purchase orders if this fails
                }
                
                // Load purchased part invoices to check paid status (same as Purchases.js)
                let purchasedPartInvoices = {};
                try {
                    const allInvoices = await partService.getAllPurchasedPartInvoices();
                    purchasedPartInvoices = allInvoices || {};
                } catch (error) {
                    console.warn('Error loading purchased part invoices:', error);
                    // Continue without invoice data if this fails
                }
                
                // Helper function to get invoices for a line item (same as Purchases.js)
                const getInvoicesForLineItem = (purchasedPartId) => {
                    return purchasedPartInvoices[purchasedPartId] || [];
                };
                
                // Get all line items from purchase orders (same approach as Purchases.js)
                const allLineItems = allPurchaseOrders.flatMap(po => 
                    po.purchasedParts.map(part => ({
                        ...part,
                        purchaseOrderNumber: po.purchaseOrderNumber,
                        purchaseOrderId: po.id,
                        placedOn: po.placedOn,
                        purchaseOrderInvoices: po.purchaseOrderInvoices || [],
                        packingSlips: po.packingSlips || []
                    }))
                );
                
                // Filter by partId and enrich with vendor order numbers and invoice data
                // Check both log.partId and log.part?.id to handle different data structures
                const enrichedLogs = allLineItems
                    .filter(log => {
                        const logPartId = log.partId || log.part?.id;
                        return logPartId === partId;
                    })
                    .map(log => {
                        const purchaseOrder = allPurchaseOrders.find(po => po.id === log.purchaseOrderId);
                        const lineItemInvoices = getInvoicesForLineItem(log.id);
                        const isPaid = lineItemInvoices.some(attachment => attachment.invoice?.isPaid) || log.isPaid;
                        
                        return {
                            ...log,
                            vendorOrderNumber: purchaseOrder?.vendorOrderNumber || '',
                            isPaid: isPaid,
                            lineItemInvoices: lineItemInvoices,
                            orderDate: log.placedOn || log.orderDate // Use placedOn from PO if available
                        };
                    })
                    .sort((a, b) => {
                        // Sort by orderDate descending (newest first)
                        const dateA = new Date(a.orderDate || a.placedOn);
                        const dateB = new Date(b.orderDate || b.placedOn);
                        return dateB - dateA;
                    });
                
                // Debug logging to help diagnose filtering issues
                if (allLineItems.length > 0) {
                    console.log('Purchase Logs Debug - Sample line item:', {
                        id: allLineItems[0].id,
                        partId: allLineItems[0].partId,
                        part: allLineItems[0].part,
                        hasPartId: !!allLineItems[0].partId,
                        hasPart: !!allLineItems[0].part,
                        partIdType: typeof allLineItems[0].partId,
                        targetPartId: partId,
                        targetPartIdType: typeof partId
                    });
                    const matchingItems = allLineItems.filter(log => {
                        const logPartId = log.partId || log.part?.id;
                        return logPartId === partId;
                    });
                    console.log('Purchase Logs Debug - Matching items:', matchingItems.length, 'out of', allLineItems.length);
                }
                console.log('Purchase Logs - All Purchase Orders:', allPurchaseOrders.length, 'All Line Items:', allLineItems.length, 'Filtered:', enrichedLogs.length, 'PartId:', partId);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        purchaseLogs: enrichedLogs
                    }
                }));
            } else if (tabIndex === 4) {
                // Load Packing Slips - get all packing slips and filter by purchased part IDs
                const allPurchaseLogs = await partService.getPurchaseLogs();
                const partPurchaseLogs = allPurchaseLogs.filter(log => log.partId === partId);
                const purchasedPartIds = new Set(partPurchaseLogs.map(log => log.id).filter(id => id));
                console.log('Packing Slips - PurchaseLogs:', partPurchaseLogs.length, 'PurchasedPartIds:', purchasedPartIds.size);
                
                // Get all packing slips
                const allPackingSlips = await partService.getAllPackingSlips();
                
                // Filter packing slips to only include ones with items for this part's purchased parts
                const filteredSlips = allPackingSlips
                    .map(slip => ({
                        ...slip,
                        items: slip.items?.filter(item => {
                            const itemPurchasedPartId = item.purchasedPartId || item.purchasedPart?.id;
                            return itemPurchasedPartId && purchasedPartIds.has(itemPurchasedPartId);
                        }) || []
                    }))
                    .filter(slip => slip.items && slip.items.length > 0);
                
                console.log('Packing Slips - All:', allPackingSlips.length, 'Filtered:', filteredSlips.length);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        packingSlips: filteredSlips
                    }
                }));
            } else if (tabIndex === 5) {
                // Load Sales Logs - filter by assemblies/subassemblies that use this part
                const allSalesLogs = await assemblyService.getSalesLogs();
                // Check first log structure for debugging
                if (allSalesLogs.length > 0) {
                    console.log('Sample Sales Log:', allSalesLogs[0]);
                }
                console.log('Sales Logs - Assemblies loaded:', assemblies?.length || 0, 'SubAssemblies loaded:', subAssemblies?.length || 0);
                const salesBomIndex = buildBomIndex(assemblies, subAssemblies);
                const salesTarget = { id: partId, kind: 'part' };
                const filteredLogs = allSalesLogs.filter(log => {
                    // Check if this sales log directly references this part
                    if (log.partId === partId) {
                        return true;
                    }
                    // Check if this sales log uses an assembly that (transitively) contains this part
                    if (log.assemblyId) {
                        const node = salesBomIndex.assemblyById.get(log.assemblyId);
                        if (node && containerHasTarget(node, 'assembly', salesTarget, salesBomIndex)) {
                            return true;
                        }
                    }
                    // Check if this sales log uses a subassembly that (transitively) contains this part
                    if (log.subAssemblyId) {
                        const node = salesBomIndex.subById.get(log.subAssemblyId);
                        if (node && containerHasTarget(node, 'subassembly', salesTarget, salesBomIndex)) {
                            return true;
                        }
                    }
                    return false;
                });
                console.log('Sales Logs - All:', allSalesLogs.length, 'Filtered:', filteredLogs.length, 'PartId:', partId);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        salesLogs: filteredLogs
                    }
                }));
            }
        } catch (error) {
            console.error('Error loading calculation contributions data:', error);
        } finally {
            if (!background) {
                this.setState({ loadingCalculationContributions: false });
            }
        }
    };

    render() {
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Edit Part" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        let partOptions = [];
        const filteredPartsForOptions = this.state.parts.filter(x => x.part.isActive && x.part.id !== editingPart.part.id);
        filteredPartsForOptions.forEach((part) => {
            partOptions.push({
                id: part.part.id, 
                partId: `${part.part.partId} rev ${part.part.revision}`,
                part: part.part
            });
        });

        let partTypeOptions = [];
        let manufacturerForOptions = [];
        let tertiaryTypeOptions = [];
        let allBrands = []; // Collect ALL brands from parts, assemblies, and subassemblies
        
        this.state.parts.filter(x => x.part.isActive).forEach((part) => {
            if (part.part.partType !== '' && !partTypeOptions.includes(part.part.partType))
                partTypeOptions.push(part.part.partType);
            if (part.part.manufacturerFor !== '' && !manufacturerForOptions.find(x => x.subType === part.part.manufacturerFor && x.partType === part.part.partType))
                manufacturerForOptions.push({subType: part.part.manufacturerFor, partType: part.part.partType});
            if (part.part.tertiaryType !== '' && !tertiaryTypeOptions.find(x => x.subType === part.part.tertiaryType && x.partType === part.part.partType && x.manufacturerFor === part.part.manufacturerFor))
                tertiaryTypeOptions.push({subType: part.part.tertiaryType, partType: part.part.partType, manufacturerFor: part.part.manufacturerFor});
            if (part.part.brand && part.part.brand !== '' && !allBrands.includes(part.part.brand))
                allBrands.push(part.part.brand);
        });

        partTypeOptions = partTypeOptions.sort();
        manufacturerForOptions = manufacturerForOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);
        tertiaryTypeOptions = tertiaryTypeOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);
        
        // Collect brands from assemblies
        assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            if (assembly.assembly.brand && assembly.assembly.brand !== '' && !allBrands.includes(assembly.assembly.brand))
                allBrands.push(assembly.assembly.brand);
        });
        
        // Collect brands from subassemblies
        subAssemblies.filter(x => x.subAssembly.isActive).forEach((subAssembly) => {
            if (subAssembly.subAssembly.brand && subAssembly.subAssembly.brand !== '' && !allBrands.includes(subAssembly.subAssembly.brand))
                allBrands.push(subAssembly.subAssembly.brand);
        });
        
        const brandOptions = allBrands.sort();

        const bomAssemblies = assemblies.filter(x => x.assembly.partsToAssemblies.find(y => y.partFk === editingPart.part.id));
        const bomSubAssemblies = subAssemblies.filter(x => x.subAssembly.partsToSubAssemblies.find(y => y.partFk === editingPart.part.id));

        // Get current part index for navigation
        const currentIndex = this.state.parts.findIndex(p => p.part.id === editingPart.part.id);
        const hasPrevious = currentIndex > 0;
        const hasNext = currentIndex < this.state.parts.length - 1;

        return (
            <DashboardTemplate title="Edit Part" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div style={{marginBottom: 0, marginTop: -10}}>
                        <div style={{position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 0}}>
                            {/* Left: Back and Save buttons */}
                            <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-start'}}>
                                <Button onClick={() => {globalProps.history.length > 1 ? globalProps.history.goBack() : globalProps.history.push('/admin/parts')}} className="success-button-lg">Back</Button>
                                {this.state.savingPart === true ?
                                    <CircularProgress/>
                                    :
                                    <Button 
                                        onClick={this.savePart}
                                        className="save-button-lg"
                                        style={{ 
                                            backgroundColor: this.checkForChanges() ? '#7fff84' : '#93e7eb',
                                            color: 'black'
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                }
                            </div>
                            
                            {/* Center: Part number and revision with navigation arrows */}
                            <div style={{position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '15px'}}>
                                <div 
                                    onClick={hasPrevious ? this.navigateToPreviousPart : undefined}
                                    style={{
                                        fontSize: '24px',
                                        color: hasPrevious ? 'white' : '#666',
                                        cursor: hasPrevious ? 'pointer' : 'default',
                                        userSelect: 'none',
                                        padding: '5px'
                                    }}
                                >
                                    ←
                                </div>
                                <h1 style={{margin: 0, textAlign: 'center', color: 'white'}}>{editingPart.part.partId} rev {editingPart.part.revision}</h1>
                                <div 
                                    onClick={hasNext ? this.navigateToNextPart : undefined}
                                    style={{
                                        fontSize: '24px',
                                        color: hasNext ? 'white' : '#666',
                                        cursor: hasNext ? 'pointer' : 'default',
                                        userSelect: 'none',
                                        padding: '5px'
                                    }}
                                >
                                    →
                                </div>
                            </div>
                            
                            {/* Right: Duplicate, Deactivate and Delete buttons */}
                            <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-end'}}>
                                {this.state.savingPart !== true && editingPart.part.id !== '' ?
                                    <>
                                        <Button onClick={this.clonePart} className="success-button-lg">Duplicate</Button>
                                        {bomAssemblies.length === 0 && bomSubAssemblies.length === 0 && editingPart.totalSold === 0 && !editingPart.part.supersededById && !editingPart.part.supersedesId ?
                                            <Button onClick={this.deletePart} className="delete-button-lg">Permanently Delete</Button>
                                            : ""}
                                        {editingPart.part.isActive ? 
                                            <Button onClick={this.deactivatePart} className="delete-button-lg">Deactivate</Button>
                                            :
                                            <Button onClick={this.reactivatePart} className="success-button-lg">Reactivate</Button>
                                        }
                                    </>
                                    : ""}
                            </div>
                        </div>
                    </div>
                    
                    {/* Search Bar for Part Navigation */}
                    <div style={{display: 'flex', justifyContent: 'center'}}>
                        <Autocomplete
                            options={this.state.parts ? this.state.parts.map(part => ({
                                part: part.part,
                                value: part.part.id
                            })) : []}
                            getOptionLabel={(option) => option.part ? `${option.part.partId} rev ${option.part.revision}` : ''}
                            style={{width: 300, padding: 8, backgroundColor: 'white'}}
                            renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search for another part..."
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    globalProps.history.push(`/admin/parts/${newValue.value}`);
                                    window.location.reload();
                                }
                            }}
                        />
                    </div>
                    
                    <div style={{display: 'flex', gap: '20px', maxWidth: 1800, margin: '0 auto', alignItems: 'flex-start'}}>
                        {/* Column 1: Basic Part Information */}
                        <div style={{flex: '1', minWidth: '300px', maxWidth: '33%', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            {/* Basic Information Container */}
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 15}}>
                                <h2 style={{marginTop: 0}}>Basic Information</h2>
                            <TextField value={this.state.editingPart.part.partId}
                                       onChange={this.changePartId} label={"Part Number"} variant="outlined"
                                       style={{width: '24%', margin: '0 5px 5px'}} inputProps={{maxLength: 50}}
                                       autoComplete="off"/>
                            <TextField value={this.state.editingPart.part.description}
                                       onChange={this.changeDescription} label={"Description"}
                                       variant="outlined" multiline={true} style={{width: '70%', margin: '0 5px 5px'}}
                                       inputProps={{maxLength: 500}} autoComplete="off"/>
                            <TextField value={this.state.editingPart.part.revision}
                                       onChange={this.changeRevision} label={"Revision"} variant="outlined"
                                       style={{width: '24%', margin: '0 5px 20px'}} inputProps={{maxLength: 5}}
                                       autoComplete="off"/>
                            <TextField value={this.state.editingPart.part.revisionNotes}
                                       onChange={this.changeRevisionNotes} label={"Revision Notes"} variant="outlined"
                                       style={{width: '70%', margin: '0 5px 20px'}}
                                       inputProps={{maxLength: 500}} autoComplete="off"/>
                            <TextField
                                value={this.state.editingPart.part.weightPounds || ''}
                                onChange={this.changeWeightPounds}
                                label={"Weight (Pounds)"}
                                variant="outlined"
                                type="number"
                                style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                inputProps={{min: 0}}
                                autoComplete="off"/>
                            <TextField
                                value={this.state.editingPart.part.weightOunces || ''}
                                onChange={this.changeWeightOunces}
                                label={"Weight (Ounces)"}
                                variant="outlined"
                                type="number"
                                style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                inputProps={{min: 0, max: 15}}
                                autoComplete="off"/>
                            <TextField value={this.state.editingPart.part.leadTimeInWeeks || ''}
                                       onChange={this.changeLeadTime} label={"Lead Time (weeks)"}
                                       variant="outlined" style={{width: '23%', margin: '0 5px 10px'}}
                                       autoComplete="off"/>
                            <TextField value={this.state.editingPart.part.minimumQuantity ?? ''} 
                                       onChange={this.changeMinimumQuantity} label={"Min Qty Floor"}
                                       variant="outlined" style={{width: '23%', margin: '0 5px 10px'}}
                                       type="number" inputProps={{min: 0, step: 1}} autoComplete="off"/>
                            <FormControlLabel
                                style={{margin: '0 auto'}}
                                control={
                                    <Checkbox
                                        checked={!!this.state.editingPart.part.reorderByMinimumQuantity}
                                        onChange={this.changeReorderByMinimumQuantity}
                                        color="primary"
                                    />
                                }
                                label="Reorder by min qty only"
                            />
                            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '8px auto 0', gap: 16, flexWrap: 'wrap'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <Checkbox
                                        checked={!!this.state.editingPart.part.isOnBackorder}
                                        onChange={this.changeIsOnBackorder}
                                        color="primary"
                                    />
                                    On backorder
                                </div>
                                {this.state.editingPart.part.isOnBackorder && (
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <DatePicker
                                            label="Backorder ETA"
                                            value={this.state.editingPart.part.backorderEta || null}
                                            onChange={this.changeBackorderEta}
                                            format="MM/dd/yyyy"
                                            inputVariant="outlined"
                                            style={{width: 220}}
                                            minDate={addDays(new Date(), 1)}
                                            clearable
                                        />
                                    </MuiPickersUtilsProvider>
                                )}
                            </div>
                            <br />
                            <FormControl style={{width: '48%', margin: '10px 5px 20px'}} variant="outlined">
                                <InputLabel id="cost-type-select-label">Inventory Strategy</InputLabel>
                                <Select
                                    value={this.state.editingPart.part.inventoryStrategy ?? 'A'}
                                    style={{width: '100%'}}
                                    label="Inventory Strategy"
                                >
                                    <MenuItem value={'A'}
                                              onClick={() => this.handleInventoryStrategyChange('A')}>A (used in few assemblies)</MenuItem>
                                    <MenuItem value={'B'}
                                              onClick={() => this.handleInventoryStrategyChange('B')}>B (used in many assemblies)</MenuItem>
                                </Select>
                            </FormControl>
                            </div>
                            
                            {/* Organization Container */}
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 5}}>
                                <h2 style={{marginTop: 0}}>Organization</h2>
                            <Autocomplete autoHighlight
                                id="ddlPartType"
                                style={{marginBottom: 20, width: '100%'}}
                                options={partTypeOptions}
                                onChange={(event, newValue) => this.changePartTypeFreeSolo(newValue)}
                                onInputChange={(event, newInputValue) => this.changePartTypeInput(newInputValue)}
                                        value={this.state.editingPart.part.partType}
                                        freeSolo
                                filterOptions={(options, { inputValue }) => {
                                    const inputVal = this.state.partTypeInputValue || inputValue || '';
                                    
                                    if (!inputVal || inputVal.trim() === '') {
                                        // Only exclude current value if it's unique to this part
                                        const currentValue = this.state.editingPart.part.partType;
                                        const isValueUsedByOtherParts = this.state.parts.some(part => 
                                            part.part.id !== this.state.editingPart.part.id && 
                                            part.part.partType === currentValue
                                        );
                                        return isValueUsedByOtherParts ? options : options.filter(option => option !== currentValue);
                                    }
                                    
                                    // When typing, show ALL matching options (including current value if it matches)
                                    return options.filter(option => {
                                        return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                    });
                                }}
                                className="autocomplete-dropdown"
                                renderInput={(params) => <TextField {...params} inputProps={{
                                    ...params.inputProps
                                }} label="Part Category (Primary)" autoComplete="new-password" variant="outlined"/>}
                            />
                            {this.state.editingPart.part.partType.length > 0 ?
                                <div>
                                    <Autocomplete autoHighlight
                                        id="ddlManufacturerFor"
                                        style={{marginBottom: 20, width: '100%'}}
                                        options={manufacturerForOptions.filter(x => x.partType === this.state.editingPart.part.partType).map(x => x.subType)}
                                        onChange={(event, newValue) => this.changeManufacturerForFreeSolo(newValue)}
                                        onInputChange={(event, newInputValue) => this.changeManufacturerForInput(newInputValue)}
                                        value={this.state.editingPart.part.manufacturerFor}
                                        freeSolo
                                        filterOptions={(options, { inputValue }) => {
                                            const inputVal = this.state.manufacturerForInputValue || inputValue || '';
                                            
                                            if (!inputVal || inputVal.trim() === '') {
                                                // Only exclude current value if it's unique to this part
                                                const currentValue = this.state.editingPart.part.manufacturerFor;
                                                const isValueUsedByOtherParts = this.state.parts.some(part => 
                                                    part.part.id !== this.state.editingPart.part.id && 
                                                    part.part.manufacturerFor === currentValue
                                                );
                                                return isValueUsedByOtherParts ? options : options.filter(option => option !== currentValue);
                                            }
                                            
                                            // When typing, show ALL matching options (including current value if it matches)
                                            return options.filter(option => {
                                                return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                            });
                                        }}
                                        className="autocomplete-dropdown"
                                        renderInput={(params) => <TextField {...params} inputProps={{
                                            ...params.inputProps
                                        }} label="Part Category (Secondary)" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                    />
                                    <Autocomplete autoHighlight
                                        id="ddlTertiaryType"
                                        style={{marginBottom: 20, width: '100%'}}
                                        options={[...new Set(tertiaryTypeOptions.map(x => x.subType))]}
                                        onChange={(event, newValue) => this.changeTertiaryTypeFreeSolo(newValue)}
                                        onInputChange={(event, newInputValue) => this.changeTertiaryTypeInput(newInputValue)}
                                        value={this.state.editingPart.part.tertiaryType}
                                        freeSolo
                                        filterOptions={(options, { inputValue }) => {
                                            const inputVal = this.state.tertiaryTypeInputValue || inputValue || '';
                                            
                                            if (!inputVal || inputVal.trim() === '') {
                                                // Only exclude current value if it's unique to this part
                                                const currentValue = this.state.editingPart.part.tertiaryType;
                                                const isValueUsedByOtherParts = this.state.parts.some(part => 
                                                    part.part.id !== this.state.editingPart.part.id && 
                                                    part.part.tertiaryType === currentValue
                                                );
                                                return isValueUsedByOtherParts ? options : options.filter(option => option !== currentValue);
                                            }
                                            
                                            // When typing, show ALL matching options (including current value if it matches)
                                            return options.filter(option => {
                                                return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                            });
                                        }}
                                        className="autocomplete-dropdown"
                                        renderInput={(params) => <TextField {...params} inputProps={{
                                            ...params.inputProps
                                        }} label="Part Category (Tertiary)" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                    />
                                    <Autocomplete autoHighlight
                                        id="ddlBrand"
                                        style={{marginBottom: 20, width: '100%'}}
                                        options={brandOptions}
                                        onChange={(event, newValue) => this.changeBrandFreeSolo(newValue)}
                                        onInputChange={(event, newInputValue) => this.changeBrandInput(newInputValue)}
                                        value={this.state.editingPart.part.brand || ''}
                                        freeSolo
                                        filterOptions={(options, { inputValue }) => {
                                            const inputVal = this.state.brandInputValue || inputValue || '';
                                            
                                            if (!inputVal || inputVal.trim() === '') {
                                                // Only exclude current value if it's unique to this part
                                                const currentValue = this.state.editingPart.part.brand;
                                                const isValueUsedByOtherParts = this.state.parts.some(part => 
                                                    part.part.id !== this.state.editingPart.part.id && 
                                                    part.part.brand === currentValue
                                                );
                                                return isValueUsedByOtherParts ? options : options.filter(option => option !== currentValue);
                                            }
                                            
                                            // When typing, show ALL matching options (including current value if it matches)
                                            return options.filter(option => {
                                                return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                            });
                                        }}
                                        className="autocomplete-dropdown"
                                        renderInput={(params) => <TextField {...params} inputProps={{
                                            ...params.inputProps
                                        }} label="Brand" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                    />
                                    
                                    {/* Inventory Item Checkbox */}
                                    <div style={{marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                        <Checkbox
                                            checked={this.state.editingPart.part.isInventoryItem}
                                            onChange={this.changeIsInventoryItem}
                                            color="primary"
                                        />
                                        <center>Inventory item</center>
                                    </div>
                                </div>
                                : ""}
                            </div>

                            {/* Vehicle Fitment Container (SEMA / dealer) */}
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 5, marginTop: 5}}>
                                <VehicleFitmentPanel productType="Part" productId={this.state.editingPart.part.id}/>
                            </div>

                            {/* Printing Container */}
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 5}}>
                                <h2 style={{marginTop: 0}}>Printing</h2>
                            <FormControl style={{width: '50%', margin: '0 auto 20px'}} variant="outlined">
                                <InputLabel id="label-size-select-label">Label Size</InputLabel>
                                <Select
                                    value={editingPart.part.isSmallBinSize ? 'small' : 'large'}
                                    style={{width: '100%', textAlign: 'left'}}
                                    label="Label Size"
                                >
                                    <MenuItem value={'large'}
                                              onClick={() => this.changeIsSmallBinSize({target: {checked: false}})}>Large grey wire rack bins (4x2")</MenuItem>
                                    <MenuItem value={'small'}
                                              onClick={() => this.changeIsSmallBinSize({target: {checked: true}})}>Small clear organizer bins (2.25x0.75")</MenuItem>
                                </Select>
                            </FormControl>
                            <Button onClick={this.printPartLabel} className="success-button-lg" style={{margin: '8px 0 20px 10px'}}>
                                <span><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Print Labels</span>
                            </Button>
                            </div>
                            
                            {/* Images Container */}
                            <div 
                                className="container" 
                                onDragOver={this.handleDragOverMain}
                                onDragLeave={this.handleDragLeaveMain}
                                onDrop={async (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    this.setState({ isDraggingOverMain: false });
                                    const files = Array.from(event.dataTransfer.files);
                                    if (files.length > 0) {
                                        // Process files - will replace blank slots first, then append
                                        await this.processImageFiles(files, 0);
                                    }
                                }}
                                style={{
                                    padding: '20px', 
                                    backgroundColor: this.state.isDraggingOverMain ? '#E3F2FD' : 'white', 
                                    borderRadius: '8px', 
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                    marginBottom: 0, 
                                    marginTop: 5,
                                    border: this.state.isDraggingOverMain ? '2px dashed #2196F3' : 'none',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            >
                                <h2 style={{marginTop: 0}}>
                                    {this.state.isDraggingOverMain ? 'Drop images here to add' : 'Images'}
                                </h2>
                            {this.state.editingPart.part.imageURLs.map((imageURL, index) => {
                                return (
                                    <div key={index} style={{
                                        marginBottom: 20
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginBottom: 10
                                        }}>
                                            {this.state.editingPart.part.imageURLs.length > 1 ?
                                                <div style={{marginRight: 5}}
                                                     onClick={() => this.removeImage(imageURL)}>
                                                    <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                        width: 28,
                                                        fontSize: 18,
                                                        cursor: "pointer"
                                                    }}/>
                                                </div>
                                                : ""
                                            }
                                            <TextField value={imageURL}
                                                       onChange={(event) => this.changeImageURL(this.state.editingPart.part.imageURLs.indexOf(imageURL), event)}
                                                       label={"Image URL"} variant="outlined" style={{width: '80%'}}
                                                       inputProps={{maxLength: 500}} autoComplete="off"/>
                                            {imageURL.length > 0 ?
                                                <img
                                                    src={this.state.editingPart.part.imageURLs[this.state.editingPart.part.imageURLs.indexOf(imageURL)]}
                                                    width="18%" alt={this.state.editingPart.part.partId}
                                                    style={{display: 'inline-block', marginLeft: 10, cursor: 'pointer'}}
                                                    onClick={() => this.openImageModal(imageURL)}/>
                                                : ""}
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: 10,
                                marginBottom: 20
                            }}>
                                <span style={{marginRight: 10, fontSize: 14, color: '#666'}}>OR</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(event) => this.handleImageUpload(0, event)}
                                    style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                />
                                {this.state.uploadingImage0 && <CircularProgress size={20} />}
                            </div>
                            <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20}}>
                                <Button onClick={this.addNewImage} className="success-button-lg">Add Another Image</Button>
                            </div>
                                        <br />
                            </div>
                            
                            {/* Files Container */}
                            <div 
                                className="container" 
                                onDragOver={this.handleDragOverFilesMain}
                                onDragLeave={this.handleDragLeaveFilesMain}
                                onDrop={async (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    this.setState({ isDraggingOverFilesMain: false });
                                    const files = Array.from(event.dataTransfer.files);
                                    if (files.length > 0) {
                                        // Process files - will replace blank slots first, then append
                                        await this.processFileFiles(files, 0);
                                    }
                                }}
                                style={{
                                    padding: '20px', 
                                    backgroundColor: this.state.isDraggingOverFilesMain ? '#E3F2FD' : 'white', 
                                    borderRadius: '8px', 
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                    marginBottom: 0, 
                                    marginTop: 15,
                                    border: this.state.isDraggingOverFilesMain ? '2px dashed #2196F3' : 'none',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            >
                                <h2 style={{marginTop: 0}}>
                                    {this.state.isDraggingOverFilesMain ? 'Drop files here to add' : 'Files'}
                                </h2>
                            {this.getFileURLsArray().map((fileURL, index) => {
                                return (
                                    <div key={index} style={{
                                        marginBottom: 20
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginBottom: 10
                                        }}>
                                            {fileURL && fileURL.length > 0 ?
                                                <div style={{marginRight: 5}}
                                                     onClick={() => this.removeFile(fileURL)}>
                                                    <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                        width: 28,
                                                        fontSize: 18,
                                                        cursor: "pointer"
                                                    }}/>
                                                </div>
                                                : ""
                                            }
                                            {fileURL && fileURL.length > 0 ? (
                                                <>
                                                    <Typography 
                                                        variant="body1" 
                                                        style={{
                                                            width: '80%',
                                                            padding: '12px 14px',
                                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#333',
                                                            fontSize: '16px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {this.getFileNameFromUrl(fileURL)}
                                                    </Typography>
                                                    <Button
                                                        onClick={() => this.downloadFile(fileURL, this.getFileURLsArray().indexOf(fileURL))}
                                                        style={{
                                                            marginLeft: 10,
                                                            padding: '8px 16px',
                                                            backgroundColor: '#2196F3',
                                                            color: 'white',
                                                            borderRadius: '4px',
                                                            fontSize: '14px',
                                                            textTransform: 'none'
                                                        }}
                                                    >
                                                        Download
                                                    </Button>
                                                </>
                                            ) : (
                                                <Typography 
                                                    variant="body2" 
                                                    style={{
                                                        width: '80%',
                                                        color: '#999',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    No file uploaded
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: 10,
                                marginBottom: 20
                            }}>
                                <input
                                    type="file"
                                    multiple
                                    onChange={(event) => this.handleFileUpload(0, event)}
                                    style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                />
                                {this.state.uploadingFile0 && <CircularProgress size={20} />}
                            </div>
                            </div>
                        </div>
                        
                        {/* Column 2: Vendors */}
                        <div style={{flex: '1', minWidth: '300px', maxWidth: '33%'}}>
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: 15}}>
                                <h2 style={{marginTop: 0}}>Vendors</h2>
                            {this.state.editingPart.part.vendorParts
                                .filter(vendorPart => !vendorPart.markedForDeletion) // Hide vendor parts marked for deletion
                                .map((vendorPart) => {
                            return (
                                <div style={{
                                    border: '1px solid #000',
                                    borderRadius: 8,
                                    padding: 15,
                                    marginBottom: 20,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative'
                                }}>
                                    {this.state.editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion).length > 1 ?
                                        <div className="purchase-order-part-remove"
                                             onClick={() => this.removeVendorPart(vendorPart)}>
                                            <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                width: 28,
                                                fontSize: 18,
                                                cursor: "pointer"
                                            }}/>
                                        </div>
                                        : ""
                                    }
                                    <div>
                                        <Checkbox onClick={() => {
                                            this.setDefaultVendorId(vendorPart.vendorId)
                                        }}
                                                  checked={editingPart.part.selectedVendorId === vendorPart.vendorId || (editingPart.part.vendorParts.filter(vp => !vp.markedForDeletion).length === 1 && !vendorPart.markedForDeletion)}/>
                                        Default Vendor
                                    </div>
                                    <Autocomplete
                                        value={this.state.vendors.find(x => x.id === vendorPart.vendorId) || { name: vendorPart.vendorName, id: vendorPart.vendorId }}
                                        onChange={(event, newValue) => this.changeVendorAutocomplete(this.state.editingPart.part.vendorParts.indexOf(vendorPart), newValue)}
                                        onInputChange={(event, newInputValue) => this.changeVendorInput(this.state.editingPart.part.vendorParts.indexOf(vendorPart), newInputValue)}
                                        options={this.state.vendors}
                                        getOptionLabel={(option) => option.name || ''}
                                        getOptionSelected={(option, value) => option.id === value.id}
                                        filterOptions={(options, { inputValue }) => {
                                            // Use the stored input value from state as fallback
                                            const currentInputValue = inputValue || this.state[`vendorInput_${this.state.editingPart.part.vendorParts.indexOf(vendorPart)}`] || '';
                                            
                                            if (!currentInputValue || currentInputValue.trim() === '') {
                                                return options;
                                            }
                                            
                                            return options.filter(option => {
                                                return option.name && 
                                                    option.name.toLowerCase().includes(currentInputValue.toLowerCase());
                                            });
                                        }}
                                        freeSolo
                                        style={{width: '60%', margin: '0 5px 10px', display: 'inline-block'}}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Vendor"
                                                variant="outlined"
                                                autoComplete="off"
                                            />
                                        )}
                                    />
                                    <FormControl variant="outlined" style={{width: '28%', margin: '0 5px 0px'}}>
                                        <InputLabel id="cost-type-select-label">Cost Type</InputLabel>
                                        <Select value={vendorPart.costType ?? 0}
                                                style={{margin: '0 5px 20px', textAlign: 'left'}} label="Cost Type">
                                            <MenuItem value={1}
                                                      onClick={() => this.handleCostTypeChange(vendorPart, 1)}>Fixed</MenuItem>
                                            <MenuItem value={0}
                                                      onClick={() => this.handleCostTypeChange(vendorPart, 0)}>Tiered</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div style={{width: '100%', margin: '0 5px 20px', display: 'flex', alignItems: 'center'}}>
                                        <TextField value={vendorPart.partUrl || ''}
                                                   onChange={(event) => this.changePartUrl(vendorPart, event.target.value)}
                                                   label={"Part URL"} variant="outlined" 
                                                   style={{width: '85%', marginRight: '10px'}}
                                                   inputProps={{maxLength: 500}} autoComplete="off"/>
                                        <Button 
                                            onClick={() => this.openPartUrl(vendorPart.partUrl)}
                                            disabled={!vendorPart.partUrl || vendorPart.partUrl.trim() === ''}
                                            variant="contained" 
                                            color="primary"
                                            style={{minWidth: '60px', height: '56px'}}
                                        >
                                            Go
                                        </Button>
                                    </div>
                                    {vendorPart.costType === 1 ?
                                        <div>
                                            <TextField value={vendorPart.quantity || ''}
                                                       onChange={(event) => this.changeQuantity(vendorPart, event.target.value)}
                                                       label={"Lot Quantity"}
                                                       variant="outlined" type="number" inputProps={{min: 1}}
                                                       style={{width: '28%', margin: '0 5px 20px'}}
                                                       autoComplete="off"/>
                                            <TextField value={vendorPart.basePrice || ''}
                                                       onChange={(event) => this.changeLotPrice(vendorPart, event.target.value)}
                                                       label={"Lot Cost"} variant="outlined" type="number"
                                                       style={{width: '28%', margin: '0 5px 20px'}}
                                                       InputProps={{
                                                           startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                       }} autoComplete="off"/>
                                            <div>${(vendorPart.basePrice / vendorPart.quantity).toFixed(2)} per part
                                            </div>
                                        </div>
                                        :
                                        <div style={{maxWidth: 300, margin: '0 auto'}}>
                                            {vendorPart.tieredCostFormulas.map((tieredFormula, index) => {
                                                return (<div key={tieredFormula.id} style={{display: 'flex', marginBottom: 20, width: '100%'}}>
                                                    <div><Checkbox onChange={(event) => {
                                                        this.selectTieredFormula(vendorPart, tieredFormula.id)
                                                    }} checked={tieredFormula.isSelected} inputProps={{ 'aria-label': `Select tiered formula ${tieredFormula.id}` }} color="primary"/></div>
                                                    <div><TextField value={tieredFormula.quantity || ''}
                                                                    onChange={(event) => {
                                                                        tieredFormula.quantity = event.target.value;
                                                                        this.setState({editingPart: {...editingPart}});
                                                                    }}
                                                                    onKeyDown={(event) => {
                                                                        if (event.key === 'Tab' && !event.shiftKey) {
                                                                            event.preventDefault();
                                                                            // Focus the quantity field of the next line
                                                                            const nextIndex = index + 1;
                                                                            if (nextIndex < vendorPart.tieredCostFormulas.length) {
                                                                                const nextQuantityField = document.querySelector(`input[data-quantity-field="${vendorPart.tieredCostFormulas[nextIndex].id}"]`);
                                                                                if (nextQuantityField) {
                                                                                    nextQuantityField.focus();
                                                                                    nextQuantityField.select();
                                                                                }
                                                                            } else {
                                                                                // If this is the last quantity field, go to the first price field
                                                                                const firstPriceField = document.querySelector(`input[data-price-field="${vendorPart.tieredCostFormulas[0].id}"]`);
                                                                                if (firstPriceField) {
                                                                                    firstPriceField.focus();
                                                                                    firstPriceField.select();
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                    label="Quantity" variant="outlined"
                                                                    autoComplete="off"
                                                                    inputProps={{
                                                                        'data-quantity-field': tieredFormula.id
                                                                    }}
                                                                    inputRef={(input) => {
                                                                        if (input && this.state.focusQuantityField && this.state.focusQuantityField === tieredFormula.id) {
                                                                            input.focus();
                                                                            input.select();
                                                                            this.setState({focusQuantityField: null});
                                                                        }
                                                                    }}/></div>
                                                    <div style={{marginLeft: 10}}><TextField
                                                        value={tieredFormula.price || ''}
                                                        onChange={(event) => {
                                                            tieredFormula.price = event.target.value;
                                                            this.setState({editingPart: {...editingPart}});
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Tab' && !event.shiftKey) {
                                                                event.preventDefault();
                                                                // Focus the price field of the next line
                                                                const nextIndex = index + 1;
                                                                if (nextIndex < vendorPart.tieredCostFormulas.length) {
                                                                    const nextPriceField = document.querySelector(`input[data-price-field="${vendorPart.tieredCostFormulas[nextIndex].id}"]`);
                                                                    if (nextPriceField) {
                                                                        nextPriceField.focus();
                                                                        nextPriceField.select();
                                                                    }
                                                                } else {
                                                                    // If this is the last price field and we're under 10 lines, create a new line
                                                                    if (vendorPart.tieredCostFormulas.length < 10) {
                                                                        this.addTieredCostFormulaAndFocus(vendorPart);
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                        label="Price" variant="outlined"
                                                        autoComplete="off"
                                                        inputProps={{
                                                            'data-price-field': tieredFormula.id
                                                        }}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                        }}/></div>
                                                    {vendorPart.tieredCostFormulas.length > 1 ?
                                                        <div style={{marginLeft: 10}}
                                                             onClick={() => this.removeTieredCostFormula(vendorPart, tieredFormula)}>
                                                            <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                                width: 28,
                                                                fontSize: 18,
                                                                marginTop: 18,
                                                                cursor: "pointer"
                                                            }}/>
                                                        </div>
                                                        : ""}
                                                </div>)
                                            })}
                                            {(vendorPart.tieredCostFormulas?.length || 0) < 10 ?
                                                <Button onClick={() => this.addTieredCostFormula(vendorPart)} className="success-button-lg">Add</Button>
                                                : ""}
                                        </div>
                                    }
                                    <div style={{ marginTop: 10 }}>
                                        <Button onClick={() => this.openPurchaseDrawerFromVendor(vendorPart)} variant="contained" color="primary" className="success-button-lg">
                                            Purchase
                                        </Button>
                                    </div>
                                </div>);
                            })}
                                <Button onClick={this.addNewVendor} className="success-button-lg" style={{marginBottom: 20}}>Add Another Vendor</Button>
                            </div>
                        </div>
                        
                        {/* Column 3: Revisions */}
                        <div style={{flex: '1', minWidth: '300px', maxWidth: '33%'}}>
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: 15, marginBottom: 20}}>
                                <h2 style={{marginTop: 0}}>Revisions</h2>
                        
                        {/* All revisions table */}
                        <div>
                            <table className="parts-table" style={{ width: '100%', maxWidth: 375, margin: '0 auto' }}>
                                <thead>
                                    <tr>
                                        <th style={{minWidth: 120}}>Part Number</th>
                                        <th>Revision</th>
                                        <th>Currently<br/>Purchasing</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.parts && this.state.parts
                                        .filter(p => p.part.partId === editingPart.part.partId)
                                        .sort((a, b) => a.part.revision.localeCompare(b.part.revision))
                                        .map(partData => {
                                            const part = partData.part;
                                            return (
                                                <tr key={part.id} style={{ backgroundColor: !part.isActive ? '#a6a6a6' :part.id === editingPart.part.id ? '#e2f6ff' : 'transparent' }}>
                                                    <td>
                                                        <a href={`/admin/parts/${part.id}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                                                            {part.partId}
                                                        </a>
                                                    </td>
                                                    <td>{part.revision}</td>
                                                    <td>
                                                        <Checkbox 
                                                            checked={part.currentlyPurchasing || false}
                                                            onChange={() => this.handleRevisionCurrentlyPurchasingChange(part.id, !part.currentlyPurchasing)}
                                                            style={{padding: 0}}
                                                        />
                                                    </td>
                                                    <td>{part.isActive ? 'Active' : 'Inactive'}</td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>
                        
                        <Button onClick={() => this.setState({newRevisionOpen: true})} className="success-button-lg" style={{margin: '20px 0'}}>New Revision</Button>
                        <Autocomplete autoHighlight
                            id="ddlPartSupersedes"
                            options={partOptions}
                            defaultValue={partOptions.find(x => x.id === this.state.editingPart.part.supersedesId)}
                            onChange={this.changeSupersedes}
                            getOptionLabel={(option) => option.partId}
                            getOptionSelected={(option) => option.id === this.state.editingPart.part.supersedesId}
                            className="autocomplete-dropdown"
                            style={{maxWidth: 300, marginBottom: 20}}
                            renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                            renderInput={(params) => <TextField {...params} inputProps={{
                                ...params.inputProps
                            }} label="Supersedes Part" variant="outlined" autoComplete="new-password"/>}
                        />
                        <Autocomplete autoHighlight
                            id="ddlPartSupersededBy"
                            options={partOptions}
                            defaultValue={partOptions.find(x => x.id === this.state.editingPart.part.supersededById)}
                            onChange={this.changeSupersededBy}
                            getOptionLabel={(option) => option.partId}
                            getOptionSelected={(option) => option.id === this.state.editingPart.part.supersededById}
                            className="autocomplete-dropdown"
                            style={{maxWidth: 300, marginBottom: 20}}
                            renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                            renderInput={(params) => <TextField {...params} inputProps={{
                                ...params.inputProps
                            }} label="Superseded By Part" variant="outlined" autoComplete="new-password"/>}
                        />
                            </div>
                            <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 20}}>
                                <h2 style={{marginTop: 0}}>MSRP / Pricing</h2>
                                <FormControlLabel
                                    style={{display: 'block', marginBottom: 12}}
                                    control={<Switch color="primary" checked={!!this.state.editingPart.part.msrpUseMarginPricing} onChange={this.changeMsrpUseMarginPricing} />}
                                    label="Use margin-based MSRP (from default vendor unit cost)"
                                />
                                {this.state.editingPart.part.msrpUseMarginPricing ? (
                                    <div>
                                        <TextField label="Margin %" variant="outlined" style={{width: '45%', marginRight: 8, marginBottom: 8}}
                                            value={this.state.editingPart.part.msrpMarginPercent ?? ''} onChange={this.changeMsrpMarginPercent} type="number"
                                            helperText="Applied before rounding"/>
                                        <Typography variant="subtitle2" style={{marginTop: 8, marginBottom: 6}}>Rounding (after margin %)</Typography>
                                        <TextField label="Threshold ($)" variant="outlined" size="small"
                                            style={{width: '31%', marginRight: '2%', marginBottom: 12, minWidth: 100}}
                                            value={this.state.editingPart.part.msrpRoundThreshold ?? ''} onChange={this.changeMsrpRoundThreshold} type="number"
                                            helperText={`Default ${DEFAULT_MSRP_ROUND_THRESHOLD}`}/>
                                        <TextField label="Increment below ($)" variant="outlined" size="small"
                                            style={{width: '31%', marginRight: '2%', marginBottom: 12, minWidth: 100}}
                                            value={this.state.editingPart.part.msrpRoundBelowIncrement ?? ''} onChange={this.changeMsrpRoundBelowIncrement} type="number" inputProps={{step: '0.01'}}
                                            helperText={`Default ${DEFAULT_MSRP_ROUND_BELOW_INCREMENT}`}/>
                                        <TextField label="Increment at/above ($)" variant="outlined" size="small"
                                            style={{width: '31%', marginBottom: 12, minWidth: 100}}
                                            value={this.state.editingPart.part.msrpRoundAtOrAboveIncrement ?? ''} onChange={this.changeMsrpRoundAtOrAboveIncrement} type="number" inputProps={{step: '0.01'}}
                                            helperText={`Default ${DEFAULT_MSRP_ROUND_AT_OR_ABOVE_INCREMENT}`}/>
                                        {(() => {
                                            const preview = this.getMsrpCostPreviewRows();
                                            if (preview.kind === 'none') {
                                                return <Typography variant="body2" style={{marginTop: 8}}>Select a default vendor (checkbox in Vendors) to mirror unit costs.</Typography>;
                                            }
                                            const tierRows = this.getMsrpTierDisplayRows();
                                            if (preview.rows.length === 0 && tierRows.length === 0) {
                                                return <Typography variant="body2">No cost tiers to display for the default vendor.</Typography>;
                                            }
                                            return (
                                                <>
                                                <Typography variant="body2" style={{marginTop: 8, marginBottom: 6, color: '#555'}}>
                                                    Override MSRP per line, add custom qty breaks, or uncheck Include to omit from the copy table. Vendor tiers follow default vendor costs; manual lines are yours only.
                                                </Typography>
                                                <div style={{overflowX: 'auto', marginTop: 8}}>
                                                <table className="parts-table" style={{width: '100%', minWidth: 520}}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{width: 46, whiteSpace: 'nowrap'}}>Include</th>
                                                            <th style={{whiteSpace: 'nowrap'}}>Qty break</th>
                                                            <th style={{whiteSpace: 'nowrap'}}>Unit cost</th>
                                                            <th style={{whiteSpace: 'nowrap'}}>Calc MSRP</th>
                                                            <th style={{whiteSpace: 'nowrap'}}>MSRP</th>
                                                            <th style={{width: 72}}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tierRows.map((row) => {
                                                            const qtyLabel = preview.kind === 'fixed' && row.source === 'vendor'
                                                                ? (row.quantity ? `${row.quantity}` : '—')
                                                                : row.source === 'manual' ? '' : `≥ ${row.quantity}`;
                                                            const hasOverride = row.source === 'vendor' && row.msrpOverride != null && Number.isFinite(row.msrpOverride);
                                                            const msrpFieldValue =
                                                                row.msrpOverride != null
                                                                    ? row.msrpOverride
                                                                    : row.effectiveMsrp;
                                                            return (
                                                                <tr key={row.id} style={row.excluded ? { opacity: 0.55 } : undefined}>
                                                                    <td>
                                                                        <Checkbox
                                                                            color="primary"
                                                                            checked={!row.excluded}
                                                                            onChange={(e) => this.changeMsrpTierIncluded(row.id, e.target.checked)}
                                                                            size="small"
                                                                        />
                                                                    </td>
                                                                    <td style={{whiteSpace: 'nowrap'}}>
                                                                        {row.source === 'manual' ? (
                                                                            <TextField
                                                                                value={row.quantity}
                                                                                onChange={(e) => this.changeMsrpTierQuantity(row.id, e.target.value)}
                                                                                type="number"
                                                                                size="small"
                                                                                variant="outlined"
                                                                                style={{width: 72}}
                                                                                inputProps={{ min: 1 }}
                                                                            />
                                                                        ) : (
                                                                            qtyLabel
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {row.source === 'vendor' && Number.isFinite(row.unitCost) ? (
                                                                            <TextField
                                                                                value={row.unitCost.toFixed(3)}
                                                                                disabled
                                                                                size="small"
                                                                                variant="outlined"
                                                                                style={{width: 100}}
                                                                                InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>}}
                                                                            />
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {row.source === 'vendor' && Number.isFinite(row.calculatedMsrp) ? (
                                                                            <TextField
                                                                                value={row.calculatedMsrp.toFixed(2)}
                                                                                disabled
                                                                                size="small"
                                                                                variant="outlined"
                                                                                style={{width: 100}}
                                                                                InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>}}
                                                                            />
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        <TextField
                                                                            value={
                                                                                msrpFieldValue == null
                                                                                    ? ''
                                                                                    : getNumberInputDisplayValue(msrpFieldValue)
                                                                            }
                                                                            onChange={(e) => this.changeMsrpTierMsrp(row.id, e.target.value)}
                                                                            type="number"
                                                                            size="small"
                                                                            variant="outlined"
                                                                            style={{width: 110}}
                                                                            InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>}}
                                                                            placeholder={row.source === 'vendor' && Number.isFinite(row.calculatedMsrp) ? row.calculatedMsrp.toFixed(2) : ''}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        {row.source === 'manual' ? (
                                                                            <Button size="small" color="secondary" onClick={() => this.removeMsrpTierLine(row.id)}>Delete</Button>
                                                                        ) : hasOverride ? (
                                                                            <Button size="small" onClick={() => this.clearMsrpTierOverride(row.id)}>Reset</Button>
                                                                        ) : row.excluded ? (
                                                                            <Button size="small" onClick={() => this.changeMsrpTierIncluded(row.id, true)}>Restore</Button>
                                                                        ) : (
                                                                            <Button size="small" color="secondary" onClick={() => this.removeMsrpTierLine(row.id)}>Exclude</Button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                </div>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onClick={this.addMsrpTierLine}
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Add tier line
                                                </Button>
                                                <br />
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={this.openMsrpCopyTable}
                                                    className="success-button-lg"
                                                    style={{ marginTop: 8 }}
                                                    disabled={getActiveMsrpTierCopyRows(tierRows).length === 0}
                                                >
                                                    Copy MSRP Table
                                                </Button>
                                                </>
                                            );
                                        })()}
                                        <Typography variant="caption" display="block" style={{marginTop: 8, color: '#555'}}>
                                            Turn off the switch above to set a single MSRP manually (stored in MSRP).
                                        </Typography>
                                    </div>
                                ) : (
                                    <>
                                    <TextField value={this.state.editingPart.part.msrp || ''} type="number"
                                        onChange={this.changeMSRP} label={"MSRP"} variant="outlined" fullWidth style={{marginTop: 8}}
                                        InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>}} autoComplete="off"/>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={this.openMsrpCopyTable}
                                        className="success-button-lg"
                                        style={{ marginTop: 12 }}
                                        disabled={!this.state.editingPart.part.msrp}
                                    >
                                        Copy MSRP Table
                                    </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        </div>
                    </div>

                    
                    {/* Calculation Contributions Container - Full Width Tile (spans all columns) */}
                    <div className="container" style={{minHeight: '80vh', padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 30, marginTop: 20, width: '100%', maxWidth: 1800, marginLeft: 'auto', marginRight: 'auto'}}>
                        <h2 style={{marginTop: 0}}>Calculation Contributions</h2>
                        <Tabs value={this.state.calculationContributionsTab} onChange={this.handleCalculationContributionsTabChange} style={{marginTop: '10px', marginBottom: '20px'}}>
                            <Tab label="Inventory Calculations" />
                            <Tab label="Inventory Logs" />
                            <Tab label="Package Logs" />
                            <Tab label="Purchase Logs" />
                            <Tab label="Packing Slips" />
                            <Tab label="Sales Logs" />
                            <Tab label="Parent/Child" />
                        </Tabs>
                        
                        {/* Tab Panel 0: Inventory Calculations */}
                        {this.state.calculationContributionsTab === 0 && (
                            <Box>
                                {this.state.inventoryData ? (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '3px', maxWidth: 300, margin: '0 auto'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>4 Week Sales / wk:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.fourWeekAvgSales.toFixed(2)}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>24 Week Sales / wk:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.twentyFourWeekAvgSales.toFixed(2)}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Total Received:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.totalReceived}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Total Sold:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.totalSold}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Total Unpackaged:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.totalUnpackaged}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Total in FG/SubA:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.totalInFG}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Packaging Deficit:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.packagingDeficit || '-'}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Effective Inventory:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.effectiveInventory}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Critical Inventory Quantity:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.criticalInventoryQuantity}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Min Qty:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.minQuantity}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>On Order:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.onOrder}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Earliest ETA:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.earliestETA || '-'}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>In Transit:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.inTransit ?? 0}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Weeks of Stock:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.weeksOfStock}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Lead Time:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.leadTimeInWeeks} weeks</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Weeks to Order:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px', color: this.state.inventoryData.weeksToOrderColor}}>{this.state.inventoryData.weeksToOrder}</span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '8px',
                                            backgroundColor: this.state.inventoryData.criticalInventoryWeeksColor || '#f5f5f5',
                                            borderRadius: '4px',
                                        }}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Critical Inventory Weeks:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.criticalInventoryWeeks}</span>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>Number to Order:</span>
                                            <span style={{fontWeight: 'bold', fontSize: '14px'}}>{this.state.inventoryData.numberToOrder}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading inventory calculations...</div>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 1: Inventory Logs */}
                        {this.state.calculationContributionsTab === 1 && (
                            <Box>
                                {this.state.loadingCalculationContributions ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading inventory logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.inventoryLogs === null ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading inventory logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.inventoryLogs.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                        No inventory logs found for this part
                                    </Typography>
                                ) : (
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th style={{minWidth: 170}}>Date</th>
                                                    <th style={{minWidth: 130}}>User</th>
                                                    <th style={{minWidth: 100}}>Quantity</th>
                                                    <th style={{minWidth: 200}}>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.calculationContributionsData.inventoryLogs
                                                    .sort((a, b) => new Date(b.logDate) - new Date(a.logDate))
                                                    .map((log) => {
                                                    const adminUser = this.state.adminUsers.find(x => x.id === log.userId);
                                                    const userName = adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : 'Unknown';
                                                    return (
                                                        <tr key={log.id}>
                                                            <td>{formatUtcForDisplay(log.logDate)}</td>
                                                            <td>{userName}</td>
                                                            <td>{log.quantity}</td>
                                                            <td>{log.reason || log.notes || '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 2: Package Logs */}
                        {this.state.calculationContributionsTab === 2 && (
                            <Box>
                                {this.state.loadingCalculationContributions ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading package logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.packageLogs === null ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading package logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.packageLogs.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                        No package logs found for assemblies/subassemblies using this part
                                    </Typography>
                                ) : (
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th style={{minWidth: 170}}>Date</th>
                                                    <th style={{minWidth: 130}}>User</th>
                                                    <th style={{minWidth: 150}}>Assembly/SubAssembly</th>
                                                    <th style={{minWidth: 100}}>Quantity</th>
                                                    <th style={{minWidth: 200}}>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.calculationContributionsData.packageLogs
                                                    .sort((a, b) => new Date(b.logDate) - new Date(a.logDate))
                                                    .map((log) => {
                                                    const assemblyData = log.assemblyId ? 
                                                        assemblies?.find(a => a.assembly.id === log.assemblyId) : null;
                                                    const subAssemblyData = log.subAssemblyId ?
                                                        subAssemblies?.find(sa => sa.subAssembly.id === log.subAssemblyId) : null;
                                                    const assemblyName = assemblyData?.assembly.assemblyId || subAssemblyData?.subAssembly.subAssemblyId || 'Unknown';
                                                    const assemblyId = assemblyData?.assembly.id || subAssemblyData?.subAssembly.id;
                                                    const adminUser = this.state.adminUsers.find(x => x.id === log.userId);
                                                    const userName = adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : 'Unknown';
                                                    return (
                                                        <tr key={log.id}>
                                                            <td>{formatUtcForDisplay(log.logDate)}</td>
                                                            <td>{userName}</td>
                                                            <td>
                                                                {assemblyId ? (
                                                                    <span
                                                                        onClick={(e) => {
                                                                            if (e.ctrlKey || e.metaKey) {
                                                                                window.open(`/admin/assemblies/${assemblyId}`);
                                                                            } else {
                                                                                globalProps.history.push(`/admin/assemblies/${assemblyId}`);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            fontWeight: 800,
                                                                            color: '#000000',
                                                                            textDecoration: 'none',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        {assemblyName}
                                                                    </span>
                                                                ) : (
                                                                    assemblyName
                                                                )}
                                                            </td>
                                                            <td>{log.quantity}</td>
                                                            <td>{log.reason}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 3: Purchase Logs */}
                        {this.state.calculationContributionsTab === 3 && (
                            <Box>
                                {this.state.loadingCalculationContributions ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading purchase logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.purchaseLogs === null ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading purchase logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.purchaseLogs.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                        No purchase logs found for this part
                                    </Typography>
                                ) : (
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th style={{minWidth: 90}}>PO #</th>
                                                    <th style={{minWidth: 110}}>Order Date</th>
                                                    <th style={{minWidth: 150}}>Company</th>
                                                    <th style={{minWidth: 120}}>Vendor Order #</th>
                                                    <th style={{minWidth: 65}}>Quantity</th>
                                                    <th style={{minWidth: 350}}>Item</th>
                                                    <th style={{minWidth: 65}}>Unit Cost</th>
                                                    <th style={{minWidth: 65}}>Extended</th>
                                                    <th style={{minWidth: 80}}>Received</th>
                                                    <th style={{minWidth: 80}}>On Order</th>
                                                    <th style={{minWidth: 80}}>In Transit</th>
                                                    <th style={{minWidth: 80}}>ETA</th>
                                                    <th style={{minWidth: 50}}>Paid</th>
                                                    <th style={{minWidth: 85}}>Received Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.calculationContributionsData.purchaseLogs.map((log) => {
                                                    const partExpanded = this.state.parts?.find(p => p.part.id === log.partId);
                                                    const part = partExpanded?.part;
                                                    
                                                    // Calculate row background color based on "On Order" and "isPaid" status (same as Purchases.js)
                                                    const onOrderQuantity = log.onOrderQuantity || (log.quantity - log.quantityReceived);
                                                    // Use isPaid from enriched log (includes invoice check)
                                                    const isPaid = log.isPaid || false;
                                                    
                                                    let rowBackgroundColor = 'inherit';
                                                    if (log.quantityReceived >= log.quantity && isPaid) {
                                                        // Grey: Fully received and paid
                                                        rowBackgroundColor = '#929292';
                                                    } else if (log.quantityReceived >= log.quantity && !isPaid) {
                                                        // Green: Fully received and not paid
                                                        rowBackgroundColor = '#e8f5e8';
                                                    } else if (log.quantityReceived > 0 && !isPaid) {
                                                        // Yellow: Partially received and not paid
                                                        rowBackgroundColor = '#fff3c1';
                                                    } else if (isPaid && onOrderQuantity > 0) {
                                                        // Red: Partially received and paid
                                                        rowBackgroundColor = '#ffd2c1';
                                                    } else if (log.quantityReceived === 0 && (log.inTransitQuantity || 0) > 0) {
                                                        // Light cyan: Nothing received but items in transit
                                                        rowBackgroundColor = '#d7ffff';
                                                    }
                                                    
                                                    // Calculate ETA
                                                    const etaDate = log.onOrderQuantity > 0 && log.orderDate && part?.leadTimeInWeeks ? 
                                                        addDays(new Date(log.orderDate), (part.leadTimeInWeeks * 7)).toISOString().split('T')[0] : '';
                                                    
                                                    // Calculate costs (same as Purchases.js)
                                                    const unitCost = log.pricePaid && log.quantity ? (log.pricePaid / log.quantity).toFixed(2) : '0.00';
                                                    const extendedCost = log.pricePaid ? Number.parseFloat(log.pricePaid).toFixed(2) : '0.00';
                                                    
                                                    return (
                                                        <tr key={log.id} style={{backgroundColor: rowBackgroundColor}}>
                                                            <td>
                                                                {log.purchaseOrderId ? (
                                                                    <span
                                                                        onClick={(e) => {
                                                                            if (e.ctrlKey || e.metaKey) {
                                                                                window.open(`/admin/purchases/edit/${log.purchaseOrderId}`, '_blank');
                                                                            } else {
                                                                                globalProps.history.push(`/admin/purchases/edit/${log.purchaseOrderId}`);
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                                                                        title="Click to open PO (Ctrl+Click for new tab)"
                                                                    >
                                                                        {log.purchaseOrderNumber || '-'}
                                                                    </span>
                                                                ) : (
                                                                    (log.purchaseOrderNumber || '-')
                                                                )}
                                                            </td>
                                                            <td>{new Date(log.orderDate).toISOString().split('T')[0]}</td>
                                                            <td>{log.companyName || '-'}</td>
                                                            <td>{log.vendorOrderNumber || '-'}</td>
                                                            <td>{log.quantity}</td>
                                                            <td className="text-left">
                                                                {part ? (
                                                                    <PartDisplay 
                                                                        part={part}
                                                                        variant="table"
                                                                        classes={this.props.classes}
                                                                    />
                                                                ) : (
                                                                    <span>Unknown Part</span>
                                                                )}
                                                            </td>
                                                            <td>${unitCost}</td>
                                                            <td>${extendedCost}</td>
                                                            <td>{log.quantityReceived}</td>
                                                            <td>{log.onOrderQuantity > 0 ? log.onOrderQuantity : ''}</td>
                                                            <td>{log.inTransitQuantity > 0 ? log.inTransitQuantity : ''}</td>
                                                            <td>{etaDate}</td>
                                                            <td>{isPaid ? '✓' : ''}</td>
                                                            <td>{log.receivedDate ? new Date(log.receivedDate).toISOString().split('T')[0] : ''}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 4: Packing Slips */}
                        {this.state.calculationContributionsTab === 4 && (
                            <Box>
                                {this.state.loadingCalculationContributions ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading packing slips...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.packingSlips === null ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading packing slips...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.packingSlips.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                        No packing slips found for this part
                                    </Typography>
                                ) : (
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th style={{minWidth: 200}}>Name</th>
                                                    <th style={{minWidth: 150}}>Vendor</th>
                                                    <th style={{minWidth: 130}}>Date Created</th>
                                                    <th style={{minWidth: 80}}>PO #</th>
                                                    <th style={{minWidth: 100}}>Tracking #</th>
                                                    <th style={{minWidth: 80}}>Quantity</th>
                                                    <th style={{minWidth: 105}}>Status</th>
                                                    <th style={{minWidth: 130}}>Date Received</th>
                                                    <th style={{minWidth: 100}}>File</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.calculationContributionsData.packingSlips
                                                    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
                                                    .map((slip) => {
                                                    // Calculate status based on items (same logic as PackingSlips.js)
                                                    const totalExpected = slip.items?.reduce((sum, item) => sum + (item.quantityExpected || 0), 0) || 0;
                                                    const totalReceived = slip.items?.reduce((sum, item) => sum + (item.quantityReceived || 0), 0) || 0;
                                                    const isFullyReceived = isPackingSlipFullyReceived(slip);
                                                    const hasAnyReceiving = !isFullyReceived && totalReceived > 0 && totalReceived < totalExpected;
                                                    
                                                    // Apply CSS classes (same as PackingSlips.js)
                                                    const rowClassName = `packing-slip-row ${isFullyReceived ? 'received' : hasAnyReceiving ? 'partially-received' : ''}`;
                                                    
                                                    return (
                                                        <tr key={slip.id} className={rowClassName}>
                                                            <td className="text-left">
                                                                {slip.id ? (
                                                                    <span
                                                                        onClick={(e) => {
                                                                            if (e.ctrlKey || e.metaKey) {
                                                                                window.open(`/admin/packing-slips/${slip.id}`, '_blank');
                                                                            } else {
                                                                                globalProps.history.push(`/admin/packing-slips/${slip.id}`);
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                                                                        title="Click to open packing slip (Ctrl+Click for new tab)"
                                                                    >
                                                                        {slip.name}
                                                                    </span>
                                                                ) : (
                                                                    slip.name
                                                                )}
                                                            </td>
                                                            <td>{slip.vendorName || '-'}</td>
                                                            <td>{new Date(slip.dateCreated).toISOString().split('T')[0]}</td>
                                                            <td>
                                                                {(() => {
                                                                    const poId = this.getFirstPurchaseOrderId(slip);
                                                                    const poNum = this.getFirstPurchaseOrderNumber(slip) || '-';
                                                                    if (poId) {
                                                                        return (
                                                                            <span
                                                                                onClick={(e) => {
                                                                                    if (e.ctrlKey || e.metaKey) {
                                                                                        window.open(`/admin/purchases/edit/${poId}`, '_blank');
                                                                                    } else {
                                                                                        globalProps.history.push(`/admin/purchases/edit/${poId}`);
                                                                                    }
                                                                                }}
                                                                                style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                                                                                title="Click to open PO (Ctrl+Click for new tab)"
                                                                            >
                                                                                {poNum}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return poNum;
                                                                })()}
                                                            </td>
                                                            <td>{slip.trackingNumber || '-'}</td>
                                                            <td>{totalExpected}</td>
                                                            <td>
                                                                <span className={`packing-slip-status ${isFullyReceived ? 'received' : hasAnyReceiving ? 'partially-received' : ''}`}>
                                                                    {isFullyReceived ? 'Received' : hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {slip.dateReceived ? 
                                                                    new Date(slip.dateReceived).toISOString().split('T')[0] : 
                                                                    '-'
                                                                }
                                                            </td>
                                                            <td>
                                                                {slip.fileUrl ? (
                                                                    <span className="packing-slip-file"
                                                                        onClick={() => window.open(slip.fileUrl, '_blank')}
                                                                        style={{cursor: 'pointer', textDecoration: 'underline', color: '#1976d2'}}>
                                                                        {slip.fileName || 'Download'}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 5: Sales Logs */}
                        {this.state.calculationContributionsTab === 5 && (
                            <Box>
                                {this.state.loadingCalculationContributions ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading sales logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.salesLogs === null ? (
                                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                        <CircularProgress size={24} />
                                        <div style={{marginTop: '10px'}}>Loading sales logs...</div>
                                    </div>
                                ) : this.state.calculationContributionsData.salesLogs.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                        No sales logs found for assemblies/subassemblies using this part
                                    </Typography>
                                ) : (
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th style={{minWidth: 170}}>Date</th>
                                                    <th style={{minWidth: 130}}>Sales Order</th>
                                                    <th style={{minWidth: 100}}>Dealer Order #</th>
                                                    <th style={{minWidth: 150}}>Part Number</th>
                                                    <th style={{minWidth: 100}}>Quantity</th>
                                                    <th style={{minWidth: 150}}>Source</th>
                                                    <th style={{minWidth: 100}}>Status</th>
                                                    <th style={{minWidth: 300}}>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                  const salesChainIndex = buildBomIndex(assemblies, subAssemblies);
                                                  const chainTarget = editingPart && editingPart.part
                                                    ? { id: editingPart.part.id, kind: 'part', partNumber: editingPart.part.partId }
                                                    : null;
                                                  return this.state.calculationContributionsData.salesLogs
                                                    .sort((a, b) => new Date(b.logDate) - new Date(a.logDate))
                                                    .map((log) => {
                                                    const assemblyData = log.assemblyId ? 
                                                        assemblies?.find(a => a.assembly.id === log.assemblyId) : null;
                                                    const subAssemblyData = log.subAssemblyId ?
                                                        subAssemblies?.find(sa => sa.subAssembly.id === log.subAssemblyId) : null;
                                                    const assemblyName = assemblyData?.assembly.assemblyId || subAssemblyData?.subAssembly.subAssemblyId || 'Unknown';
                                                    const assemblyId = assemblyData?.assembly.id || subAssemblyData?.subAssembly.id;

                                                    // Full BOM hierarchy (with quantities) from the sold item down to this part,
                                                    // plus the rolled-up effective quantity (sold qty x BOM multipliers).
                                                    let contributionChains = [];
                                                    let effectiveQty = log.quantity;
                                                    if (chainTarget) {
                                                        const rootNode = assemblyData?.assembly || subAssemblyData?.subAssembly || null;
                                                        const rootType = assemblyData ? 'assembly' : 'subassembly';
                                                        if (rootNode) {
                                                            contributionChains = buildContributionChains(rootNode, rootType, chainTarget, salesChainIndex);
                                                            if (contributionChains.length > 0) {
                                                                effectiveQty = contributionChains.reduce(
                                                                    (sum, ch) => sum + (Number(log.quantity) || 0) * chainMultiplier(ch), 0);
                                                            }
                                                        }
                                                    }
                                                    return (
                                                        <tr key={log.id}>
                                                            <td>{formatUtcForDisplay(log.logDate)}</td>
                                                            <td>
                                                                {log.globalSequenceNumber != null ? (
                                                                    <span
                                                                        onClick={(e) => {
                                                                            if (e.ctrlKey || e.metaKey) {
                                                                                window.open(`/admin/sales/${log.globalSequenceNumber}`, '_blank');
                                                                            } else {
                                                                                globalProps.history.push(`/admin/sales/${log.globalSequenceNumber}`);
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                                                                        title="Click to open Edit Sales Order (Ctrl+Click for new tab)"
                                                                    >
                                                                        {log.compositeOrderNumber || '-'}
                                                                    </span>
                                                                ) : (
                                                                    (log.compositeOrderNumber || '-')
                                                                )}
                                                            </td>
                                                            <td>{log.orderNumber || '-'}</td>
                                                            <td style={{ textAlign: 'left' }}>
                                                                {contributionChains.length === 0 ? (
                                                                    assemblyId ? (
                                                                        <span
                                                                            onClick={(e) => {
                                                                                if (e.ctrlKey || e.metaKey) {
                                                                                    window.open(`/admin/assemblies/${assemblyId}`);
                                                                                } else {
                                                                                    globalProps.history.push(`/admin/assemblies/${assemblyId}`);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                fontWeight: 800,
                                                                                color: '#000000',
                                                                                textDecoration: 'none',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            {assemblyName}
                                                                        </span>
                                                                    ) : (
                                                                        assemblyName
                                                                    )
                                                                ) : (
                                                                    contributionChains.map((chain, ci) => (
                                                                        <div key={ci} style={{ marginBottom: ci < contributionChains.length - 1 ? 8 : 0 }}>
                                                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                                                {formatBomQty(log.quantity)}×{' '}
                                                                                {assemblyId ? (
                                                                                    <span
                                                                                        onClick={(e) => {
                                                                                            if (e.ctrlKey || e.metaKey) {
                                                                                                window.open(`/admin/assemblies/${assemblyId}`);
                                                                                            } else {
                                                                                                globalProps.history.push(`/admin/assemblies/${assemblyId}`);
                                                                                            }
                                                                                        }}
                                                                                        style={{ fontWeight: 800, color: '#000000', textDecoration: 'none', cursor: 'pointer' }}
                                                                                    >
                                                                                        {assemblyName}
                                                                                    </span>
                                                                                ) : (
                                                                                    assemblyName
                                                                                )}
                                                                            </div>
                                                                            {chain.map((step, si) => (
                                                                                <div
                                                                                    key={si}
                                                                                    style={{ marginLeft: (si + 1) * 10, color: '#555', fontSize: '0.85em', whiteSpace: 'nowrap' }}
                                                                                >
                                                                                    ↳ {formatBomQty(step.qty)}× {step.partNumber}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </td>
                                                            <td title={contributionChains.length > 0 ? `Rolled up from sold quantity ${log.quantity} via BOM` : undefined}>{contributionChains.length > 0 ? formatBomQty(effectiveQty) : log.quantity}</td>
                                                            <td>{log.source}</td>
                                                            <td style={{
                                                                color: normalizeStatus(log.status) === 'Fulfilled' ? '#4caf50' : 
                                                                       normalizeStatus(log.status) === 'Pending' ? '#ff9800' : 
                                                                       normalizeStatus(log.status) === 'Cancelled' ? '#f44336' : 
                                                                       normalizeStatus(log.status) === 'Refunded' ? '#9c27b0' : '#666',
                                                                fontWeight: 'bold'
                                                            }}>{normalizeStatus(log.status)}</td>
                                                            <td>{log.reason}</td>
                                                        </tr>
                                                    );
                                                });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Box>
                        )}
                        
                        {/* Tab Panel 6: Parent/Child */}
                        {this.state.calculationContributionsTab === 6 && (
                            <Box>
                                <div style={{display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
                                    <div className="filter-toggle" onClick={() => this.setState({showParentChildOptionalColumns: !this.state.showParentChildOptionalColumns})}>
                                        <Checkbox checked={this.state.showParentChildOptionalColumns} />
                                        <span className="filter-label">Show All Columns</span>
                                    </div>
                                    <div className="filter-toggle" onClick={() => this.setState({showParentChildImpliedParts: !this.state.showParentChildImpliedParts})}>
                                        <Checkbox checked={this.state.showParentChildImpliedParts} />
                                        <span className="filter-label">Show Implied Parts</span>
                                    </div>
                                </div>
                                <div className={`parts-table-container cc-parentchild${this.state.showParentChildOptionalColumns ? '' : ' cc-hide-optional'}`}>
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th style={{minWidth: 60}}>Quantity</th>
                                                <th style={{minWidth: 165}}>Assembly/SubAssembly ID</th>
                                                <th style={{minWidth: 35}}>Rev</th>
                                                <th style={{minWidth: 50}}>Implied Part</th>
                                                <th style={{minWidth: 500, borderRight: '1px solid #fe65ff'}}>Description</th>
                                                <th style={{minWidth: 95, border: '1px solid #fe65ff'}}>Total Packaged</th>
                                                <th style={{minWidth: 95, border: '1px solid #fe65ff'}}>Total Packaged<br/>w/ Rev</th>
                                                <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>Total Unpackaged</th>
                                                <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>Total Unpackaged<br/>w/ Rev</th>
                                                <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>Total Needed<br/>to Package<br/>w/ Rev</th>
                                                <th style={{minWidth: 125, border: '1px solid #fe65ff'}}>Enough to Package?<br/>w/ Rev</th>
                                                <th style={{minWidth: 115}}>Min Needed<br/>On Hand<br/>w/ Rev</th>
                                                <th style={{minWidth: 115}}>Avg On Hand<br/>Packaged<br/>w/ Rev</th>
                                                <th style={{minWidth: 115}}>Total On Hand<br/>Packaged</th>
                                                <th style={{minWidth: 115, borderRight: '1px solid #fe65ff'}}>Total On Hand<br/>Packaged<br/>w/ Rev</th>
                                                <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>Packaging Deficit<br/>w/ Rev</th>
                                                <th style={{minWidth: 70}}>Total Sold</th>
                                                <th style={{minWidth: 70}}>Total Sold<br/>w/ Rev</th>
                                                <th style={{minWidth: 80}}>4 week<br/>Sales / wk</th>
                                                <th style={{minWidth: 115}}>4 week<br/>Sales / wk w/ Rev</th>
                                                <th style={{minWidth: 80}}>24 week<br/>Sales / wk</th>
                                                <th style={{minWidth: 115}}>24 week<br/>Sales / wk w/ Rev</th>
                                                <th style={{minWidth: 70}}>Unit Cost</th>
                                                <th style={{minWidth: 100}}>Extended Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const bomAssemblies = assemblies?.filter(x => x.assembly.isActive && x.assembly.partsToAssemblies.find(y => y.partFk === editingPart.part.id)) || [];
                                                const bomSubAssemblies = subAssemblies?.filter(x => x.subAssembly.isActive && x.subAssembly.partsToSubAssemblies.find(y => y.partFk === editingPart.part.id)) || [];
                                                const showImpliedParts = this.state.showParentChildImpliedParts;
                                                
                                                let rows = [];
                                                
                                                // Process assemblies
                                                bomAssemblies.forEach((assemblyData) => {
                                                    const bomItem = assemblyData.assembly.partsToAssemblies.find(pta => pta.partFk === editingPart.part.id);
                                                    if (!bomItem) return;
                                                    if (bomItem.isImpliedPart && !showImpliedParts) return;
                                                    
                                                    const parentExpanded = assemblyData;
                                                    const averageSalesFourAndTwentyFour = (parentExpanded.fourWeekSalesPerWeekWithRev + parentExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                    let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                                                    let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                                                    let avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;
                                                    let totalToPackage = parentExpanded.totalAssembledOnHand < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHand : 0;
                                                    const totalNeededToPackage = Math.ceil(totalToPackage) * bomItem.quantity;
                                                    
                                                    rows.push(
                                                        <tr key={`assembly-${assemblyData.assembly.id}`}>
                                                            <td>{bomItem.quantity || 0}</td>
                                                            <td className="text-left">
                                                                <span
                                                                    onClick={(e) => {
                                                                        if (e.ctrlKey || e.metaKey) {
                                                                            window.open(`/admin/assemblies/${assemblyData.assembly.id}`);
                                                                        } else {
                                                                            globalProps.history.push(`/admin/assemblies/${assemblyData.assembly.id}`);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        fontWeight: 800,
                                                                        color: '#000000',
                                                                        textDecoration: 'none',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {assemblyData.assembly.assemblyId}
                                                                </span>
                                                            </td>
                                                            <td>{assemblyData.assembly.revision}</td>
                                                            <td style={{textAlign: 'center'}}>{bomItem.isImpliedPart ? '✓' : ''}</td>
                                                            <td className="description-ellipsis text-left">{assemblyData.assembly?.description || ''}</td>
                                                            <td>{parentExpanded.totalAssembled * bomItem.quantity}</td>
                                                            <td>{parentExpanded.totalAssembledWithRev * bomItem.quantity}</td>
                                                            <td>{editingPart.totalUnpackaged || 0}</td>
                                                            <td>{editingPart.totalUnpackaged || 0}</td>
                                                            <td>{totalNeededToPackage.toFixed(0)}</td>
                                                            <td>{(editingPart.totalUnpackaged || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                            <td>{(bomItem && !bomItem.isImpliedPart ? Math.ceil(minAssembledOnHand) * bomItem.quantity : 0).toFixed(0)}</td>
                                                            <td>{(Math.ceil(avgAssembledOnHand) * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{parentExpanded.totalAssembledOnHand * bomItem.quantity}</td>
                                                            <td>{parentExpanded.totalAssembledOnHandWithRev * bomItem.quantity}</td>
                                                            <td>{((parentExpanded.totalAssembledOnHandWithRev * bomItem.quantity) - (Math.ceil(avgAssembledOnHand) * bomItem.quantity)).toFixed(0)}</td>
                                                            <td>{(parentExpanded.totalSold * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{(parentExpanded.fourWeekSalesPerWeek * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.twentyFourWeekSalesPerWeek * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * bomItem.quantity).toFixed(2)}</td>
                                                            <td>${editingPart.cost && editingPart.quantity ? (editingPart.cost / editingPart.quantity).toFixed(2) : "-"}</td>
                                                            <td>${editingPart.cost && editingPart.quantity ? ((editingPart.cost / editingPart.quantity) * bomItem.quantity).toFixed(2) : "-"}</td>
                                                        </tr>
                                                    );
                                                });
                                                
                                                // Process subassemblies
                                                bomSubAssemblies.forEach((subAssemblyData) => {
                                                    const bomItem = subAssemblyData.subAssembly.partsToSubAssemblies.find(ptsa => ptsa.partFk === editingPart.part.id);
                                                    if (!bomItem) return;
                                                    if (bomItem.isImpliedPart && !showImpliedParts) return;
                                                    
                                                    const parentExpanded = subAssemblyData;
                                                    const averageSalesFourAndTwentyFour = (parentExpanded.fourWeekSalesPerWeekWithRev + parentExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                    let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                                                    let minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                                                    let avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;
                                                    let totalToPackage = parentExpanded.totalAssembledOnHand < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHand : 0;
                                                    const totalNeededToPackage = Math.ceil(totalToPackage) * bomItem.quantity;
                                                    
                                                    rows.push(
                                                        <tr key={`subassembly-${subAssemblyData.subAssembly.id}`}>
                                                            <td>{bomItem.quantity || 0}</td>
                                                            <td className="text-left">
                                                                <span
                                                                    onClick={(e) => {
                                                                        if (e.ctrlKey || e.metaKey) {
                                                                            window.open(`/admin/assemblies/${subAssemblyData.subAssembly.id}`);
                                                                        } else {
                                                                            globalProps.history.push(`/admin/assemblies/${subAssemblyData.subAssembly.id}`);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        fontWeight: 800,
                                                                        color: '#000000',
                                                                        textDecoration: 'none',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {subAssemblyData.subAssembly.subAssemblyId}
                                                                </span>
                                                            </td>
                                                            <td>{subAssemblyData.subAssembly.revision}</td>
                                                            <td style={{textAlign: 'center'}}>{bomItem.isImpliedPart ? '✓' : ''}</td>
                                                            <td className="description-ellipsis text-left">{subAssemblyData.subAssembly?.description || ''}</td>
                                                            <td>{parentExpanded.totalAssembled * bomItem.quantity}</td>
                                                            <td>{parentExpanded.totalAssembledWithRev * bomItem.quantity}</td>
                                                            <td>{editingPart.totalUnpackaged || 0}</td>
                                                            <td>{editingPart.totalUnpackaged || 0}</td>
                                                            <td>{totalNeededToPackage.toFixed(0)}</td>
                                                            <td>{(editingPart.totalUnpackaged || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                            <td>{(bomItem && !bomItem.isImpliedPart ? Math.ceil(minAssembledOnHand) * bomItem.quantity : 0).toFixed(0)}</td>
                                                            <td>{(Math.ceil(avgAssembledOnHand) * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{parentExpanded.totalAssembledOnHand * bomItem.quantity}</td>
                                                            <td>{parentExpanded.totalAssembledOnHandWithRev * bomItem.quantity}</td>
                                                            <td>{((parentExpanded.totalAssembledOnHandWithRev * bomItem.quantity) - (Math.ceil(avgAssembledOnHand) * bomItem.quantity)).toFixed(0)}</td>
                                                            <td>{(parentExpanded.totalSold * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * bomItem.quantity).toFixed(0)}</td>
                                                            <td>{(parentExpanded.fourWeekSalesPerWeek * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.twentyFourWeekSalesPerWeek * bomItem.quantity).toFixed(2)}</td>
                                                            <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * bomItem.quantity).toFixed(2)}</td>
                                                            <td>${editingPart.cost && editingPart.quantity ? (editingPart.cost / editingPart.quantity).toFixed(2) : "-"}</td>
                                                            <td>${editingPart.cost && editingPart.quantity ? ((editingPart.cost / editingPart.quantity) * bomItem.quantity).toFixed(2) : "-"}</td>
                                                        </tr>
                                                    );
                                                });
                                                
                                                if (rows.length === 0) {
                                                    rows.push(
                                                        <tr key="empty">
                                                            <td colSpan="24" style={{textAlign: 'center', padding: 20}}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    This part is not used in any assemblies or subassemblies
                                                                </Typography>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                
                                                return rows;
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </Box>
                        )}
                    </div>
                {this.state.newRevisionOpen ?
                    <DrawerMenuRight title={"New Revision"} open={this.state.newRevisionOpen}
                                     onClose={() => this.setState({newRevisionOpen: false})}
                                     hasUnsavedChanges={
                                         this.state.newRevision !== undefined || 
                                         this.state.newRevisionNotes !== undefined
                                     }
                                     onSubmit={this.cloneNewRevision}
                                     submitConfirmMessage="Are you sure you want to create a new revision?"
                                     >
                        <div style={{marginBottom: 20}}>Current revision: {editingPart.part.revision}</div>
                        <TextField defaultValue={''}
                                   onChange={(event) => this.setState({newRevision: event.target.value})} label={"Revision"} variant="outlined"
                                   style={{width: '48%', marginBottom: 20}} inputProps={{maxLength: 5}}
                                   autoComplete="off"/>
                        <TextField defaultValue={''}
                                   onChange={(event) => this.setState({newRevisionNotes: event.target.value})} label={"Revision Notes"} variant="outlined"
                                   style={{width: '48%', marginBottom: 20, marginLeft: 10}}
                                   inputProps={{maxLength: 500}} autoComplete="off"/>
                        {(() => {
                            const hasImages = editingPart.part.imageURLs && editingPart.part.imageURLs.length > 0 && editingPart.part.imageURLs.some(url => url && url.trim() !== '');
                            const hasFiles = editingPart.part.fileURLs && (
                                (Array.isArray(editingPart.part.fileURLs) && editingPart.part.fileURLs.length > 0 && editingPart.part.fileURLs.some(url => url && url.trim() !== '')) ||
                                (typeof editingPart.part.fileURLs === 'string' && editingPart.part.fileURLs.trim() !== '')
                            );
                            if (hasImages || hasFiles) {
                                return (
                                    <div style={{marginBottom: 20}}>
                                        <Checkbox
                                            checked={this.state.cloneImagesAndFiles}
                                            onChange={(e) => this.setState({cloneImagesAndFiles: e.target.checked})}
                                            color="primary"
                                        />
                                        <Typography variant="body1" component="span">
                                            Clone Images & Files
                                        </Typography>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {this.state.savingPart === true ?
                            <CircularProgress/>
                            :
                            <Button onClick={this.cloneNewRevision} className="success-button-lg">Clone To New Revision</Button>
                        }
                    </DrawerMenuRight>
                : ""}
                    {this.state.showCloneDialog ?
                        <DrawerMenuRight title={`Clone ${editingPart.part.partId} rev ${editingPart.part.revision}`} open={this.state.showCloneDialog}
                                         onClose={this.cancelClonePart}
                                         hasUnsavedChanges={false}
                                         onSubmit={this.handleClonePartSubmit}
                                         submitConfirmMessage="Are you sure you want to clone this part?"
                                         >
                            <TextField defaultValue={''}
                                       onChange={this.changeClonedPartId} label={"Part Number"} variant="outlined"
                                       style={{width: '48%', marginBottom: 20}} inputProps={{maxLength: 50}}
                                       autoComplete="off"/>
                            <TextField defaultValue={''}
                                       onChange={this.changeClonedRevision} label={"Revision"} variant="outlined"
                                       style={{width: '48%', marginBottom: 20, marginLeft: 10}} inputProps={{maxLength: 5}}
                                       autoComplete="off"/>
                            {(() => {
                                const hasImages = editingPart.part.imageURLs && editingPart.part.imageURLs.length > 0 && editingPart.part.imageURLs.some(url => url && url.trim() !== '');
                                const hasFiles = editingPart.part.fileURLs && (
                                    (Array.isArray(editingPart.part.fileURLs) && editingPart.part.fileURLs.length > 0 && editingPart.part.fileURLs.some(url => url && url.trim() !== '')) ||
                                    (typeof editingPart.part.fileURLs === 'string' && editingPart.part.fileURLs.trim() !== '')
                                );
                                if (hasImages || hasFiles) {
                                    return (
                                        <div style={{marginBottom: 20}}>
                                            <Checkbox
                                                checked={this.state.cloneImagesAndFiles}
                                                onChange={(e) => this.setState({cloneImagesAndFiles: e.target.checked})}
                                                color="primary"
                                            />
                                            <Typography variant="body1" component="span">
                                                Clone Images & Files
                                            </Typography>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {this.state.cloningPart ? <CircularProgress/> : <Button onClick={this.handleClonePartSubmit} className="success-button-lg">Clone Part</Button>}
                        </DrawerMenuRight>
                    : ""}
                
                {/* Image Modal */}
                <Dialog
                    open={this.state.imageModalOpen}
                    onClose={this.closeImageModal}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogContent style={{padding: '20px', textAlign: 'center'}}>
                        {this.state.selectedImageUrl && (
                            <img 
                                src={this.state.selectedImageUrl} 
                                alt="Part Image"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    objectFit: 'contain'
                                }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeImageModal}>Close</Button>
                    </DialogActions>
                </Dialog>

                <DrawerMenuRight
                    open={this.state.purchaseDrawerOpen}
                    onClose={this.closePurchaseDrawer}
                    title="Purchase Parts"
                    customWidth={1400}
                    hasUnsavedChanges={this.state.newLogParts.length > 0 || this.state.newVendorOrderNumber !== ''}
                >
                    <div style={{padding: '20px'}}>
                        <div style={{marginBottom: 20, width: '22%', display: 'inline-block'}}>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <DatePicker
                                    label="Order Date"
                                    value={this.state.newPurchaseOrderDate}
                                    onChange={(newValue) => this.setState({newPurchaseOrderDate: newValue})}
                                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                        <div style={{marginBottom: 20, width: '24%', display: 'inline-block'}}>
                            <TextField
                                label="Vendor Order Number (Optional)"
                                value={this.state.newVendorOrderNumber}
                                onChange={(event) => this.setState({newVendorOrderNumber: event.target.value})}
                                variant="outlined"
                                style={{width: '100%'}}
                                inputProps={{maxLength: 200}}
                                autoComplete="off"
                            />
                        </div>
                        <div className="parts-table-container">
                            <table className="parts-table">
                                <thead>
                                    <tr>
                                        <th style={{minWidth: 300}}>PN + Description</th>
                                        <th style={{minWidth: 80}}>Qty</th>
                                        <th style={{minWidth: 90}}>Vendor</th>
                                        <th style={{minWidth: 120}}>Price</th>
                                        <th style={{minWidth: 90}}>Total</th>
                                        <th style={{minWidth: 80}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.newLogParts.map((row, index) => {
                                        if (row.isFamilyRow) {
                                            return (
                                                <tr key={row.id}>
                                                    <td>
                                                        {row.selectedOption && !row.isModifying ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <div style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'}} onClick={() => this.handlePartDisplayClick(index)}>
                                                                    {row.isFamilyRow && <FontAwesomeIcon icon={faPeopleCarry} style={{ color: '#2196F3' }} />}
                                                                    <PartDisplay
                                                                        part={{
                                                                            partId: row.isFamilyRow ? (row.selectedOption.family?.partFamilyId || row.selectedOption.family?.description || row.partId || `Family ${row.familyId}`) : (row.selectedOption.part?.partId || row.partId),
                                                                            revision: row.isFamilyRow ? '' : (row.selectedOption.part?.revision || ''),
                                                                            description: row.isFamilyRow ? (row.selectedOption.family?.description || row.description) : (row.selectedOption.part?.description || row.description)
                                                                        }}
                                                                        variant="table"
                                                                        classes={this.props.classes}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Autocomplete
                                                                autoHighlight
                                                                options={[
                                                                    ...(this.state.partFamilies || []).map(family => ({ title: `${family.partFamilyId} (Family)`, value: family.id, isFamily: true, family })),
                                                                    ...(this.state.parts || []).map(part => ({ title: `${part.part.partId} rev ${part.part.revision}`, value: part.part.id, isFamily: false, part: part.part }))
                                                                ]}
                                                                getOptionLabel={(option) => option.family ? option.family.partFamilyId : (option.part ? `${option.part.partId} rev ${option.part.revision}` : option.title || '')}
                                                                value={row.selectedOption}
                                                                onChange={(event, newValue) => {
                                                                    if (newValue?.isFamily) {
                                                                        this.handleFamilyChange(event, newValue, index);
                                                                        setTimeout(() => {
                                                                            const updatedRows = [...this.state.newLogParts];
                                                                            if (updatedRows[index]) updatedRows[index].isModifying = false;
                                                                            this.setState({ newLogParts: updatedRows });
                                                                        }, 0);
                                                                    } else if (newValue?.part) {
                                                                        let updatedRows = [...this.state.newLogParts];
                                                                        const currentRow = updatedRows[index];
                                                                        if (currentRow?.familyId) {
                                                                            updatedRows = updatedRows.filter((r, i) => i === index || r.familyId !== currentRow.familyId);
                                                                        }
                                                                        const rowIndex = updatedRows.findIndex(r => r.id === currentRow?.id);
                                                                        if (rowIndex !== -1) {
                                                                            const r = updatedRows[rowIndex];
                                                                            const purchasingPart = this.state.parts?.find(x => x.part.id === newValue.part.id);
                                                                            if (purchasingPart) {
                                                                                const vp = purchasingPart.part.vendorParts?.find(x => x.vendorId === purchasingPart.part.selectedVendorId) || purchasingPart.part.vendorParts?.[0];
                                                                                const vendorName = vp ? this.state.vendors.find(x => x.id === vp.vendorId)?.name : '';
                                                                                let initialPrice = 0, initialQuantity = 1;
                                                                                if (vp) {
                                                                                    if (vp.costType === 0 && vp.tieredCostFormulas?.length > 0) {
                                                                                        const st = [...vp.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                                                                                        initialPrice = st[0].price;
                                                                                        initialQuantity = st[0].quantity;
                                                                                    } else {
                                                                                        initialPrice = Number(vp.basePrice / vp.quantity).toFixed(3) || 0;
                                                                                        initialQuantity = vp.quantity || 1;
                                                                                    }
                                                                                }
                                                                                r.selectedOption = newValue;
                                                                                r.isModifying = false;
                                                                                r.isFamilyRow = false;
                                                                                r.familyId = null;
                                                                                r.partId = newValue.part.id;
                                                                                r.vendor = vendorName;
                                                                                r.vendorId = vp?.vendorId;
                                                                                r.price = parseFloat(initialPrice);
                                                                                r.total = initialPrice * initialQuantity;
                                                                                r.partQuantity = initialQuantity;
                                                                                r.vendorParts = purchasingPart.part.vendorParts;
                                                                            }
                                                                        }
                                                                        this.setState({ newLogParts: updatedRows });
                                                                    }
                                                                }}
                                                                renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                renderInput={(params) => <TextField {...params} variant="outlined" inputProps={{ ...params.inputProps, tabIndex: index * 4 + 1, 'data-row-index': index, 'data-field-type': 'part-number' }} />}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <TextField type="number" value={getNumberInputDisplayValue(row.familyQuantity)} onChange={(event) => this.handleFamilyQuantityChange(event, index)} variant="outlined" style={{width: '75%'}} inputProps={{ tabIndex: index * 4 + 2, 'data-row-index': index, 'data-field-type': 'family-quantity' }} onKeyDown={(event) => this.handleQuantityKeyDown(event, index)} autoComplete="off" />
                                                    </td>
                                                    <td></td><td></td><td></td>
                                                    <td><FontAwesomeIcon icon={faTrash} style={{ cursor: 'pointer', color: 'black' }} onClick={() => this.removeLine(index)} /></td>
                                                </tr>
                                            );
                                        }
                                        const partExpanded = this.state.parts?.find(x => x.part.id === (row.selectedOption?.part?.id || row.partId));
                                        return (
                                            <tr key={row.id} style={{backgroundColor: row.isFamilyPart ? '#f5f5f5' : 'inherit'}}>
                                                <td className="text-left">
                                                    {row.selectedOption && !row.isModifying ? (
                                                        <div style={{cursor: 'pointer'}} onClick={() => this.handlePartDisplayClick(index)}>
                                                            <PartDisplay part={row.selectedOption?.part || partExpanded?.part || { partId: 'Unknown Part', revision: '', description: '' }} variant="table" classes={this.props.classes} />
                                                        </div>
                                                    ) : (
                                                        <Autocomplete
                                                            autoHighlight
                                                            options={[
                                                                ...(this.state.partFamilies || []).map(family => ({ title: `${family.partFamilyId} (Family)`, value: family.id, isFamily: true, family })),
                                                                ...(this.state.parts || []).map(part => ({ title: `${part.part.partId} rev ${part.part.revision}`, value: part.part.id, isFamily: false, part: part.part }))
                                                            ]}
                                                            getOptionLabel={(option) => option.title || ''}
                                                            value={row.selectedOption}
                                                            onChange={(event, newValue) => {
                                                                if (newValue?.isFamily) {
                                                                    this.handleFamilyChange(event, newValue, index);
                                                                    setTimeout(() => { const u = [...this.state.newLogParts]; if (u[index]) u[index].isModifying = false; this.setState({ newLogParts: u }); }, 0);
                                                                } else if (newValue?.part) {
                                                                    let updatedRows = [...this.state.newLogParts];
                                                                    const currentRow = updatedRows[index];
                                                                    if (currentRow?.familyId) updatedRows = updatedRows.filter((r, i) => i === index || r.familyId !== currentRow.familyId);
                                                                    const rowIndex = updatedRows.findIndex(r => r.id === currentRow?.id);
                                                                    if (rowIndex !== -1) {
                                                                        const r = updatedRows[rowIndex];
                                                                        const purchasingPart = this.state.parts?.find(x => x.part.id === newValue.part.id);
                                                                        if (purchasingPart) {
                                                                            const vp = purchasingPart.part.vendorParts?.find(x => x.vendorId === purchasingPart.part.selectedVendorId) || purchasingPart.part.vendorParts?.[0];
                                                                            const vendorName = vp ? this.state.vendors.find(x => x.id === vp.vendorId)?.name : '';
                                                                            let initialPrice = 0, initialQuantity = 1;
                                                                            if (vp) {
                                                                                if (vp.costType === 0 && vp.tieredCostFormulas?.length > 0) {
                                                                                    const st = [...vp.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                                                                                    initialPrice = vp.tieredCostFormulas.find(t => t.isSelected)?.price ?? st[0].price;
                                                                                    initialQuantity = vp.tieredCostFormulas.find(t => t.isSelected)?.quantity ?? st[0].quantity;
                                                                                } else {
                                                                                    initialPrice = Number(vp.basePrice / vp.quantity).toFixed(3) || 0;
                                                                                    initialQuantity = vp.quantity || 1;
                                                                                }
                                                                            }
                                                                            r.selectedOption = newValue;
                                                                            r.isModifying = false;
                                                                            r.isFamilyRow = false;
                                                                            r.familyId = null;
                                                                            r.partId = newValue.part.id;
                                                                            r.vendor = vendorName;
                                                                            r.vendorId = vp?.vendorId;
                                                                            r.price = parseFloat(initialPrice);
                                                                            r.total = initialPrice * initialQuantity;
                                                                            r.partQuantity = initialQuantity;
                                                                            r.vendorParts = purchasingPart.part.vendorParts;
                                                                        }
                                                                    }
                                                                    this.setState({ newLogParts: updatedRows });
                                                                }
                                                            }}
                                                            renderOption={(option) => <PartAutocompleteOption option={option} variant="part" />}
                                                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Part" />}
                                                        />
                                                    )}
                                                </td>
                                                <td>
                                                    <TextField type="number" value={getNumberInputDisplayValue(row.partQuantity)} onChange={(event) => this.handlePartQuantityChange(event, index)} variant="outlined" style={{width: '75%'}} inputProps={{ tabIndex: index * 4 + 2, 'data-row-index': index, 'data-field-type': 'part-quantity' }} onKeyDown={(event) => this.handleQuantityKeyDown(event, index)} autoComplete="off" />
                                                </td>
                                                <td>
                                                    {row.vendorParts?.length > 0 ? (
                                                        <FormControl variant="outlined" style={{minWidth: 150}}>
                                                            <Select value={row.vendorId || ''} onChange={(event) => this.handleVendorChange(event, index)} inputProps={{ tabIndex: -1, 'data-row-index': index, 'data-field-type': 'vendor' }}>
                                                                {row.vendorParts.map((vp) => {
                                                                    const vendor = this.state.vendors.find(x => x.id === vp.vendorId);
                                                                    return <MenuItem key={vp.vendorId} value={vp.vendorId}>{vendor?.name}</MenuItem>;
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (row.vendor || '')}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <TextField type="number" value={getNumberInputDisplayValue(row.price)} onChange={(event) => this.handlePartPriceChange(event, index)} variant="outlined" style={{width: '75%'}} InputProps={{ startAdornment: <span style={{marginRight: 8}}>$</span> }} inputProps={{ tabIndex: -1, 'data-row-index': index, 'data-field-type': 'price' }} autoComplete="off" />
                                                        {row.vendorParts?.find(vp => vp.vendorId === row.vendorId)?.costType === 0 && (
                                                            <FontAwesomeIcon icon={faInfoCircle} style={{ cursor: 'help' }} title={this.getTieredPricingTooltip(row.vendorParts.find(vp => vp.vendorId === row.vendorId))} />
                                                        )}
                                                    </div>
                                                </td>
                                                <td>${(row.total || 0).toFixed(2)}</td>
                                                <td><FontAwesomeIcon icon={faTrash} style={{ cursor: 'pointer', color: 'black' }} onClick={() => this.removeLine(index)} /></td>
                                            </tr>
                                        );
                                    })}
                                    <tr style={{backgroundColor: '#f0f0f0', fontWeight: 'bold'}}>
                                        <td colSpan={5} style={{textAlign: 'right'}}>Total:</td>
                                        <td>${this.state.newLogParts.filter(row => !row.isFamilyRow).reduce((sum, row) => sum + (row.total || 0), 0).toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <center>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.addNewLine}
                                className="success-button-lg"
                                style={{margin: '20px 0'}}
                            >
                                Add Line
                            </Button>
                            <br/>
                            {this.state.savingPurchaseLog ?
                                <CircularProgress/>
                                :
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.submitPurchaseOrder}
                                    disabled={this.state.newLogParts.filter(x => !x.isFamilyRow).length === 0}
                                    className="success-button-lg"
                                >
                                    Submit Purchase Orders
                                </Button>
                            }
                        </center>
                    </div>
                </DrawerMenuRight>

                <Dialog
                    open={this.state.showCopyTable}
                    onClose={this.closeCopyTable}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Copy Tables for Email</DialogTitle>
                    <DialogContent style={{ textAlign: 'center' }}>
                        {Object.entries(this.state.copyTablesByVendor).map(([vendor, tableData], vendorIndex) => (
                            <div key={vendor} style={{ marginBottom: vendorIndex < Object.keys(this.state.copyTablesByVendor).length - 1 ? '30px' : '0px' }}>
                                <h3 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    {vendor}
                                </h3>
                                <table style={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    margin: '10px auto',
                                    borderCollapse: 'collapse',
                                    border: '1px solid black',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '12px'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center'
                                            }}>
                                                Quantity
                                            </th>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center'
                                            }}>
                                                Item
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'center'
                                                }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'left'
                                                }}>
                                                    {item.partNumber} rev {item.revision}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                                    <Button
                                        onClick={() => this.copyVendorTableToClipboard(vendor, tableData)}
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                    >
                                        Copy {vendor} Table
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeCopyTable} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={this.state.showMsrpCopyTable}
                    onClose={this.closeMsrpCopyTable}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Copy MSRP Table for Email</DialogTitle>
                    <DialogContent style={{ textAlign: 'center' }}>
                        {this.state.msrpCopyTableData && (
                                <table style={{
                                    width: '100%',
                                    maxWidth: '280px',
                                    margin: '10px auto',
                                    borderCollapse: 'collapse',
                                    border: '1px solid black',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '12px',
                                }}>
                                    <thead>
                                        <tr>
                                            <th colSpan={2} style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center',
                                            }}>
                                                {formatMsrpCopyPartHeader(
                                                    this.state.msrpCopyTableData.partNumber,
                                                    this.state.msrpCopyTableData.revision
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.msrpCopyTableData.rows.map((row, index) => (
                                            <tr key={index}>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'center',
                                                }}>
                                                    {row.quantity}
                                                </td>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'right'
                                                }}>
                                                    {formatMsrpCopyPriceDisplay(row.msrp)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.copyMsrpTableToClipboard} className="success-button-lg">
                            Copy Table
                        </Button>
                        <Button onClick={this.closeMsrpCopyTable} className="cancel-button-lg">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </DashboardTemplate>
        );
    }
}

let globalProps;

    EditPart.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditPart);
